import React, { useState, useEffect } from "react";
import {
  supabase, signIn, signOut, getSession, updateProfile,
  fetchTeams, createTeam, updateTeam as supabaseUpdateTeam, deleteTeam as supabaseDeleteTeam,
  fetchTasks, createTask, updateTask, deleteTask, addComment,
  fetchNotifications, insertNotification, markNotificationRead,
  markAllNotificationsRead, fetchActivity, insertActivity,
  renameUserEverywhere,
  fetchMeetings, upsertMeeting, deleteMeetingById,
} from "./supabaseClient";

/* ── CSS variable injection for dark mode ─────────────────── */
function injectThemeVars(dark) {
  const light = {
    "--purple":"#A100FF","--purple-bg":"#FAF0FF","--purple-dim":"#F3E0FF",
    "--navy":"#1A0533","--bg":"#FFFFFF","--bg-off":"#F8F7FA",
    "--border":"#E8E0EF","--text-primary":"#1A0533","--text-second":"#6B5880",
    "--text-muted":"#9B88AF","--success":"#00875A","--success-bg":"#E3FCF2",
    "--warning":"#FF8B00","--warning-bg":"#FFF4E5","--danger":"#DE350B",
    "--danger-bg":"#FFEBE6","--info":"#0065FF","--info-bg":"#E6F0FF",
    "--page-bg":"#F8F7FA"
  };
  const darkVars = {
    "--purple":"#C466FF","--purple-bg":"#1E0840","--purple-dim":"#2D1052",
    "--navy":"#0D0020","--bg":"#1A1025","--bg-off":"#120D1E",
    "--border":"#2D1F45","--text-primary":"#F0E8FF","--text-second":"#B89FD4",
    "--text-muted":"#7A6599","--success":"#36B37E","--success-bg":"#0D2E1F",
    "--warning":"#FFAB00","--warning-bg":"#2E1F00","--danger":"#FF5630",
    "--danger-bg":"#2E0D00","--info":"#4C9AFF","--info-bg":"#0D1A2E",
    "--page-bg":"#0D0020"
  };
  const vars = dark ? darkVars : light;
  const root = document.documentElement;
  Object.entries(vars).forEach(([k,v]) => root.style.setProperty(k, v));
  document.body.style.background = vars["--page-bg"];
}

/* ── Accenture design tokens ─────────────────────────────── */
const AC = {
  purple:     "#A100FF",
  purpleLight:"#CC66FF",
  purpleDim:  "#F3E0FF",
  purpleBg:   "#FAF0FF",
  navy:       "#1A0533",
  navyMid:    "#2D0A4E",
  navyLight:  "#3D1260",
  white:      "#FFFFFF",
  offwhite:   "#F8F7FA",
  border:     "#E8E0EF",
  borderMid:  "#D0C4E0",
  textPrimary:"#1A0533",
  textSecond: "#6B5880",
  textMuted:  "#9B88AF",
  surface:    "#FFFFFF",
  surfaceAlt: "#F8F7FA",
  success:    "#00875A",
  successBg:  "#E3FCF2",
  warning:    "#FF8B00",
  warningBg:  "#FFF4E5",
  danger:     "#DE350B",
  dangerBg:   "#FFEBE6",
  info:       "#0065FF",
  infoBg:     "#E6F0FF",
};



const SHIFTS = [
  { id: "morning",   label: "Morning",   time: "07:00 – 16:00", icon: "🌅" },
  { id: "afternoon", label: "Afternoon", time: "10:00 – 19:00", icon: "☀️" },
  { id: "mid",       label: "Mid",       time: "13:00 – 22:00", icon: "🌆" },
  { id: "night",     label: "Night",     time: "22:00 – 07:00", icon: "🌙" },
  { id: "flexible",  label: "Flexible",  time: "Varies",        icon: "🔄" },
];

const MEMBER_STATUSES = [
  { id: "present",      label: "Present",       color: "#00875A", bg: "#E3FCF2", border: "#ABF5D1", dot: "#00875A",  icon: "●",  note: "Currently on shift"       },
  { id: "busy",         label: "Busy",          color: "#DE350B", bg: "#FFEBE6", border: "#FFBDAD", dot: "#DE350B",  icon: "🔴", note: "Occupied — limited availability" },
  { id: "rest_day",     label: "Rest day",      color: "#6B5880", bg: "#F0EBF7", border: "#D0C4E0", dot: "#9B88AF",  icon: "◐",  note: "Scheduled day off"        },
  { id: "on_leave",     label: "On leave",      color: "#0065FF", bg: "#E6F0FF", border: "#B3CFFF", dot: "#0065FF",  icon: "✈",  note: "Approved planned leave"   },
  { id: "sick_leave",   label: "Sick leave",    color: "#FF8B00", bg: "#FFF4E5", border: "#FFD28A", dot: "#FF8B00",  icon: "🤒", note: "Medical / unwell"         },
  { id: "long_weekend", label: "Long weekend",  color: "#A100FF", bg: "#F3E0FF", border: "#CC66FF", dot: "#A100FF",  icon: "🌴", note: "Extended weekend break"   },
  { id: "training",     label: "In training",   color: "#FF8B00", bg: "#FFF4E5", border: "#FFD28A", dot: "#FFAB00",  icon: "📚", note: "Learning & development"   },
  { id: "away",         label: "Away",          color: "#FF8B00", bg: "#FFF4E5", border: "#FFD28A", dot: "#FF8B00",  icon: "◌",  note: "Idle / stepped away"      },
  { id: "offline",      label: "Offline",       color: "#6B5880", bg: "#F0EBF7", border: "#D0C4E0", dot: "#9B88AF",  icon: "◯",  note: "Not available / offline"  },
];

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function makeDefaultTeams() {
return [
  { id:"t1", name:"Design",      color:"#A100FF", members:[
    {id:"m1",name:"Jackie Chen",    role:"Lead Designer",    shift:"morning",   shiftStart:"07:00",shiftEnd:"16:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
    {id:"m2",name:"Jeff Patel",  role:"UI Designer",      shift:"afternoon", shiftStart:"10:00",shiftEnd:"19:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"on_leave"},
    {id:"m3",name:"Tom Eriksson", role:"Motion Designer",  shift:"flexible",  shiftStart:"09:00",shiftEnd:"17:00",days:["Mon","Wed","Fri"],            status:"present"},
  ]},
  { id:"t2", name:"Engineering", color:"#0065FF", members:[
    {id:"m4",name:"James Park",   role:"Tech Lead",        shift:"morning",   shiftStart:"07:00",shiftEnd:"16:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
    {id:"m5",name:"Lin Wei",      role:"Backend Eng.",     shift:"night",     shiftStart:"22:00",shiftEnd:"07:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
    {id:"m6",name:"Sara Müller",  role:"Frontend Eng.",    shift:"afternoon", shiftStart:"10:00",shiftEnd:"19:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"sick_leave"},
    {id:"m7",name:"Dev Sharma",   role:"DevOps",           shift:"mid",       shiftStart:"13:00",shiftEnd:"22:00",days:["Tue","Wed","Thu","Sat","Sun"],status:"rest_day"},
  ]},
  { id:"t3", name:"Marketing",   color:"#FF8B00", members:[
    {id:"m8",name:"Sofia Reyes",  role:"Marketing Lead",   shift:"morning",   shiftStart:"07:00",shiftEnd:"16:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
    {id:"m9",name:"Kai Tanaka",   role:"Content Writer",   shift:"flexible",  shiftStart:"10:00",shiftEnd:"18:00",days:["Mon","Tue","Thu","Fri"],      status:"training"},
  ]},
  { id:"t4", name:"Operations",  color:"#00875A", members:[
    {id:"m10",name:"Nadia Osei",  role:"Ops Manager",      shift:"morning",   shiftStart:"07:00",shiftEnd:"16:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
  ]},
  { id:"t5", name:"Product",     color:"#DE350B", members:[
    {id:"m11",name:"Carlos Ibarra",role:"Product Manager", shift:"afternoon", shiftStart:"10:00",shiftEnd:"19:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"present"},
    {id:"m12",name:"Yuna Kim",     role:"Product Analyst", shift:"morning",   shiftStart:"07:00",shiftEnd:"16:00",days:["Mon","Tue","Wed","Thu","Fri"],status:"long_weekend"},
  ]},
];
}

const TEAM_PALETTE = ["#A100FF","#0065FF","#FF8B00","#00875A","#DE350B","#CC66FF","#2684FF","#FFAB00","#36B37E","#FF5630","#6554C0","#00B8D9","#FF7452","#57D9A3","#4C9AFF"];

const PRIORITIES = ["Low","Medium","High","Critical"];
const STATUSES   = ["To Do","In Progress","Review","Done"];

const PRIORITY_META = {
  Low:      { color: "#00875A", bg: "#E3FCF2", border: "#ABF5D1" },
  Medium:   { color: "#FF8B00", bg: "#FFF4E5", border: "#FFD28A" },
  High:     { color: "#DE350B", bg: "#FFEBE6", border: "#FFBDAD" },
  Critical: { color: "#A100FF", bg: "#F3E0FF", border: "#CC66FF" },
};

const STATUS_META = {
  "To Do":       { color: "#6B5880", bg: "#F0EBF7", border: "#D0C4E0", bar: "#9B88AF" },
  "In Progress": { color: "#0065FF", bg: "#E6F0FF", border: "#B3CFFF", bar: "#0065FF" },
  "Review":      { color: "#FF8B00", bg: "#FFF4E5", border: "#FFD28A", bar: "#FF8B00" },
  "Done":        { color: "#00875A", bg: "#E3FCF2", border: "#ABF5D1", bar: "#00875A" },
};

function genId() { return Math.random().toString(36).slice(2,9); }
function daysUntil(d) {
  if (!d) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.round((new Date(d+"T00:00:00") - now) / 86400000);
}

function DeadlineBadge({ date }) {
  if (!date) return null;
  const days = daysUntil(date);
  let color = "var(--text-muted)", bg = "var(--bg-off)", label = `${days}d left`;
  if (days < 0)        { color = AC.danger;  bg = AC.dangerBg;  label = `${Math.abs(days)}d overdue`; }
  else if (days === 0) { color = AC.warning; bg = AC.warningBg; label = "Due today"; }
  else if (days <= 2)  { color = AC.warning; bg = AC.warningBg; }
  return (
    <span style={{ fontSize:11, color, background:bg, borderRadius:4, padding:"2px 7px", fontWeight:500, whiteSpace:"nowrap" }}>
      ⏱ {label}
    </span>
  );
}

function Avatar({ name, size=28 }) {
  const initials = name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
  const colors = ["#A100FF","#0065FF","#00875A","#FF8B00","#DE350B","#6554C0","#00B8D9"];
  const idx = name.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % colors.length;
  const bg = colors[idx];
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:bg, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.36, fontWeight:700, flexShrink:0, letterSpacing:"-0.02em" }}>{initials}</div>
  );
}

function detectService(url) {
  try {
    const h = new URL(url).hostname.toLowerCase();
    if (h.includes("figma.com"))       return { label:"Figma",        icon:"🎨", color:"#A100FF" };
    if (h.includes("docs.google.com")) return { label:"Google Doc",   icon:"📄", color:"#0065FF" };
    if (h.includes("sheets.google"))   return { label:"Google Sheet", icon:"📊", color:"#00875A" };
    if (h.includes("drive.google"))    return { label:"Drive",        icon:"📁", color:"#0065FF" };
    if (h.includes("notion.so"))       return { label:"Notion",       icon:"◻",  color:"#1A0533" };
    if (h.includes("github.com"))      return { label:"GitHub",       icon:"⌥",  color:"#6B5880" };
    if (h.includes("atlassian.net")||h.includes("jira.")) return { label:"Jira", icon:"◈", color:"#0065FF" };
    if (h.includes("linear.app"))      return { label:"Linear",       icon:"◑",  color:"#6554C0" };
    if (h.includes("slack.com"))       return { label:"Slack",        icon:"❖",  color:"#DE350B" };
    if (h.includes("miro.com"))        return { label:"Miro",         icon:"⬡",  color:"#FF8B00" };
    if (h.includes("loom.com"))        return { label:"Loom",         icon:"⏺",  color:"#A100FF" };
    return { label:new URL(url).hostname.replace("www.",""), icon:"🔗", color:"var(--text-second)" };
  } catch { return { label:"Link", icon:"🔗", color:"var(--text-second)" }; }
}

function LinkChip({ url, onRemove }) {
  const svc = detectService(url);
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:4, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, padding:"3px 8px 3px 6px", maxWidth:200 }}>
      <span style={{ fontSize:11 }}>{svc.icon}</span>
      <a href={url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:svc.color, textDecoration:"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120, fontWeight:500 }} onClick={e=>e.stopPropagation()}>{svc.label}</a>
      {onRemove && <button onClick={e=>{e.stopPropagation();onRemove();}} style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", padding:"0 0 0 2px", fontSize:11, lineHeight:1 }}>✕</button>}
    </div>
  );
}

/* ─── Profile role editor (inline in profile dropdown header) */
function ProfileNameEditor({ name, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(name);
  useEffect(() => { if (!editing) setVal(name); }, [name, editing]);
  function save() { const n = val.trim(); if (n && n !== name) onSave(n); setEditing(false); }
  if (editing) {
    return (
      <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:2 }}>
        <input autoFocus value={val} onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") save(); if(e.key==="Escape") setEditing(false); }}
          style={{ flex:1, background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:4, color:"#fff", fontSize:13, fontWeight:700, padding:"3px 8px", outline:"none", fontFamily:"inherit" }}/>
        <button onClick={save} style={{ background:AC.purple, border:"none", borderRadius:4, color:"#fff", fontSize:10, fontWeight:700, padding:"3px 8px", cursor:"pointer" }}>✓</button>
        <button onClick={()=>setEditing(false)} style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:4, color:"rgba(255,255,255,0.6)", fontSize:10, padding:"3px 6px", cursor:"pointer" }}>✕</button>
      </div>
    );
  }
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
      <span style={{ fontSize:14, fontWeight:700, color:"#fff" }}>{name}</span>
      <button onClick={()=>{ setVal(name); setEditing(true); }}
        style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:3, color:"rgba(255,255,255,0.45)", fontSize:9, padding:"1px 6px", cursor:"pointer", fontWeight:600 }}>✎</button>
    </div>
  );
}

function ProfileRoleEditor({ role, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(role);

  function save() {
    const r = val.trim();
    if (r) onSave(r);
    setEditing(false);
  }

  if (editing) {
    return (
      <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:2 }}>
        <input
          autoFocus
          value={val}
          onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") save(); if(e.key==="Escape") setEditing(false); }}
          style={{ flex:1, background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:4, color:"#fff", fontSize:11, padding:"3px 8px", outline:"none", fontFamily:"inherit" }}
        />
        <button onClick={save}
          style={{ background:AC.purple, border:"none", borderRadius:4, color:"#fff", fontSize:10, fontWeight:700, padding:"3px 8px", cursor:"pointer" }}>✓</button>
        <button onClick={()=>setEditing(false)}
          style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:4, color:"rgba(255,255,255,0.6)", fontSize:10, padding:"3px 6px", cursor:"pointer" }}>✕</button>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
      <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)" }}>{role}</span>
      <button onClick={()=>{ setVal(role); setEditing(true); }}
        style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:3, color:"rgba(255,255,255,0.45)", fontSize:9, padding:"1px 6px", cursor:"pointer", fontWeight:600 }}>
        ✎ Edit
      </button>
    </div>
  );
}

/* ─── Inline shift editor (used inside profile dropdown) ──── */
function ShiftEditor({ member, teamColor, onSave }) {
  const [editing, setEditing]       = useState(false);
  const [localShift, setLocalShift] = useState(member.shift||"flexible");
  const [localStart, setLocalStart] = useState(member.shiftStart||"09:00");
  const [localEnd,   setLocalEnd]   = useState(member.shiftEnd||"17:00");
  const [localDays,  setLocalDays]  = useState(member.days||["Mon","Tue","Wed","Thu","Fri"]);

  const shift = SHIFTS.find(s=>s.id===(member.shift||"flexible"))||SHIFTS[4];

  function pickPreset(s) {
    setLocalShift(s.id);
    if (s.id!=="flexible") { const [a,b]=s.time.split(" – "); setLocalStart(a); setLocalEnd(b); }
  }

  function save() { onSave(localShift, localStart, localEnd, localDays); setEditing(false); }

  const inp = { background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:11, padding:"4px 7px", outline:"none", fontFamily:"monospace", colorScheme:"light", flex:1 };

  return (
    <div style={{ borderBottom:"1px solid var(--border)" }}>
      {/* Collapsed view */}
      <div style={{ padding:"10px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:editing?10:6 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.08em", textTransform:"uppercase" }}>My Shift</div>
          <button onClick={()=>setEditing(v=>!v)}
            style={{ background:editing?AC.purple:AC.offwhite, border:`1px solid ${editing?AC.purple:AC.border}`, borderRadius:4, color:editing?"#fff":AC.textSecond, padding:"2px 9px", cursor:"pointer", fontSize:10, fontWeight:600 }}>
            {editing ? "Cancel" : "✎ Edit"}
          </button>
        </div>

        {!editing ? (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:7 }}>
              <span style={{ fontSize:13 }}>{shift.icon}</span>
              <span style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{shift.label}</span>
              <span style={{ fontSize:11, color:"var(--text-muted)", fontFamily:"monospace" }}>{member.shiftStart||"–"} – {member.shiftEnd||"–"}</span>
            </div>
            <div style={{ display:"flex", gap:3 }}>
              {DAYS.map(d=>(
                <span key={d} style={{ width:22, height:18, borderRadius:3, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, background:(member.days||[]).includes(d)?teamColor:AC.offwhite, color:(member.days||[]).includes(d)?"#fff":AC.textMuted, border:`1px solid ${(member.days||[]).includes(d)?teamColor:AC.border}` }}>{d[0]}</span>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Preset buttons */}
            <div style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600, marginBottom:5 }}>SHIFT TYPE</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:10 }}>
              {SHIFTS.map(s=>(
                <button key={s.id} onClick={()=>pickPreset(s)}
                  style={{ background:localShift===s.id?teamColor:AC.offwhite, border:`1.5px solid ${localShift===s.id?teamColor:AC.border}`, borderRadius:5, padding:"4px 8px", cursor:"pointer", fontSize:10, color:localShift===s.id?"#fff":AC.textSecond, fontWeight:localShift===s.id?700:400 }}>
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
            {/* Time inputs */}
            <div style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600, marginBottom:5 }}>HOURS</div>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
              <span style={{ fontSize:10, color:"var(--text-muted)", flexShrink:0 }}>From</span>
              <input type="time" value={localStart} onChange={e=>setLocalStart(e.target.value)} style={inp}/>
              <span style={{ color:"var(--text-muted)", fontSize:11 }}>→</span>
              <span style={{ fontSize:10, color:"var(--text-muted)", flexShrink:0 }}>To</span>
              <input type="time" value={localEnd} onChange={e=>setLocalEnd(e.target.value)} style={inp}/>
            </div>
            {/* Days */}
            <div style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600, marginBottom:5 }}>WORKING DAYS</div>
            <div style={{ display:"flex", gap:3, marginBottom:10 }}>
              {DAYS.map(d=>(
                <button key={d} onClick={()=>setLocalDays(ds=>ds.includes(d)?ds.filter(x=>x!==d):[...ds,d])}
                  style={{ width:28, height:24, borderRadius:4, border:`1.5px solid ${localDays.includes(d)?teamColor:AC.border}`, background:localDays.includes(d)?teamColor:AC.offwhite, color:localDays.includes(d)?"#fff":AC.textSecond, fontSize:9, fontWeight:700, cursor:"pointer" }}>
                  {d[0]}
                </button>
              ))}
            </div>
            {/* Preview + save */}
            <div style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, padding:"6px 10px", marginBottom:8, fontSize:11, color:"var(--text-second)" }}>
              {SHIFTS.find(s=>s.id===localShift)?.icon} {SHIFTS.find(s=>s.id===localShift)?.label} · {localStart} – {localEnd}
            </div>
            <button onClick={save}
              style={{ width:"100%", background:AC.purple, border:"none", borderRadius:4, color:"#fff", padding:"8px 0", fontSize:12, fontWeight:600, cursor:"pointer" }}>
              Save Changes
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Status prompt screen (shown on every login) ────────── */
function StatusPromptScreen({ user, teams, todayDay, onConfirm }) {
  const member    = teams.flatMap(t=>(t.members||[]).map(m=>({...m,teamColor:t.color,teamName:t.name}))).find(m=>m.name===user.name);
  const teamColor = member?.teamColor || AC.purple;

  const [selStatus,   setSelStatus]   = useState(member?.status     || "present");
  const [leaveUntil,  setLeaveUntil]  = useState(member?.leaveUntil || "");
  const [showShift,   setShowShift]   = useState(false);
  const [selShift,    setSelShift]    = useState(member?.shift       || "flexible");
  const [shiftStart,  setShiftStart]  = useState(member?.shiftStart  || "09:00");
  const [shiftEnd,    setShiftEnd]    = useState(member?.shiftEnd    || "17:00");
  const [selDays,     setSelDays]     = useState(member?.days        || ["Mon","Tue","Wed","Thu","Fri"]);

  const SELECTABLE   = MEMBER_STATUSES.filter(s => s.id !== "rest_day");
  const selectedSm   = MEMBER_STATUSES.find(s=>s.id===selStatus) || MEMBER_STATUSES[0];
  const selectedShift = SHIFTS.find(s=>s.id===selShift) || SHIFTS[4];

  function pickPreset(s) {
    setSelShift(s.id);
    if (s.id !== "flexible") { const [a,b]=s.time.split(" – "); setShiftStart(a); setShiftEnd(b); }
  }

  const tInp = { background:"transparent", border:"none", outline:"none", fontFamily:"monospace", fontSize:14, fontWeight:600, color:"var(--text-primary)", colorScheme:"light", flex:1, padding:0 };

  return (
    <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif", padding:"20px 0", overflowY:"auto" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{ width:480, maxWidth:"95vw" }}>

        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, justifyContent:"center" }}>
          <div style={{ width:30, height:30, background:AC.purple, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ color:"#fff", fontSize:16, fontWeight:900 }}>{">"}</span>
          </div>
          <span style={{ color:"#fff", fontSize:20, fontWeight:700, letterSpacing:"0.04em" }}>MYTASK</span>
        </div>

        <div style={{ background:"var(--bg)", borderRadius:10, overflow:"hidden", boxShadow:"0 24px 60px rgba(0,0,0,0.4)" }}>

          {/* Header */}
          <div style={{ background:AC.navyMid, padding:"20px 28px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background:AC.purple, border:"2px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:16, fontWeight:700, flexShrink:0 }}>
                {user.name.split(" ").map(n=>n[0]).join("")}
              </div>
              <div>
                <div style={{ color:"#fff", fontSize:16, fontWeight:700, marginBottom:2 }}>
                  Good {new Date().getHours()<12?"morning":new Date().getHours()<17?"afternoon":"evening"}, {user.name.split(" ")[0]}!
                </div>
                <div style={{ color:"rgba(255,255,255,0.5)", fontSize:12 }}>
                  {user.role}{member?` · ${member.teamName}`:""} · Today is {todayDay}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding:"24px 28px" }}>

            {/* Status label */}
            <div style={{ fontSize:15, fontWeight:700, color:"var(--text-primary)", marginBottom:4 }}>What's your status today?</div>
            <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:16 }}>Let your team know how you're available. You can change this anytime from your profile.</div>

            {/* Status grid */}
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:20 }}>
              {SELECTABLE.map(s => {
                const isActive = selStatus===s.id;
                return (
                  <button key={s.id} onClick={()=>setSelStatus(s.id)}
                    style={{ background:isActive?s.bg:AC.offwhite, border:`2px solid ${isActive?s.border:AC.border}`, borderRadius:8, padding:"11px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left", transition:"all 0.1s" }}>
                    <div style={{ width:34, height:34, borderRadius:"50%", background:isActive?s.dot+"22":"rgba(0,0,0,0.04)", border:`2px solid ${isActive?s.dot:AC.borderMid}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>
                      {s.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:isActive?700:500, color:isActive?s.color:"var(--text-primary)" }}>{s.label}</div>
                      <div style={{ fontSize:11, color:"var(--text-muted)" }}>{s.note}</div>
                    </div>
                    <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${isActive?s.dot:AC.borderMid}`, background:isActive?s.dot:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {isActive && <span style={{ color:"#fff", fontSize:10, fontWeight:700 }}>✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* On leave — date range input */}
            {selStatus === "on_leave" && (
              <div style={{ background:"#E6F0FF", border:"1.5px solid #B3CFFF", borderRadius:8, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:16 }}>✈</span>
                  <div style={{ fontSize:13, fontWeight:700, color:AC.info }}>On leave details</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:5 }}>Leave until</div>
                    <input type="date" value={leaveUntil} onChange={e=>setLeaveUntil(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      style={{ background:"var(--bg)", border:`1px solid #B3CFFF`, borderRadius:6, color:"var(--text-primary)", fontSize:13, padding:"8px 12px", outline:"none", fontFamily:"inherit", width:"100%", boxSizing:"border-box", colorScheme:"light" }}/>
                  </div>
                  {leaveUntil && (
                    <div style={{ background:"var(--bg)", border:`1px solid #B3CFFF`, borderRadius:6, padding:"8px 12px", flexShrink:0, textAlign:"center" }}>
                      <div style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600, marginBottom:2 }}>DURATION</div>
                      <div style={{ fontSize:13, fontWeight:700, color:AC.info }}>
                        {Math.max(1, Math.round((new Date(leaveUntil)-new Date().setHours(0,0,0,0))/86400000)+1)}d
                      </div>
                    </div>
                  )}
                </div>
                {!leaveUntil && <div style={{ fontSize:11, color:"#2684FF", marginTop:8, opacity:0.7 }}>Select a return date so your team knows when you're back</div>}
              </div>
            )}

            {/* Optional shift section */}
            <div style={{ border:`1.5px solid ${showShift?teamColor:AC.border}`, borderRadius:8, overflow:"hidden", marginBottom:20, transition:"border-color 0.15s" }}>
              {/* Toggle header */}
              <button onClick={()=>setShowShift(v=>!v)}
                style={{ width:"100%", background:showShift?teamColor+"12":AC.offwhite, border:"none", padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, textAlign:"left" }}>
                <span style={{ fontSize:16 }}>{selectedShift.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:showShift?teamColor:"var(--text-primary)" }}>
                    {showShift ? "Edit my shift" : `My shift: ${selectedShift.label} · ${shiftStart} – ${shiftEnd}`}
                  </div>
                  {!showShift && <div style={{ fontSize:11, color:"var(--text-muted)" }}>Optional — tap to update your hours for today</div>}
                </div>
                <span style={{ color:showShift?teamColor:AC.textMuted, fontSize:12, fontWeight:700, transform:showShift?"rotate(180deg)":"none", transition:"transform 0.15s", display:"inline-block" }}>▾</span>
              </button>

              {/* Expanded shift editor */}
              {showShift && (
                <div style={{ padding:"16px 16px 20px", borderTop:"1px solid var(--border)" }}>
                  {/* Preset pills */}
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Shift type</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:16 }}>
                    {SHIFTS.map(s=>(
                      <button key={s.id} onClick={()=>pickPreset(s)}
                        style={{ background:selShift===s.id?teamColor:AC.offwhite, border:`2px solid ${selShift===s.id?teamColor:AC.border}`, borderRadius:6, padding:"7px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"all 0.1s" }}>
                        <span style={{ fontSize:14 }}>{s.icon}</span>
                        <div>
                          <div style={{ fontSize:12, fontWeight:selShift===s.id?700:500, color:selShift===s.id?"#fff":"var(--text-primary)" }}>{s.label}</div>
                          <div style={{ fontSize:10, color:selShift===s.id?"rgba(255,255,255,0.65)":AC.textMuted, fontFamily:"monospace" }}>{s.time}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Time inputs */}
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Shift hours</div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"9px 12px" }}>
                      <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:700, flexShrink:0 }}>FROM</span>
                      <input type="time" value={shiftStart} onChange={e=>setShiftStart(e.target.value)} style={tInp}/>
                    </div>
                    <span style={{ color:"var(--text-muted)", fontSize:14 }}>→</span>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"9px 12px" }}>
                      <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:700, flexShrink:0 }}>TO</span>
                      <input type="time" value={shiftEnd} onChange={e=>setShiftEnd(e.target.value)} style={tInp}/>
                    </div>
                  </div>

                  {/* Working days */}
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Working days</div>
                  <div style={{ display:"flex", gap:5 }}>
                    {DAYS.map(d=>(
                      <button key={d} onClick={()=>setSelDays(ds=>ds.includes(d)?ds.filter(x=>x!==d):[...ds,d])}
                        style={{ flex:1, padding:"7px 0", borderRadius:5, border:`2px solid ${selDays.includes(d)?teamColor:AC.border}`, background:selDays.includes(d)?teamColor:AC.offwhite, color:selDays.includes(d)?"#fff":AC.textSecond, fontSize:11, fontWeight:selDays.includes(d)?700:400, cursor:"pointer", transition:"all 0.1s" }}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm button */}
            <button onClick={()=>onConfirm(selStatus, leaveUntil, selShift, shiftStart, shiftEnd, selDays)}
              style={{ width:"100%", background:AC.purple, border:"none", borderRadius:6, color:"#fff", padding:"13px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              Confirm & Enter Workspace
            </button>

          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:14, fontSize:11, color:"rgba(255,255,255,0.25)" }}>
          MyTask · Work Management Platform
        </div>
      </div>
    </div>
  );
}

/* ─── Registered users ───────────────────────────────────── */
const REGISTERED_USERS = [
  { id:"u1",  name:"Jackie Chen",     role:"Lead Designer",    team:"t1", email:"jackie.chen@mytask.io",     password:"jackie123"    },
  { id:"u2",  name:"Jeff Patel",   role:"UI Designer",      team:"t1", email:"jeff.patel@mytask.io",   password:"jeff123"   },
  { id:"u3",  name:"Tom Eriksson",  role:"Motion Designer",  team:"t1", email:"tom.eriksson@mytask.io",  password:"tom123"     },
  { id:"u4",  name:"James Park",    role:"Tech Lead",        team:"t2", email:"james.park@mytask.io",    password:"james123"   },
  { id:"u5",  name:"Lin Wei",       role:"Backend Eng.",     team:"t2", email:"lin.wei@mytask.io",       password:"lin123"     },
  { id:"u6",  name:"Sara Müller",   role:"Frontend Eng.",    team:"t2", email:"sara.muller@mytask.io",   password:"sara123"    },
  { id:"u7",  name:"Dev Sharma",    role:"DevOps",           team:"t2", email:"dev.sharma@mytask.io",    password:"dev123"     },
  { id:"u8",  name:"Sofia Reyes",   role:"Marketing Lead",   team:"t3", email:"sofia.reyes@mytask.io",   password:"sofia123"   },
  { id:"u9",  name:"Kai Tanaka",    role:"Content Writer",   team:"t3", email:"kai.tanaka@mytask.io",    password:"kai123"     },
  { id:"u10", name:"Nadia Osei",    role:"Ops Manager",      team:"t4", email:"nadia.osei@mytask.io",    password:"nadia123"   },
  { id:"u11", name:"Carlos Ibarra", role:"Product Manager",  team:"t5", email:"carlos.ibarra@mytask.io", password:"carlos123"  },
  { id:"u12", name:"Yuna Kim",      role:"Product Analyst",  team:"t5", email:"yuna.kim@mytask.io",      password:"yuna123"    },
];

/* ─── Login screen ───────────────────────────────────────── */
function LoginScreen({ onLogin }) {
  const [tab,setTab]           = useState("login");
  const [signupDone, setSignupDone] = useState(false);
  const [email,setEmail]       = useState("");
  const [password,setPassword] = useState("");
  const [name,setName]         = useState("");
  const [role,setRole]         = useState("");
  const [error,setError]       = useState("");
  const [loading,setLoading]   = useState(false);
  const [showPw,setShowPw]     = useState(false);

  // Detect invite or password reset from URL hash OR query params (Supabase sends both formats)
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);
  const isInvite   = hash.includes("type=invite")   || searchParams.get("type") === "invite";
  const isRecovery = hash.includes("type=recovery") || searchParams.get("type") === "recovery";
  const [showSetPassword, setShowSetPassword] = useState(isInvite || isRecovery);
  const [newPassword,  setNewPassword]  = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [setPwError,   setSetPwError]   = useState("");
  const [setPwLoading, setSetPwLoading] = useState(false);
  const [setPwDone,    setSetPwDone]    = useState(false);
  const [inviteName,   setInviteName]   = useState("");
  const [inviteRole,   setInviteRole]   = useState("Member");

  async function handleSetPassword() {
    const _h = window.location.hash; const _s = new URLSearchParams(window.location.search);
    const isInviteFlow = _h.includes("type=invite") || _s.get("type") === "invite";
    if (isInviteFlow && !inviteName.trim()) { setSetPwError("Please enter your name."); return; }
    if (!newPassword || newPassword.length < 6) { setSetPwError("Password must be at least 6 characters."); return; }
    if (newPassword !== newPassword2) { setSetPwError("Passwords don't match."); return; }
    setSetPwLoading(true); setSetPwError("");
    try {
      // Set password + update name/role in auth metadata
      const updates = { password: newPassword };
      if (isInviteFlow) updates.data = { name: inviteName.trim(), role: inviteRole };
      const { error: e } = await supabase.auth.updateUser(updates);
      if (e) throw e;
      // Also save name/role to profiles table for invited users
      if (isInviteFlow) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.from('profiles').upsert({
            id: session.user.id,
            name: inviteName.trim(),
            role: inviteRole,
            email: session.user.email,
          }, { onConflict: 'id' });
        }
      }
      window.history.replaceState(null, "", window.location.pathname);
      setSetPwDone(true);
    } catch(e) { setSetPwError(e.message || "Could not set password. Please try again."); }
    setSetPwLoading(false);
  }

  async function handleLogin() {
    if (!email.trim() || !password) { setError("Email and password are required."); return; }
    setLoading(true); setError("");
    try {
      const user = await signIn(email.trim(), password);
      onLogin(user);
    } catch(e) { setError(e.message || "Incorrect email or password."); }
    setLoading(false);
  }

  async function handleSignUp() {
    if (!name.trim() || !email.trim() || !password) { setError("Name, email and password are required."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim(), password,
        options: { data: { name: name.trim(), role: role.trim() || "Member" } }
      });
      if (signUpError) throw signUpError;
      setSignupDone(true);
    } catch(e) { setError(e.message || "Sign up failed. Try a different email."); }
    setLoading(false);
  }

  // ── FORGOT PASSWORD ──────────────────────────────────────
  const [showForgot,    setShowForgot]    = useState(false);
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotSent,    setForgotSent]    = useState(false);
  const [forgotError,   setForgotError]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  async function handleForgotPassword() {
    if (!forgotEmail.trim()) { setForgotError("Please enter your email address."); return; }
    setForgotLoading(true); setForgotError("");
    try {
      const { error: e } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: window.location.origin + window.location.pathname,
      });
      if (e) throw e;
      setForgotSent(true);
    } catch(e) { setForgotError(e.message || "Could not send reset email. Check the address."); }
    setForgotLoading(false);
  }

  // ── SET PASSWORD SCREEN (invite or recovery) ─────────────
  if (showSetPassword) {
    const isInviteFlow = hash.includes("type=invite") || new URLSearchParams(window.location.search).get("type") === "invite";
    return (
      <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
        <div style={{ width:420, maxWidth:"94vw" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36, justifyContent:"center" }}>
            <div style={{ width:36, height:36, background:AC.purple, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ color:"#fff", fontSize:20, fontWeight:900, lineHeight:1 }}>{">"}</span>
            </div>
            <span style={{ color:"#fff", fontSize:22, fontWeight:700, letterSpacing:"0.04em" }}>MYTASK</span>
          </div>
          <div style={{ background:"var(--bg)", borderRadius:8, overflow:"hidden", boxShadow:"0 24px 60px rgba(0,0,0,0.4)", padding:"28px 32px" }}>
            {setPwDone ? (
              <div style={{ textAlign:"center", padding:"16px 0" }}>
                <div style={{ fontSize:40, marginBottom:16 }}>🎉</div>
                <div style={{ fontSize:17, fontWeight:700, color:"var(--text-primary)", marginBottom:8 }}>
                  {isInviteFlow ? "Welcome to MyTask!" : "Password updated!"}
                </div>
                <div style={{ fontSize:13, color:AC.textMuted, marginBottom:24, lineHeight:1.6 }}>
                  {isInviteFlow
                    ? "Your account is ready. You can now sign in anytime with your email and password."
                    : "Your new password has been saved. You can now sign in."}
                </div>
                <button onClick={async ()=>{
                  const { data:{ session } } = await supabase.auth.getSession();
                  if (session) {
                    const user = { id:session.user.id, name:session.user.user_metadata?.name||session.user.email.split("@")[0], email:session.user.email, role:session.user.user_metadata?.role||"Member" };
                    onLogin(user);
                  } else { setShowSetPassword(false); }
                }} style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14 }}>
                  {isInviteFlow ? "Enter MyTask →" : "Go to Sign In →"}
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:18, fontWeight:700, color:"var(--text-primary)", marginBottom:6 }}>
                    {isInviteFlow ? "👋 Welcome! Set your password" : "Set new password"}
                  </div>
                  <div style={{ fontSize:13, color:AC.textMuted, lineHeight:1.5 }}>
                    {isInviteFlow
                      ? "You have been invited to MyTask. Create a password to secure your account."
                      : "Choose a new password for your account."}
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  {isInviteFlow && (
                    <>
                      <div>
                        <label style={LBL}>Your full name</label>
                        <input style={INP} type="text" placeholder="e.g. Sam Hauser"
                          value={inviteName} onChange={e=>{setInviteName(e.target.value);setSetPwError("");}} autoFocus/>
                      </div>
                      <div>
                        <label style={LBL}>Your role</label>
                        <input style={INP} type="text" placeholder="e.g. Policy Analyst, Team Lead…"
                          value={inviteRole} onChange={e=>{setInviteRole(e.target.value);setSetPwError("");}}/>
                      </div>
                    </>
                  )}
                  <div>
                    <label style={LBL}>{isInviteFlow ? "Create password" : "New password"}</label>
                    <div style={{ position:"relative" }}>
                      <input style={{ ...INP, paddingRight:40 }} type={showPw?"text":"password"}
                        placeholder="At least 6 characters" value={newPassword}
                        onChange={e=>{setNewPassword(e.target.value);setSetPwError("");}}
                        onKeyDown={e=>e.key==="Enter"&&handleSetPassword()} autoFocus/>
                      <button onClick={()=>setShowPw(v=>!v)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--text-muted)", fontSize:14 }}>
                        {showPw?"🙈":"👁"}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={LBL}>Confirm password</label>
                    <input style={INP} type="password" placeholder="Re-enter your password"
                      value={newPassword2}
                      onChange={e=>{setNewPassword2(e.target.value);setSetPwError("");}}
                      onKeyDown={e=>e.key==="Enter"&&handleSetPassword()}/>
                  </div>
                  {setPwError && <div style={{ fontSize:12, color:AC.danger, background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, padding:"8px 12px" }}>{setPwError}</div>}
                  <button onClick={handleSetPassword} disabled={setPwLoading}
                    style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14, opacity:setPwLoading?0.7:1 }}>
                    {setPwLoading ? "Saving…" : isInviteFlow ? "Set password & continue" : "Set new password"}
                  </button>
                </div>
              </>
            )}
          </div>
          <div style={{ textAlign:"center", marginTop:20, fontSize:12, color:"rgba(255,255,255,0.3)" }}>MyTask · Work Management Platform</div>
        </div>
      </div>
    );
  }

  // ── FORGOT PASSWORD SCREEN ───────────────────────────────
  if (showForgot) return (
    <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{ width:420, maxWidth:"94vw" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36, justifyContent:"center" }}>
          <div style={{ width:36, height:36, background:AC.purple, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ color:"#fff", fontSize:20, fontWeight:900, lineHeight:1 }}>{">"}</span>
          </div>
          <span style={{ color:"#fff", fontSize:22, fontWeight:700, letterSpacing:"0.04em" }}>MYTASK</span>
        </div>
        <div style={{ background:"var(--bg)", borderRadius:8, overflow:"hidden", boxShadow:"0 24px 60px rgba(0,0,0,0.4)", padding:"28px 32px" }}>
          {forgotSent ? (
            <div style={{ textAlign:"center", padding:"16px 0" }}>
              <div style={{ fontSize:40, marginBottom:16 }}>📧</div>
              <div style={{ fontSize:16, fontWeight:700, color:"var(--text-primary)", marginBottom:8 }}>Check your email</div>
              <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.6, marginBottom:20 }}>
                We sent a password reset link to<br/>
                <strong style={{ color:"var(--text-primary)" }}>{forgotEmail}</strong><br/><br/>
                Click the link in the email to set a new password.
              </div>
              <button onClick={()=>{setShowForgot(false);setForgotSent(false);setForgotEmail("");}}
                style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14 }}>
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:18, fontWeight:700, color:"var(--text-primary)", marginBottom:6 }}>Forgot your password?</div>
                <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Enter your email and we'll send you a reset link.</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div>
                  <label style={LBL}>Work email</label>
                  <input style={INP} type="email" placeholder="you@yourcompany.com"
                    value={forgotEmail} autoFocus
                    onChange={e=>{setForgotEmail(e.target.value);setForgotError("");}}
                    onKeyDown={e=>e.key==="Enter"&&handleForgotPassword()}/>
                </div>
                {forgotError && <div style={{ fontSize:12, color:AC.danger, background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, padding:"8px 12px" }}>{forgotError}</div>}
                <button onClick={handleForgotPassword} disabled={forgotLoading}
                  style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14, opacity:forgotLoading?0.7:1 }}>
                  {forgotLoading ? "Sending…" : "Send Reset Link"}
                </button>
                <button onClick={()=>{setShowForgot(false);setForgotError("");}}
                  style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:13, textAlign:"center" }}>
                  ← Back to Sign In
                </button>
              </div>
            </>
          )}
        </div>
        <div style={{ textAlign:"center", marginTop:20, fontSize:12, color:"rgba(255,255,255,0.3)" }}>MyTask · Work Management Platform</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{ width:420, maxWidth:"94vw" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36, justifyContent:"center" }}>
          <div style={{ width:36, height:36, background:AC.purple, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ color:"#fff", fontSize:20, fontWeight:900, lineHeight:1 }}>{">"}</span>
          </div>
          <span style={{ color:"#fff", fontSize:22, fontWeight:700, letterSpacing:"0.04em" }}>MYTASK</span>
        </div>
        <div style={{ background:"var(--bg)", borderRadius:8, overflow:"hidden", boxShadow:"0 24px 60px rgba(0,0,0,0.4)" }}>
          <div style={{ display:"flex", borderBottom:"1px solid var(--border)" }}>
            {[["login","Sign In"],["signup","Create Account"]].map(([t,l])=>(
              <button key={t} onClick={()=>{setTab(t);setError("");}}
                style={{ flex:1, background:tab===t?"var(--bg)":"var(--bg-off)", border:"none", padding:"14px 0", fontSize:13, fontWeight:tab===t?700:400, color:tab===t?AC.purple:AC.textMuted, cursor:"pointer", borderBottom:tab===t?`2px solid ${AC.purple}`:"2px solid transparent" }}>
                {l}
              </button>
            ))}
          </div>
          <div style={{ padding:"28px 32px" }}>
            {signupDone ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:40, marginBottom:16 }}>📧</div>
                <div style={{ fontSize:16, fontWeight:700, color:"var(--text-primary)", marginBottom:8 }}>Check your email</div>
                <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.6, marginBottom:20 }}>
                  We sent a confirmation link to<br/>
                  <strong style={{ color:"var(--text-primary)" }}>{email}</strong><br/><br/>
                  Click the link in the email, then come back and sign in.
                </div>
                <button onClick={()=>{ setSignupDone(false); setTab("login"); }}
                  style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14 }}>
                  Go to Sign In
                </button>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {tab==="signup" && (
                <>
                  <div>
                    <label style={LBL}>Full name</label>
                    <input style={INP} placeholder="Your full name" value={name}
                      onChange={e=>{setName(e.target.value);setError("");}}
                      onKeyDown={e=>e.key==="Enter"&&handleSignUp()}/>
                  </div>
                  <div>
                    <label style={LBL}>Role (optional)</label>
                    <input style={INP} placeholder="e.g. Designer, Engineer, Manager" value={role}
                      onChange={e=>setRole(e.target.value)}/>
                  </div>
                </>
              )}
              <div>
                <label style={LBL}>Work email</label>
                <input style={INP} type="email" placeholder="you@yourcompany.com" value={email}
                  onChange={e=>{setEmail(e.target.value);setError("");}}
                  onKeyDown={e=>e.key==="Enter"&&(tab==="login"?handleLogin():handleSignUp())}/>
              </div>
              <div>
                <label style={LBL}>Password</label>
                <div style={{ position:"relative" }}>
                  <input style={{ ...INP, paddingRight:40 }} type={showPw?"text":"password"}
                    placeholder={tab==="signup"?"At least 6 characters":"Your password"}
                    value={password}
                    onChange={e=>{setPassword(e.target.value);setError("");}}
                    onKeyDown={e=>e.key==="Enter"&&(tab==="login"?handleLogin():handleSignUp())}/>
                  <button onClick={()=>setShowPw(v=>!v)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--text-muted)", fontSize:14 }}>
                    {showPw?"🙈":"👁"}
                  </button>
                </div>
              </div>
              {error && <div style={{ fontSize:12, color:AC.danger, background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, padding:"8px 12px", lineHeight:1.5 }}>{error}</div>}
              <button onClick={tab==="login"?handleLogin:handleSignUp} disabled={loading}
                style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14, marginTop:4, opacity:loading?0.7:1 }}>
                {loading ? "Please wait…" : tab==="login" ? "Sign In" : "Create Account"}
              </button>
              {tab==="login" && (
                <button onClick={()=>{setShowForgot(true);setForgotEmail(email);setForgotError("");setForgotSent(false);}}
                  style={{ background:"none", border:"none", color:AC.purple, cursor:"pointer", fontSize:12, textAlign:"center", padding:0 }}>
                  Forgot your password?
                </button>
              )}
              </div>
            )}
            {!signupDone && tab==="login" && (
              <div style={{ marginTop:20, paddingTop:16, borderTop:"1px solid var(--border)", fontSize:12, color:"var(--text-muted)", textAlign:"center" }}>
                Don't have an account?{" "}
                <button onClick={()=>{setTab("signup");setError("");}} style={{ background:"none", border:"none", color:AC.purple, fontWeight:600, cursor:"pointer", fontSize:12 }}>
                  Create one free →
                </button>
              </div>
            )}
          </div>
        </div>
        <div style={{ textAlign:"center", marginTop:20, fontSize:12, color:"rgba(255,255,255,0.3)" }}>MyTask · Work Management Platform</div>
      </div>
    </div>
  );
}


function makeSampleTasks() {
return [

  // ── t1: Design Team ─────────────────────────────────────
  { id:genId(), title:"Redesign onboarding flow",        description:"Full redesign of the user onboarding experience based on Q1 UX research findings. Focus on reducing drop-off at step 3.", team:"t1", assignees:["Jackie Chen","Jeff Patel"], priority:"High",     status:"In Progress", dueDate:"2026-04-25", links:["https://figma.com/file/onboarding","https://docs.google.com/document/d/ux-research"], comments:[{id:genId(),author:"Jackie Chen",tag:"update",text:"Frames 1–4 done, working on the confirmation screen now.",at:new Date(Date.now()-1000*60*90).toISOString()},{id:genId(),author:"Jeff Patel",tag:"question",text:"Should we include the team invite step in onboarding or keep it post-signup?",at:new Date(Date.now()-1000*60*30).toISOString()}] },
  { id:genId(), title:"Component library audit",         description:"Audit all existing design components for consistency. Flag anything that diverges from the new design system tokens.", team:"t1", assignees:["Jeff Patel"],             priority:"Medium",   status:"To Do",       dueDate:"2026-04-29", links:["https://figma.com/file/components"], comments:[] },
  { id:genId(), title:"Mobile app icon refresh",         description:"Update all 24 app icons to match the new brand guidelines. Export at 1x, 2x and 3x.",                                 team:"t1", assignees:["Tom Eriksson"],          priority:"Low",      status:"In Progress", dueDate:"2026-05-02", links:["https://figma.com/file/icons"], comments:[{id:genId(),author:"Tom Eriksson",tag:"update",text:"Completed 18/24 icons. The notification and settings ones need a second pass.",at:new Date(Date.now()-1000*60*60*3).toISOString()}] },
  { id:genId(), title:"Dashboard dark mode",             description:"Design dark mode variants for all dashboard components. Coordinate with engineering on CSS variable implementation.", team:"t1", assignees:["Jackie Chen"],           priority:"High",     status:"Review",      dueDate:"2026-04-23", links:["https://figma.com/file/darkmode","https://github.com/org/repo/issues/88"], comments:[{id:genId(),author:"Jackie Chen",tag:"done",text:"All screens exported and shared with Sara for implementation review.",at:new Date(Date.now()-1000*60*60).toISOString()}] },
  { id:genId(), title:"Landing page hero redesign",      description:"Redesign the hero section of the marketing landing page for the Q2 campaign launch.",                                 team:"t1", assignees:["Jeff Patel","Jackie Chen"], priority:"Critical", status:"To Do",       dueDate:"2026-04-22", links:["https://figma.com/file/landing","https://notion.so/brand-guidelines"], comments:[] },
  { id:genId(), title:"Accessibility audit — dashboard", description:"Run WCAG 2.1 AA audit on dashboard screens. Document all contrast failures and keyboard navigation gaps.",            team:"t1", assignees:["Tom Eriksson"],          priority:"Medium",   status:"Done",        dueDate:"2026-04-17", links:[], comments:[{id:genId(),author:"Tom Eriksson",tag:"done",text:"Audit complete. Found 6 contrast issues, all documented in the Notion doc.",at:new Date(Date.now()-1000*60*60*24*2).toISOString()}] },
  { id:genId(), title:"Illustration set for empty states",description:"Create 8 custom illustrations for empty state screens (no tasks, no notifications, etc.).",                          team:"t1", assignees:["Jackie Chen"],           priority:"Low",      status:"To Do",       dueDate:"2026-05-07", links:["https://figma.com/file/illustrations"], comments:[] },
  { id:genId(), title:"Design sprint — Q3 features",     description:"Facilitate 3-day design sprint for Q3 feature set. Prepare materials, recruit participants, synthesise outputs.",     team:"t1", assignees:["Jackie Chen","Jeff Patel","Tom Eriksson"], priority:"High", status:"To Do", dueDate:"2026-05-12", links:["https://miro.com/board/sprint"], comments:[] },

  // ── t2: Engineering Team ─────────────────────────────────
  { id:genId(), title:"Fix auth token expiry bug",        description:"Users are silently logged out on mobile after 30 min idle. Token refresh logic is failing on Android Chrome.",       team:"t2", assignees:["James Park","Lin Wei"],  priority:"Critical", status:"In Progress", dueDate:"2026-04-22", links:["https://github.com/org/repo/issues/42","https://linear.app/issue/ENG-204"], comments:[{id:genId(),author:"James Park",tag:"blocker",text:"Root cause found — the refresh endpoint returns 401 when the user's timezone offset changes. Tricky edge case.",at:new Date(Date.now()-1000*60*45).toISOString()},{id:genId(),author:"Lin Wei",tag:"update",text:"Wrote a fix, needs James to review before merge.",at:new Date(Date.now()-1000*60*15).toISOString()}] },
  { id:genId(), title:"Set up CI/CD pipeline",            description:"Configure GitHub Actions for automated testing (Jest + Cypress) and deployment to staging and prod on merge.",       team:"t2", assignees:["Lin Wei","Dev Sharma"],  priority:"High",     status:"Done",        dueDate:"2026-04-18", links:["https://github.com/org/repo/actions"], comments:[{id:genId(),author:"Dev Sharma",tag:"done",text:"Pipeline is live. Deploy to staging on every PR, prod on merge to main. Avg build time 4m 12s.",at:new Date(Date.now()-1000*60*60*24*3).toISOString()}] },
  { id:genId(), title:"Database query optimisation",      description:"Several dashboard queries are taking 2–4 seconds. Profile and optimise the top 5 slowest queries identified in Datadog.", team:"t2", assignees:["Lin Wei"],        priority:"High",     status:"In Progress", dueDate:"2026-04-26", links:["https://github.com/org/repo/issues/61"], comments:[{id:genId(),author:"Lin Wei",tag:"update",text:"Indexes added to tasks.team_id and tasks.assignee. Query time dropped from 2.4s to 320ms.",at:new Date(Date.now()-1000*60*60*2).toISOString()}] },
  { id:genId(), title:"Implement dark mode API",          description:"Add CSS variable switching to support dark mode. Coordinate with design team on token values.",                       team:"t2", assignees:["Sara Müller"],          priority:"Medium",   status:"To Do",       dueDate:"2026-04-30", links:["https://github.com/org/repo/issues/77","https://figma.com/file/darkmode"], comments:[] },
  { id:genId(), title:"Migrate to PostgreSQL 16",         description:"Upgrade from PostgreSQL 14 to 16 in all environments. Test all queries against the new planner.",                    team:"t2", assignees:["Dev Sharma","Lin Wei"], priority:"High",     status:"Review",      dueDate:"2026-04-24", links:["https://github.com/org/repo/pull/312"], comments:[{id:genId(),author:"Dev Sharma",tag:"update",text:"Staging migration complete. All tests passing. Waiting for James to approve prod migration window.",at:new Date(Date.now()-1000*60*60*5).toISOString()}] },
  { id:genId(), title:"Push notification service",        description:"Build backend for push notifications — in-app and mobile. Use websockets for real-time delivery.",                  team:"t2", assignees:["Sara Müller","James Park"], priority:"Medium", status:"To Do",       dueDate:"2026-05-05", links:["https://linear.app/issue/ENG-218"], comments:[] },
  { id:genId(), title:"API rate limiting",                description:"Implement rate limiting on all public API endpoints to prevent abuse. 100 req/min per token.",                       team:"t2", assignees:["James Park"],           priority:"Medium",   status:"In Progress", dueDate:"2026-04-28", links:["https://github.com/org/repo/pull/298"], comments:[{id:genId(),author:"James Park",tag:"update",text:"Middleware written, deploying to staging today for testing.",at:new Date(Date.now()-1000*60*120).toISOString()}] },
  { id:genId(), title:"Unit test coverage to 80%",        description:"Current coverage is at 54%. Identify untested modules and bring the suite to 80% coverage.",                         team:"t2", assignees:["Sara Müller","Lin Wei"], priority:"Low",      status:"In Progress", dueDate:"2026-05-10", links:[], comments:[{id:genId(),author:"Sara Müller",tag:"update",text:"Coverage now at 67%. Focusing on auth and API modules this week.",at:new Date(Date.now()-1000*60*60*6).toISOString()}] },

  // ── t3: Marketing Team ───────────────────────────────────
  { id:genId(), title:"Q2 social media campaign brief",   description:"Write the full creative brief for Q2 social campaign. Include audience segments, key messages, channel strategy and KPIs.", team:"t3", assignees:["Sofia Reyes"],   priority:"High",     status:"Review",      dueDate:"2026-04-23", links:["https://notion.so/q2-brief","https://docs.google.com/document/d/brief"], comments:[{id:genId(),author:"Sofia Reyes",tag:"done",text:"Brief submitted to Kai for copy review and Carlos for stakeholder sign-off.",at:new Date(Date.now()-1000*60*60*4).toISOString()}] },
  { id:genId(), title:"Write 4 product blog posts",       description:"April content calendar: product updates, engineering blog, customer story, thought leadership piece.",                team:"t3", assignees:["Kai Tanaka"],          priority:"Medium",   status:"In Progress", dueDate:"2026-04-30", links:["https://notion.so/content-calendar"], comments:[{id:genId(),author:"Kai Tanaka",tag:"update",text:"Posts 1 and 2 drafted. Post 3 (engineering) needs input from James Park.",at:new Date(Date.now()-1000*60*60*2).toISOString()},{id:genId(),author:"Sofia Reyes",tag:"question",text:"Can we get a quote from a customer for post 4? Ping Nadia.",at:new Date(Date.now()-1000*60*60).toISOString()}] },
  { id:genId(), title:"Email nurture sequence",           description:"Design a 5-email nurture sequence for trial users who haven't converted. Work with design on templates.",            team:"t3", assignees:["Kai Tanaka","Sofia Reyes"], priority:"High",   status:"To Do",       dueDate:"2026-05-01", links:["https://notion.so/nurture-sequence"], comments:[] },
  { id:genId(), title:"Influencer outreach — April",      description:"Identify and reach out to 15 relevant SaaS influencers for partnership opportunities in Q2.",                        team:"t3", assignees:["Sofia Reyes"],          priority:"Low",      status:"Done",        dueDate:"2026-04-19", links:[], comments:[{id:genId(),author:"Sofia Reyes",tag:"done",text:"12 outreach emails sent, 4 positive replies. Moving 2 to contract stage.",at:new Date(Date.now()-1000*60*60*24*2).toISOString()}] },
  { id:genId(), title:"Webinar: product demo May",        description:"Plan and host a live product demo webinar for 200+ registered attendees. Coordinate with product team.",             team:"t3", assignees:["Sofia Reyes","Kai Tanaka"], priority:"High",   status:"In Progress", dueDate:"2026-05-08", links:["https://notion.so/webinar-plan"], comments:[{id:genId(),author:"Kai Tanaka",tag:"update",text:"Landing page live, 87 registrations so far. Recording intro video this week.",at:new Date(Date.now()-1000*60*60*3).toISOString()}] },
  { id:genId(), title:"Competitor analysis report",       description:"Analyse 6 top competitors. Evaluate positioning, pricing, feature set and recent product moves.",                   team:"t3", assignees:["Kai Tanaka"],          priority:"Medium",   status:"To Do",       dueDate:"2026-05-06", links:["https://notion.so/competitor-analysis"], comments:[] },
  { id:genId(), title:"Update website case studies",      description:"Refresh 3 existing case studies with updated metrics and new customer quotes. Coordinate with design for layout.",  team:"t3", assignees:["Sofia Reyes"],          priority:"Medium",   status:"Review",      dueDate:"2026-04-25", links:["https://notion.so/case-studies"], comments:[{id:genId(),author:"Sofia Reyes",tag:"blocker",text:"Waiting on updated metrics from Nadia's ops team. Chased twice.",at:new Date(Date.now()-1000*60*60*5).toISOString()}] },

  // ── t4: Operations Team ──────────────────────────────────
  { id:genId(), title:"Q1 financial close reconciliation",description:"Reconcile all Q1 accounts. Prepare summary for CFO review by April 22.",                                            team:"t4", assignees:["Nadia Osei"],           priority:"Critical", status:"In Progress", dueDate:"2026-04-22", links:["https://docs.google.com/spreadsheets/d/q1-finance"], comments:[{id:genId(),author:"Nadia Osei",tag:"update",text:"75% reconciled. A few vendor invoices still outstanding from March.",at:new Date(Date.now()-1000*60*60*2).toISOString()}] },
  { id:genId(), title:"Vendor contract renewals",         description:"Review and renew 8 vendor contracts due in May. Negotiate improved SLAs where possible.",                           team:"t4", assignees:["Nadia Osei"],           priority:"High",     status:"To Do",       dueDate:"2026-04-29", links:["https://docs.google.com/spreadsheets/d/vendors"], comments:[] },
  { id:genId(), title:"Onboard 3 new hires — May",        description:"Prepare onboarding packs, set up accounts, schedule orientation sessions for 3 new team members starting May 5.",  team:"t4", assignees:["Nadia Osei"],           priority:"High",     status:"To Do",       dueDate:"2026-05-02", links:["https://notion.so/onboarding"], comments:[] },
  { id:genId(), title:"Office lease renewal discussion",  description:"Prepare comparison of current lease vs. 3 alternatives. Present options to leadership by end of April.",           team:"t4", assignees:["Nadia Osei"],           priority:"Medium",   status:"In Progress", dueDate:"2026-04-28", links:["https://docs.google.com/presentation/d/lease"], comments:[{id:genId(),author:"Nadia Osei",tag:"update",text:"Shortlisted 2 alternatives. Scheduling site visits for next Tuesday.",at:new Date(Date.now()-1000*60*60*8).toISOString()}] },
  { id:genId(), title:"Update security policies",         description:"Review and update data security policies ahead of SOC2 audit in June. Coordinate with engineering.",              team:"t4", assignees:["Nadia Osei"],           priority:"High",     status:"Review",      dueDate:"2026-04-26", links:["https://notion.so/security-policy"], comments:[{id:genId(),author:"Nadia Osei",tag:"question",text:"James — does engineering have a data retention policy documented? Need it for section 4.",at:new Date(Date.now()-1000*60*60*3).toISOString()}] },
  { id:genId(), title:"Monthly ops report — April",       description:"Compile the monthly ops report: headcount, costs, vendor SLA performance, IT incidents.",                          team:"t4", assignees:["Nadia Osei"],           priority:"Medium",   status:"To Do",       dueDate:"2026-04-30", links:[], comments:[] },

  // ── t5: Product Team ─────────────────────────────────────
  { id:genId(), title:"Q2/Q3 product roadmap",            description:"Update and finalise the product roadmap for Q2 and Q3 incorporating feedback from sales, support and leadership.", team:"t5", assignees:["Carlos Ibarra","Yuna Kim"], priority:"High",   status:"In Progress", dueDate:"2026-04-25", links:["https://miro.com/board/roadmap","https://notion.so/roadmap-doc"], comments:[{id:genId(),author:"Carlos Ibarra",tag:"update",text:"Core Q2 features locked. Working on Q3 prioritisation with Yuna this week.",at:new Date(Date.now()-1000*60*60*3).toISOString()},{id:genId(),author:"Yuna Kim",tag:"note",text:"Attaching latest NPS data to inform Q3 priority ranking.",at:new Date(Date.now()-1000*60*60).toISOString()}] },
  { id:genId(), title:"User interview programme — May",   description:"Schedule and conduct 12 user interviews for the May research sprint. Focus on power-user workflows.",             team:"t5", assignees:["Yuna Kim"],             priority:"Medium",   status:"To Do",       dueDate:"2026-05-03", links:["https://notion.so/research-plan"], comments:[] },
  { id:genId(), title:"Feature spec: team mentions",      description:"Write detailed spec for @mentions in task comments. Include edge cases, notification logic and API contract.",    team:"t5", assignees:["Carlos Ibarra"],        priority:"High",     status:"Review",      dueDate:"2026-04-24", links:["https://notion.so/mentions-spec","https://linear.app/issue/PROD-88"], comments:[{id:genId(),author:"Carlos Ibarra",tag:"done",text:"Spec complete. Shared with James and Sara for engineering estimation.",at:new Date(Date.now()-1000*60*60*2).toISOString()},{id:genId(),author:"Yuna Kim",tag:"question",text:"Should mentions trigger email notifications or just in-app? Spec is ambiguous on this.",at:new Date(Date.now()-1000*60*60).toISOString()}] },
  { id:genId(), title:"Pricing page A/B test analysis",   description:"Analyse results of the 3-week A/B test on the pricing page. Write up findings and recommendation.",             team:"t5", assignees:["Yuna Kim"],             priority:"High",     status:"Done",        dueDate:"2026-04-18", links:["https://docs.google.com/spreadsheets/d/ab-test"], comments:[{id:genId(),author:"Yuna Kim",tag:"done",text:"Variant B won with +18% conversion. Recommendation to ship is in the doc.",at:new Date(Date.now()-1000*60*60*24*3).toISOString()}] },
  { id:genId(), title:"Competitive positioning workshop", description:"Facilitate a half-day workshop with product and marketing to update competitive positioning.",                    team:"t5", assignees:["Carlos Ibarra","Yuna Kim"], priority:"Medium", status:"To Do",       dueDate:"2026-05-06", links:["https://miro.com/board/positioning"], comments:[] },
  { id:genId(), title:"Release notes — v2.4",             description:"Write release notes for the v2.4 launch. Coordinate with engineering on change log and with marketing on comms.", team:"t5", assignees:["Carlos Ibarra"],        priority:"Medium",   status:"In Progress", dueDate:"2026-04-27", links:["https://notion.so/release-notes"], comments:[{id:genId(),author:"Carlos Ibarra",tag:"update",text:"First draft done, waiting on final feature list from James (dark mode + rate limiting).",at:new Date(Date.now()-1000*60*90).toISOString()}] },
  { id:genId(), title:"Churn analysis — Q1",              description:"Deep-dive into Q1 churn. Identify top 3 cancellation reasons, segment by plan and tenure, and propose remediation.", team:"t5", assignees:["Yuna Kim"],           priority:"Critical", status:"In Progress", dueDate:"2026-04-23", links:["https://docs.google.com/spreadsheets/d/churn-q1"], comments:[{id:genId(),author:"Yuna Kim",tag:"blocker",text:"Need access to Stripe cancellation reason data — Carlos can you request from ops?",at:new Date(Date.now()-1000*60*60*4).toISOString()}] },
];
}

/* ─── Global theme accessor (updated by App on each render) ── */
/* ─── Shared input styles ────────────────────────────────── */
const INP = { background:"var(--bg)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"inherit", width:"100%", boxSizing:"border-box" };
const LBL = { fontSize:11, color:"var(--text-second)", fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase", display:"block", marginBottom:5 };
const BTN_PRIMARY = { background:"var(--purple)", border:"none", borderRadius:4, color:"#fff", padding:"9px 20px", fontSize:13, fontWeight:600, cursor:"pointer", letterSpacing:"0.02em" };
const BTN_GHOST   = { background:"transparent", border:"1.5px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"9px 18px", fontSize:13, fontWeight:500, cursor:"pointer" };
/* ─── Helper: always returns assignees as an array ─────── */
function getAssignees(task) {
  if (!task) return [];
  if (Array.isArray(task.assignees)) return task.assignees;
  if (task.assignee) return [task.assignee];
  return [];
}

/* ═══════════════════════════════════════════════════════════
   APP
══════════════════════════════════════════════════════════════ */
function loadSaved(key, fallback) {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : fallback; }
  catch { return fallback; }
}

/* ─── Password Recovery / Invite Screen ─────────────────── */
function RecoveryScreen({ onDone }) {
  const isInvite = window.location.hash.includes("type=invite");
  const [password,  setPassword]  = useState("");
  const [password2, setPassword2] = useState("");
  const [showPw,    setShowPw]    = useState(false);
  const [error,     setError]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);

  async function handleSave() {
    if (!password || password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== password2) { setError("Passwords don't match."); return; }
    setLoading(true); setError("");
    try {
      const { error: e } = await supabase.auth.updateUser({ password });
      if (e) throw e;
      setDone(true);
    } catch(e) { setError(e.message || "Could not set password. Please try again."); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{ width:420, maxWidth:"94vw" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36, justifyContent:"center" }}>
          <div style={{ width:36, height:36, background:AC.purple, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ color:"#fff", fontSize:20, fontWeight:900, lineHeight:1 }}>{">"}</span>
          </div>
          <span style={{ color:"#fff", fontSize:22, fontWeight:700, letterSpacing:"0.04em" }}>MYTASK</span>
        </div>
        <div style={{ background:"var(--bg)", borderRadius:8, boxShadow:"0 24px 60px rgba(0,0,0,0.4)", padding:"28px 32px" }}>
          {done ? (
            <div style={{ textAlign:"center", padding:"16px 0" }}>
              <div style={{ fontSize:40, marginBottom:16 }}>{isInvite ? "🎉" : "✅"}</div>
              <div style={{ fontSize:17, fontWeight:700, color:"var(--text-primary)", marginBottom:8 }}>
                {isInvite ? "Welcome to MyTask!" : "Password updated!"}
              </div>
              <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:24, lineHeight:1.6 }}>
                {isInvite
                  ? "Your account is set up. You can now sign in with your email and password."
                  : "Your new password has been saved. You can now sign in."}
              </div>
              <button onClick={onDone} style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14 }}>
                {isInvite ? "Go to Sign In →" : "Go to Sign In →"}
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:18, fontWeight:700, color:"var(--text-primary)", marginBottom:6 }}>
                  {isInvite ? "👋 Welcome! Set your password" : "Set new password"}
                </div>
                <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>
                  {isInvite
                    ? "You've been invited to MyTask. Create a password to secure your account."
                    : "Choose a new password for your MyTask account."}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div>
                  <label style={LBL}>{isInvite ? "Create password" : "New password"}</label>
                  <div style={{ position:"relative" }}>
                    <input style={{ ...INP, paddingRight:40 }} type={showPw?"text":"password"}
                      placeholder="At least 6 characters" value={password} autoFocus
                      onChange={e=>{setPassword(e.target.value);setError("");}}
                      onKeyDown={e=>e.key==="Enter"&&handleSave()}/>
                    <button onClick={()=>setShowPw(v=>!v)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--text-muted)", fontSize:14 }}>
                      {showPw?"🙈":"👁"}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={LBL}>Confirm password</label>
                  <input style={INP} type="password" placeholder="Re-enter your password"
                    value={password2}
                    onChange={e=>{setPassword2(e.target.value);setError("");}}
                    onKeyDown={e=>e.key==="Enter"&&handleSave()}/>
                </div>
                {error && <div style={{ fontSize:12, color:AC.danger, background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, padding:"8px 12px" }}>{error}</div>}
                <button onClick={handleSave} disabled={loading}
                  style={{ ...BTN_PRIMARY, width:"100%", padding:"12px 0", fontSize:14, opacity:loading?0.7:1 }}>
                  {loading ? "Saving…" : isInvite ? "Set password & continue" : "Set New Password"}
                </button>
              </div>
            </>
          )}
        </div>
        <div style={{ textAlign:"center", marginTop:20, fontSize:12, color:"rgba(255,255,255,0.3)" }}>MyTask · Work Management Platform</div>
      </div>
    </div>
  );
}

/* ── Chat Messages — auto-scrolls to latest on new message or reopen ── */
function ChatMessages({ messages, chatLoading, currentUser }) {
  const bottomRef = React.useRef(null);
  React.useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, chatLoading]);
  return (
    <div style={{ flex:1, overflowY:"auto", padding:"12px", display:"flex", flexDirection:"column", gap:10, minHeight:0 }}>
      {messages.map((m,i) => (
        <div key={i} style={{ display:"flex", gap:8, justifyContent:m.role==="user"?"flex-end":"flex-start" }}>
          {m.role==="assistant" && (
            <div style={{ width:24, height:24, background:AC.purple, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, flexShrink:0, marginTop:2 }}>✨</div>
          )}
          <div style={{
            maxWidth:"82%", padding:"8px 12px", borderRadius:10,
            borderTopLeftRadius: m.role==="assistant"?2:10,
            borderTopRightRadius: m.role==="user"?2:10,
            background: m.role==="user"?AC.purple:"var(--bg-off)",
            color: m.role==="user"?"#fff":"var(--text-primary)",
            fontSize:12, lineHeight:1.6, whiteSpace:"pre-wrap"
          }}>
            {m.text}
          </div>
          {m.role==="user" && <Avatar name={currentUser.name} size={24}/>}
        </div>
      ))}
      {chatLoading && (
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <div style={{ width:24, height:24, background:AC.purple, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11 }}>✨</div>
          <div style={{ background:"var(--bg-off)", borderRadius:10, borderTopLeftRadius:2, padding:"8px 12px", fontSize:12, color:"var(--text-muted)" }}>Thinking…</div>
        </div>
      )}
      <div ref={bottomRef}/>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [teams,setTeams]   = useState([]);
  const [tasks,setTasks]   = useState([]);
  const [darkMode, setDarkMode] = useState(()=>{ try{ return JSON.parse(localStorage.getItem('tf_dark')||'false'); }catch{return false;} });
  const [appLoading, setAppLoading] = useState(true);
  const [showRecovery, setShowRecovery] = useState(false);

  // Apply theme whenever darkMode changes
  useEffect(() => {
    injectThemeVars(darkMode);
    try { localStorage.setItem('tf_dark', JSON.stringify(darkMode)); } catch {}
  }, [darkMode]);

  // ── On mount: check existing session ──────────────────
  useEffect(() => {
    // Check if this is a password recovery or invite redirect FIRST
    if (window.location.hash.includes("type=recovery")) {
      setShowRecovery(true);
      setAppLoading(false);
      return;
    }
    // Invite links: sign out any auto-session so LoginScreen shows SetPassword screen
    if (window.location.hash.includes("type=invite") || new URLSearchParams(window.location.search).get("type") === "invite") {
      // Don't auto-login — keep user on the set-password screen
      supabase.auth.getSession(); // let Supabase process the token silently (no await needed)
      setAppLoading(false);
      return;
    }

    getSession().then(user => {
      if (user) {
        setCurrentUser(user);
        loadAllData();
      }
      setAppLoading(false);
    }).catch(() => setAppLoading(false));

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') { setCurrentUser(null); setTeams([]); setTasks([]); }
      if (event === 'PASSWORD_RECOVERY') { setShowRecovery(true); setAppLoading(false); }
      // When Supabase processes an invite token it fires SIGNED_IN — ignore it, keep on set-password screen
      if (event === 'SIGNED_IN') {
        const isInviteUrl = window.location.hash.includes("type=invite") || new URLSearchParams(window.location.search).get("type") === "invite";
        if (isInviteUrl) return; // Don't auto-login on invite
      }
    });

    // Real-time: meetings sync across all users instantly
    const meetingsChannel = supabase
      .channel('meetings-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meetings' }, async () => {
        const fresh = await fetchMeetings();
        if (fresh) setMeetings(fresh);
      })
      .subscribe();

    // Real-time: re-fetch teams whenever any team row changes (status/shift updates from other users)
    const teamsChannel = supabase
      .channel('teams-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'teams' }, async () => {
        const fresh = await fetchTeams();
        if (fresh && fresh.length) setTeams(fresh);
      })
      .subscribe();

    // Real-time: re-fetch tasks whenever any task row changes
    const tasksChannel = supabase
      .channel('tasks-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, async () => {
        const fresh = await fetchTasks();
        if (fresh) setTasks(fresh);
      })
      .subscribe();

    // Real-time: re-fetch notifications so bell updates instantly for all users
    const notifsChannel = supabase
      .channel('notifs-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, async () => {
        const fresh = await fetchNotifications();
        if (fresh) setNotifications(fresh);
      })
      .subscribe();

    // Real-time: activity feed updates instantly
    const activityChannel = supabase
      .channel('activity-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'activity' }, async () => {
        const fresh = await fetchActivity();
        if (fresh) setActivity(fresh);
      })
      .subscribe();

    // Real-time: task comments appear instantly
    const commentsChannel = supabase
      .channel('comments-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments' }, async () => {
        const fresh = await fetchTasks();
        if (fresh) setTasks(fresh);
      })
      .subscribe();

    // Real-time: profile name/role/status changes reflect immediately
    const profilesChannel = supabase
      .channel('profiles-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, async () => {
        await syncStatusFromProfiles();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
      supabase.removeChannel(meetingsChannel);
      supabase.removeChannel(teamsChannel);
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(notifsChannel);
      supabase.removeChannel(activityChannel);
      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, []);

  // Sync status from profiles table into teams state — the source of truth for status
  async function syncStatusFromProfiles() {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('name, status, shift, shift_start, shift_end, days');
    if (!profiles || !profiles.length) return;
    const profileMap = Object.fromEntries(profiles.map(p => [p.name, p]));
    setTeams(current => current.map(t => ({
      ...t,
      members: (t.members||[]).map(m => {
        const p = profileMap[m.name];
        if (!p || !p.status) return m;
        return {
          ...m,
          status:     p.status,
          shift:      p.shift      || m.shift,
          shiftStart: p.shift_start|| m.shiftStart,
          shiftEnd:   p.shift_end  || m.shiftEnd,
          days:       p.days       || m.days,
        };
      })
    })));
  }

  async function loadAllData() {
    try {
      const [teamsData, tasksData, notifsData, activityData, meetingsData] = await Promise.all([
        fetchTeams(), fetchTasks(), fetchNotifications(), fetchActivity(), fetchMeetings()
      ]);
      const baseTeams = teamsData.length ? teamsData : makeDefaultTeams();
      // Immediately merge latest profile statuses so we don't show stale status
      const { data: profiles } = await supabase.from('profiles').select('name, status, shift, shift_start, shift_end, days');
      if (profiles && profiles.length) {
        const profileMap = Object.fromEntries(profiles.map(p=>[p.name,p]));
        const merged = baseTeams.map(t=>({
          ...t,
          members:(t.members||[]).map(m=>{
            const p=profileMap[m.name];
            if(!p||!p.status) return m;
            return {...m, status:p.status, shift:p.shift||m.shift, shiftStart:p.shift_start||m.shiftStart, shiftEnd:p.shift_end||m.shiftEnd, days:p.days||m.days};
          })
        }));
        setTeams(merged);
      } else {
        setTeams(baseTeams);
      }
      setTasks(tasksData.length ? tasksData : []);
      setNotifications(notifsData);
      setActivity(activityData);
      setMeetings(meetingsData || []);
    } catch(e) {
      console.error("Failed to load data:", e);
      setTeams(makeDefaultTeams());
    }
  }

  // Show loading spinner while checking session

  const [filterTeam,setFilterTeam]     = useState("All");
  const [filterStatus,setFilterStatus] = useState("All");
  const [filterPriority,setFilterPriority] = useState("All"); // "All" | "Low" | "Medium" | "High" | "Critical"
  const [filterAssignee,setFilterAssignee] = useState("All");
  const [filterDateFrom,setFilterDateFrom] = useState("");
  const [filterDateTo,  setFilterDateTo]   = useState("");
  const [view,setView]     = useState("board");
  const [calMonth, setCalMonth] = useState(() => { const d=new Date(); return {y:d.getFullYear(),m:d.getMonth()}; });
  const [calSelected, setCalSelected] = useState(null);
  const [calView, setCalView] = useState("week"); // "month" | "week"
  const [calWeekStart, setCalWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0,0,0,0);
    const day = d.getDay()===0?6:d.getDay()-1;
    d.setDate(d.getDate()-day); return d;
  });
  const [meetings, setMeetings] = useState([]);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingFormData, setMeetingFormData] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

// ── Event reminder: check every minute for meetings starting in ~15 mins ──
  useEffect(() => {
    function checkReminders() {
      const now = new Date();
      const todayIso = now.toISOString().split("T")[0];
      const userName = currentUser?.name;
      if (!userName) return;
      const myTeamIds = new Set(teams.filter(t=>(t.members||[]).some(m=>m.name===userName)).map(t=>t.id));
      const todayMeetings = (meetings||[]).filter(m => {
        if (m.date !== todayIso) return false;
        const teamIds = m.teamIds?.length ? m.teamIds : (m.teamId ? [m.teamId] : []);
        const invitees = m.invitees || [];
        if (!teamIds.length && !invitees.length) return m.createdBy === userName;
        if (teamIds.some(tid => myTeamIds.has(tid))) return true;
        if (invitees.includes(userName) || m.createdBy === userName) return true;
        return false;
      });
      for (const m of todayMeetings) {
        if (dismissedReminders.current.has(m.id)) continue;
        const [h, min] = (m.startTime || "00:00").split(":").map(Number);
        const start = new Date(now); start.setHours(h, min, 0, 0);
        const diffMins = Math.round((start - now) / 60000);
        if (diffMins >= 13 && diffMins <= 15) {
          setEventReminder({ meeting: m, minsLeft: diffMins });
          return;
        }
      }
    }
    checkReminders();
    const interval = setInterval(checkReminders, 60000);
    return () => clearInterval(interval);
  }, [meetings, teams, currentUser]);
  const [showForm,setShowForm]         = useState(false);
  const [editTask,setEditTask]         = useState(null);
  const [showTeamMgr,setShowTeamMgr]   = useState(false);
  const [reassignTask,setReassignTask] = useState(null);
  const [navPage,setNavPage]           = useState("dashboard");
  const [showSearch,setShowSearch]     = useState(false);
  const [searchQuery,setSearchQuery]   = useState("");
  const [showProfileMenu,setShowProfileMenu] = useState(false);
  const [showStatusPrompt,setShowStatusPrompt] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [eventReminder, setEventReminder]   = useState(null); // { meeting, minsLeft }
  const dismissedReminders = React.useRef(new Set()); // track dismissed/snoozed ids
  const [showActivity,   setShowActivity]   = useState(false);
  const [selectedTask,   setSelectedTask]   = useState(null);
  const [userRole,       setUserRole]       = useState(null); // overrides currentUser.role when set
  const [userShift,      setUserShift]      = useState(null); // local shift for teamless users
  const [userStatus,     setUserStatus]     = useState(null); // local status for teamless users
  const lastActivityRef  = React.useRef(Date.now());       // track last user interaction
  const preAwayStatus    = React.useRef(null);             // status before auto-away kicked in
  const awayTimerRef     = React.useRef(null);             // setTimeout handle
  const currentStatusRef = React.useRef("present");        // always holds latest status for beforeunload
  const tabAwayTimerRef  = React.useRef(null);             // timer for tab-switch away
  const [awayBanner, setAwayBanner] = useState(null);      // "away" | "tab" | null

  // ── Notifications + Activity state ───────────────────
  const [notifications, setNotifications] = useState([]);
  const [activity, setActivity]           = useState([]);


  // AI Chat state
  const [showAIChat,   setShowAIChat]   = useState(false);
  const [chatMinimized,setChatMinimized]= useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role:"assistant", text:"Hi! I'm your MyTask AI assistant. Ask me anything — or tell me to make changes. Try:\n• \"Change Fix auth bug status to Done\"\n• \"Set my status to Busy\"\n• \"What's overdue?\"" }
  ]);
  const [chatInput,    setChatInput]    = useState("");
  const [chatLoading,  setChatLoading]  = useState(false);

  // ── Poll profiles status every 5 min as safety net (catches cron job updates) ──
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => syncStatusFromProfiles(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // ── Ping last_seen every 5 minutes while user is active ────────────────────
  // This keeps the server-side cron job informed so it can auto-set Away
  // when the user has been fully inactive (no tab open) for 35+ minutes
  useEffect(() => {
    if (!currentUser) return;

    function pingLastSeen() {
      // Only ping if tab is visible and status is an active one
      if (document.hidden) return;
      const autoOverridable = ["present","busy","training"];
      if (!autoOverridable.includes(currentStatusRef.current)) return;
      supabase.from('profiles')
        .update({ last_seen: new Date().toISOString() })
        .eq('id', currentUser.id)
        .then(() => {});
    }

    // Ping immediately on login
    pingLastSeen();

    // Then every 5 minutes
    const interval = setInterval(pingLastSeen, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // ── Set Away when tab/browser is closed (only for active statuses) ────────
  useEffect(() => {
    if (!currentUser) return;
    function handleUnload() {
      // Read status from ref — safe to use in beforeunload without dependency issues
      const autoAwayStatuses = ["present","busy","training"];
      if (!autoAwayStatuses.includes(currentStatusRef.current)) return;
      const url = `https://ffyianaswjowccewnxbf.supabase.co/rest/v1/profiles?id=eq.${currentUser.id}`;
      const body = JSON.stringify({ status: "away" });
      navigator.sendBeacon(url + "&apikey=" + (process.env.REACT_APP_SUPABASE_ANON_KEY || ""), new Blob([body], { type: "application/json" }));
    }
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [currentUser?.id]);

  // ── Auto-away (like Microsoft Teams) ────────────────────────────────────
  // 30 min idle → set status to "away" and show banner
  // Any activity → restore previous status immediately
  useEffect(() => {
    if (!currentUser) return;

    const AWAY_MS = 30 * 60 * 1000; // 30 minutes

    function getCurrentStatus() {
      const member = teams.flatMap(t=>(t.members||[]).find(m=>m.name===currentUser.name)).find(Boolean);
      return userStatus || member?.status || "present";
    }

    function applyStatus(statusId) {
      setUserStatus(statusId);
      const updatedTeams = teams.map(t=>({
        ...t,
        members:(t.members||[]).map(m=> m.name===currentUser.name ? {...m, status:statusId} : m)
      }));
      supabase.from('profiles').update({ status:statusId }).eq('id', currentUser.id).then(()=>{});
      updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name))
        .forEach(t => supabaseUpdateTeam(t));
      setTeams(updatedTeams);
    }

    function scheduleTimer() {
      clearTimeout(awayTimerRef.current);
      awayTimerRef.current = setTimeout(() => {
        const cur = getCurrentStatus();
        // Only auto-away if currently in an active status
        const autoOverridable = ["present","busy","training"];
        if (!autoOverridable.includes(cur)) return;
        preAwayStatus.current = cur;
        applyStatus("away");
        setAwayBanner("away");
      }, AWAY_MS);
    }

    function onActivity() {
      lastActivityRef.current = Date.now();
      // Restore status if we were auto-set to away
      if (awayBanner) {
        const restore = preAwayStatus.current || "present";
        preAwayStatus.current = null;
        applyStatus(restore);
        setAwayBanner(null);
      }
      scheduleTimer();
    }

    const EVENTS = ["mousemove","mousedown","keydown","touchstart","scroll","click"];
    EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    scheduleTimer();

    return () => {
      EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      clearTimeout(awayTimerRef.current);
    };
  }, [currentUser?.id]);

  // ── Tab switch → Away after 30 min ─────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;

    const TAB_AWAY_MS = 30 * 60 * 1000; // 30 min away from tab

    function applyStatus(statusId) {
      currentStatusRef.current = statusId;
      setUserStatus(statusId);
      const updatedTeams = teams.map(t=>({...t, members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,status:statusId}:m)}));
      setTeams(updatedTeams);
      supabase.from('profiles').update({status:statusId}).eq('id',currentUser.id).then(()=>{});
      updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        // User switched away — start the 30 min countdown
        tabAwayTimerRef.current = setTimeout(() => {
          const autoOverridable = ["present","busy","training"];
          if (!autoOverridable.includes(currentStatusRef.current)) return;
          preAwayStatus.current = currentStatusRef.current;
          applyStatus("away");
          setAwayBanner("tab");
        }, TAB_AWAY_MS);
      } else {
        // User came back to tab
        clearTimeout(tabAwayTimerRef.current);
        // If they were set to away by tab switch, show "I'm back?" banner
        // (if already showing, just leave it — user must confirm)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(tabAwayTimerRef.current);
    };
  }, [currentUser?.id]);

  // ── Rest day → Offline (on login + at midnight) ──────────────────────────
  useEffect(() => {
    if (!currentUser) return;

    function checkRestDay() {
      const todayDay = DAYS[new Date().getDay()===0 ? 6 : new Date().getDay()-1];
      const member = teams.flatMap(t=>(t.members||[])).find(m=>m.name===currentUser.name);
      const workingDays = member?.days || ["Mon","Tue","Wed","Thu","Fri"];
      const isRestDay = !workingDays.includes(todayDay);
      const autoOverridable = ["present","busy","training","away"];
      const cur = currentStatusRef.current;
      if (isRestDay && autoOverridable.includes(cur)) {
        currentStatusRef.current = "offline";
        setUserStatus("offline");
        const updatedTeams = teams.map(t=>({...t, members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,status:"offline"}:m)}));
        setTeams(updatedTeams);
        supabase.from('profiles').update({status:"offline"}).eq('id',currentUser.id).then(()=>{});
        updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
      }
    }

    // Check immediately on mount
    checkRestDay();

    // Schedule check at next midnight
    function scheduleMidnightCheck() {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0); // next midnight
      const msUntilMidnight = midnight - now;
      return setTimeout(() => {
        checkRestDay();
        // Re-schedule for the following midnight
        const interval = setInterval(checkRestDay, 24 * 60 * 60 * 1000);
        return () => clearInterval(interval);
      }, msUntilMidnight);
    }

    const midnightTimer = scheduleMidnightCheck();
    return () => clearTimeout(midnightTimer);
  }, [currentUser?.id]);

  if (appLoading) return (
    <div style={{ minHeight:"100vh", background:AC.navy, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:40, height:40, border:`3px solid ${AC.purple}`, borderTopColor:"transparent", borderRadius:"50%", margin:"0 auto 16px", animation:"spin 0.8s linear infinite" }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes slideUp{from{transform:translate(-50%,20px);opacity:0}to{transform:translate(-50%,0);opacity:1}}`}</style>
        <div style={{ color:"rgba(255,255,255,0.5)", fontSize:13 }}>Loading MyTask…</div>
      </div>
    </div>
  );

  // ── Password recovery screen ─────────────────────────
  if (showRecovery) return <RecoveryScreen onDone={()=>{ window.history.replaceState(null,"",window.location.pathname); setShowRecovery(false); }}/>;


  // Auto-save to localStorage whenever state changes
  // Supabase handles persistence — dark mode still uses localStorage

  // Helper — get the day name for today (Mon, Tue, etc.)
  const todayDay = DAYS[new Date().getDay()===0 ? 6 : new Date().getDay()-1];

  async function handleLoginSuccess(user) {
    // Load latest role from profiles table (user may have updated it)
    const { data: profile } = await supabase.from('profiles').select('role, name, status, shift, shift_start, shift_end, days').eq('id', user.id).single();
    const updatedUser = { ...user, role: profile?.role || user.role, name: profile?.name || user.name };
    setCurrentUser(updatedUser);
    setNavPage("dashboard");
    await loadAllData();
    // Restore saved status/shift from profiles (for teamless users and as override)
    if (profile?.status) setUserStatus(profile.status);
    if (profile?.shift)  setUserShift({ shift: profile.shift, start: profile.shift_start||"09:00", end: profile.shift_end||"17:00", days: profile.days||["Mon","Tue","Wed","Thu","Fri"] });

    // ── Due date notifications — once per day per task ──────────────────────
    const todayIso = new Date().toISOString().split("T")[0];
    const tomorrowIso = new Date(Date.now()+86400000).toISOString().split("T")[0];
    const notifiedKey = `notified_due_${updatedUser.id}_${todayIso}`;
    const alreadyNotified = new Set(JSON.parse(localStorage.getItem(notifiedKey)||"[]"));
    const allTasks = await fetchTasks();
    const myDueTasks = (allTasks||[]).filter(t =>
      getAssignees(t).includes(updatedUser.name) &&
      t.status !== "Done" &&
      (t.dueDate === todayIso || t.dueDate === tomorrowIso) &&
      !alreadyNotified.has(t.id)
    );
    for (const t of myDueTasks) {
      const isDueToday = t.dueDate === todayIso;
      await insertNotification(
        updatedUser.name,
        isDueToday ? "due" : "overdue",
        t.title,
        "MyTask",
        t.team||null
      );
      alreadyNotified.add(t.id);
    }
    if (myDueTasks.length > 0) {
      localStorage.setItem(notifiedKey, JSON.stringify([...alreadyNotified]));
      const fresh = await fetchNotifications();
      if (fresh) setNotifications(fresh);
    }
    // ────────────────────────────────────────────────────────────────────────

    const member = teams.flatMap(t=>(t.members||[])).find(m=>m.name===updatedUser.name);
    if (!member) { setShowStatusPrompt(true); return; }
    const workingDays = member.days || ["Mon","Tue","Wed","Thu","Fri"];
    if (!workingDays.includes(todayDay)) {
      const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===updatedUser.name?{...m,status:"rest_day"}:m)}));
      setTeams(updatedTeams);
      const myTeamsToSave = updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===updatedUser.name));
      await Promise.all(myTeamsToSave.map(t=>supabaseUpdateTeam(t)));
    } else {
      setShowStatusPrompt(true);
    }
  }

  // Show login screen
  // If invite link, always show LoginScreen (SetPassword screen) regardless of session
  const _isInviteUrl = window.location.hash.includes("type=invite") || new URLSearchParams(window.location.search).get("type") === "invite";
  if (!currentUser || _isInviteUrl) return <LoginScreen onLogin={handleLoginSuccess}/>;

  // Show status prompt before the main app
  if (showStatusPrompt) return (
    <StatusPromptScreen
      user={currentUser}
      teams={teams}
      todayDay={todayDay}
      onConfirm={(statusId, leaveUntil, shift, shiftStart, shiftEnd, days) => {
        // Always save to profiles so it persists across sessions (teamless users too)
        setUserStatus(statusId);
        setUserShift({ shift, start:shiftStart, end:shiftEnd, days });
        supabase.from('profiles').update({ status:statusId, shift, shift_start:shiftStart, shift_end:shiftEnd, days }).eq('id', currentUser.id).then(()=>{});
        // Also update team member records if user is in teams
        const updatedTeams = teams.map(t=>({
          ...t,
          members:(t.members||[]).map(m => m.name===currentUser.name
            ? {...m, status:statusId, leaveUntil: statusId==="on_leave"?leaveUntil:"", shift, shiftStart, shiftEnd, days}
            : m)
        }));
        setTeams(updatedTeams);
        const affectedTeams = updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name));
        affectedTeams.forEach(t => supabaseUpdateTeam(t));
        setShowStatusPrompt(false);
      }}
    />
  );

  const getTeam   = id => teams.find(t=>t.id===id);
  // Find the member profile for the logged-in user (first team match, for shift/status)
  const myMemberRaw = teams.flatMap(t=>(t.members||[]).map(m=>({...m,teamId:t.id,teamName:t.name,teamColor:t.color}))).find(m=>m.name===currentUser.name);
  // For teamless users, maintain shift/status in local state
  const myMember = myMemberRaw
    ? { ...myMemberRaw, ...(userShift ? {shift:userShift.shift, shiftStart:userShift.start, shiftEnd:userShift.end, days:userShift.days} : {}), ...(userStatus ? {status:userStatus} : {}) }
    : userShift || userStatus
      ? { id:currentUser.id, name:currentUser.name, shift:userShift?.shift||"flexible", shiftStart:userShift?.start||"09:00", shiftEnd:userShift?.end||"17:00", days:userShift?.days||["Mon","Tue","Wed","Thu","Fri"], status:userStatus||"present" }
      : null;
  // Keep ref in sync so beforeunload can read it safely
  currentStatusRef.current = myMember?.status || userStatus || "present";
  const myTeamObj = myMember ? getTeam(myMember.teamId) : null;
  const myTeamId  = myMember?.teamId || null;
  // ALL teams the current user belongs to
  const myTeams   = teams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name));
  const myTeamIds = new Set(myTeams.map(t=>t.id));

  // Tasks visible to this user — tasks from ANY of their teams + tasks assigned to them
  const visibleTasks = tasks.filter(t =>
    myTeamIds.has(t.team) || getAssignees(t).includes(currentUser.name)
  );

  const filtered = visibleTasks.filter(t => {
    if (filterTeam!=="All" && t.team!==filterTeam) return false;
    if (filterStatus!=="All" && t.status!==filterStatus) return false;
    if (filterPriority!=="All" && t.priority!==filterPriority) return false;
    // Assignee/team scope filter
    if (filterAssignee==="me") {
      if (!getAssignees(t).includes(currentUser.name)) return false;
    } else if (filterAssignee!=="All") {
      if (t.team!==filterAssignee) return false;
    }
    // Date range filter
    if (filterDateFrom || filterDateTo) {
      if (!t.dueDate) return false;
      const due = new Date(t.dueDate+"T00:00:00");
      if (filterDateFrom && due < new Date(filterDateFrom+"T00:00:00")) return false;
      if (filterDateTo   && due > new Date(filterDateTo+"T23:59:59"))   return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const tn = getTeam(t.team)?.name.toLowerCase()||"";
      const assigneeStr = getAssignees(t).join(" ").toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !assigneeStr.includes(q) && !tn.includes(q) && !(t.description||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const searchResults = searchQuery.length > 1 ? visibleTasks.filter(t => {
    const q = searchQuery.toLowerCase();
    const tn = getTeam(t.team)?.name.toLowerCase()||"";
    const assigneeStr = getAssignees(t).join(" ").toLowerCase();
    return t.title.toLowerCase().includes(q)||assigneeStr.includes(q)||tn.includes(q)||(t.description||"").toLowerCase().includes(q);
  }) : [];

  const eventSearchResults = searchQuery.length > 1 ? (meetings||[]).filter(m => {
    const q = searchQuery.toLowerCase();
    const teamNames = (m.teamIds||[]).map(tid=>teams.find(t=>t.id===tid)?.name||"").join(" ").toLowerCase();
    return (m.title||"").toLowerCase().includes(q) ||
           (m.notes||"").toLowerCase().includes(q) ||
           teamNames.includes(q) ||
           (m.invitees||[]).join(" ").toLowerCase().includes(q);
  }).filter(m => {
    // Only show events relevant to current user
    const teamIds = m.teamIds?.length ? m.teamIds : (m.teamId ? [m.teamId] : []);
    return m.createdBy === currentUser.name ||
           (m.invitees||[]).includes(currentUser.name) ||
           teamIds.some(tid => myTeamIds.has(tid));
  }) : [];

  const myNotifs     = notifications.filter(n=>n.userId===currentUser.name);
  const unreadCount  = myNotifs.filter(n=>!n.read).length;
  const statusCounts = STATUSES.reduce((acc,s)=>{acc[s]=visibleTasks.filter(t=>t.status===s).length;return acc;},{});

  async function addNotif(userId, type, taskTitle, from, teamId, taskId) {
    const notif = { id:genId(), userId, type, taskTitle, taskId:taskId||null, from:from||"System", teamId:teamId||null, read:false, at:new Date().toISOString() };
    setNotifications(ns=>[notif, ...ns]);
    await insertNotification(userId, type, taskTitle, from||"System", teamId||null);
  }
  async function addActivity(user, action, taskTitle, teamId) {
    const act = { id:genId(), user, action, taskTitle, teamId:teamId||null, at:new Date().toISOString() };
    setActivity(ac=>[act, ...ac.slice(0,49)]);
    await insertActivity(user, action, taskTitle, teamId||null);
  }
  async function markAllRead() {
    setNotifications(ns=>ns.map(n=>n.userId===currentUser.name?{...n,read:true}:n));
    await markAllNotificationsRead(currentUser.name);
  }
  function timeAgo(iso) {
    const diff = Math.floor((Date.now()-new Date(iso))/1000);
    if (diff<60)  return `${diff}s ago`;
    if (diff<3600) return `${Math.floor(diff/60)}m ago`;
    if (diff<86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  }
  const totalTasks   = visibleTasks.length;
  const doneTasks    = visibleTasks.filter(t=>t.status==="Done").length;
  const myTaskCount  = tasks.filter(t=>getAssignees(t).includes(currentUser.name)&&t.status!=="Done").length;

  async function saveTask(data) {
    const teamId = data.team === "personal" ? null : data.team;
    if (editTask) {
      const updated = {...editTask, ...data, team: teamId};
      setTasks(ts=>ts.map(t=>t.id===editTask.id ? updated : t));
      await updateTask(updated);
      addActivity(currentUser.name, "updated", data.title, teamId);
    } else {
      const newTask = await createTask({...data, team: teamId}, currentUser.id);
      setTasks(ts=>[...ts, newTask]);
      addActivity(currentUser.name, "created", data.title, teamId);
      if (teamId) {
        getAssignees(data).filter(a=>a!==currentUser.name).forEach(a=>
          addNotif(a, "assigned", data.title, currentUser.name, teamId, data.id||newTask?.id)
        );
      }
    }
    setShowForm(false); setEditTask(null);
  }
  async function handleStatusChange(id, newStatus) {
    const task = tasks.find(t=>t.id===id);
    if (!task) return;
    setTasks(ts=>ts.map(t=>t.id===id?{...t,status:newStatus}:t));
    await updateTask({...task, status:newStatus});
    addActivity(currentUser.name, `moved to ${newStatus}`, task.title, task.team);

    // Notify assignees (existing)
    getAssignees(task).filter(a=>a!==currentUser.name).forEach(a=>
      addNotif(a, "status", task.title, currentUser.name, task.team, task.id)
    );

    // Notify all team members who are NOT assignees and NOT the current user
    // so the whole team stays informed on task progress
    if (task.team) {
      const team = getTeam(task.team);
      const assigneeSet = new Set(getAssignees(task));
      (team?.members||[])
        .map(m=>m.name)
        .filter(n => n !== currentUser.name && !assigneeSet.has(n))
        .forEach(n => addNotif(n, "status", task.title, currentUser.name, task.team, task.id));
    }
  }
  async function handleReassign(tid, assignees, teamId) {
    const task = tasks.find(t=>t.id===tid);
    const prevAssignees = getAssignees(task);
    const updated = {...task, assignees, team:teamId, history:[...(task.history||[]),{from:prevAssignees.join(", "),to:assignees.join(", "),at:new Date().toISOString()}]};
    setTasks(ts=>ts.map(t=>t.id===tid ? updated : t));
    await updateTask(updated);
    if (task) {
      addActivity(currentUser.name, `reassigned to ${assignees.join(", ")}`, task.title, teamId);
      assignees.filter(a=>a!==currentUser.name).forEach(a=>
        addNotif(a, "reassigned", task.title, currentUser.name, teamId, task.id)
      );
    }
    setReassignTask(null);
  }
  async function handleDeleteComment(taskId, commentId) {
    // Update local state immediately
    setTasks(ts => ts.map(t =>
      t.id === taskId
        ? { ...t, comments: (t.comments||[]).filter(c => c.id !== commentId) }
        : t
    ));
    // Delete directly from task_comments table in Supabase
    await supabase.from('task_comments').delete().eq('id', commentId);
  }

  async function handleAddComment(taskId, text, tag) {
    const task = tasks.find(t=>t.id===taskId);
    if (!task) return;
    const comment = await addComment(taskId, currentUser.name, text, tag||"update");
    setTasks(ts=>ts.map(t=>t.id===taskId?{...t,comments:[...(t.comments||[]),comment]}:t));
    // @mention notifications — use live teams
    const mentioned = text.match(/@(\w+)/g)||[];
    const allMembers = teams.flatMap(t=>(t.members||[]).map(m=>m.name));
    mentioned.forEach(m => {
      const firstName = m.slice(1).toLowerCase();
      const mentionedName = allMembers.find(n=>n.toLowerCase().startsWith(firstName));
      if (mentionedName && mentionedName !== currentUser.name) {
        addNotif(mentionedName, "mention", task.title, currentUser.name, task.team, task.id);
        addActivity(currentUser.name, `mentioned ${mentionedName.split(" ")[0]} in`, task.title, task.team);
      }
    });
  }
  async function addTeam(team) {
    const t = await createTeam(team.name, team.color, currentUser.id);
    // Auto-add creator as a member so the team shows up in their myTeams list
    const withCreator = {
      ...t,
      createdBy: currentUser.name,
      members: [{
        id: genId(),
        name: currentUser.name,
        role: currentUser.role || "Member",
        shift: userShift?.shift || "flexible",
        shiftStart: userShift?.start || "09:00",
        shiftEnd: userShift?.end || "17:00",
        days: userShift?.days || ["Mon","Tue","Wed","Thu","Fri"],
        status: userStatus || "present",
        leaveUntil: "",
      }]
    };
    await supabaseUpdateTeam(withCreator);
    const fresh = await fetchTeams();
    if (fresh) setTeams(fresh);
    return withCreator;
  }
  async function updateTeamFn(t) {
    setTeams(ts=>ts.map(x=>x.id===t.id?t:x));
    await supabaseUpdateTeam(t);
    // Refresh teams to get real Supabase UUIDs for any newly inserted members
    const fresh = await fetchTeams();
    if (fresh) setTeams(fresh);
  }
  async function deleteTeamFn(id) {
    setTeams(ts=>ts.filter(t=>t.id!==id));
    setTasks(tk=>tk.filter(t=>t.team!==id));
    if(filterTeam===id) setFilterTeam("All");
    await supabaseDeleteTeam(id);
  }


  async function handleLogout() {
    // Only set Away on logout if current status is one that implies active presence
    const autoAwayStatuses = ["present","busy","training"];
    const currentStatus = myMember?.status || userStatus || "present";
    if (autoAwayStatuses.includes(currentStatus)) {
      try {
        const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,status:"away"}:m)}));
        await Promise.all([
          supabase.from('profiles').update({status:"away"}).eq('id',currentUser.id),
          ...updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).map(t=>supabaseUpdateTeam(t)),
        ]);
      } catch(e) { /* best-effort */ }
    }
    await signOut();
    setCurrentUser(null); setTeams([]); setTasks([]); setNotifications([]); setActivity([]);
    setNavPage("dashboard"); setShowProfileMenu(false); setShowNotifPanel(false);
    setShowActivity(false); setUserRole(null); setUserShift(null); setUserStatus(null); setShowAIChat(false); setChatMinimized(false);
    setAwayBanner(null); preAwayStatus.current = null;
    clearTimeout(awayTimerRef.current); clearTimeout(tabAwayTimerRef.current);
    setChatMessages([{ role:"assistant", text:"Hi! I'm your MyTask AI assistant. Ask me anything — or tell me to make changes." }]);
  }

  // Build a context string about the current user's tasks for Claude
  function buildTaskContext() {
    const myTasks = visibleTasks.filter(t => getAssignees(t).includes(currentUser.name));
    const overdue = myTasks.filter(t => t.dueDate && daysUntil(t.dueDate) < 0 && t.status !== "Done");
    const inProg  = myTasks.filter(t => t.status === "In Progress");
    const todo    = myTasks.filter(t => t.status === "To Do");
    const done    = myTasks.filter(t => t.status === "Done");

    // Notifications for current user
    const myNotifList = notifications.filter(n=>n.userId===currentUser.name);
    const unread = myNotifList.filter(n=>!n.read);
    const notifText = myNotifList.slice(0,10).map(n=>
      `[${n.read?"read":"UNREAD"}] ${n.type}: "${n.taskTitle}" from ${n.from} (${timeAgo(n.at)})`
    ).join("\n") || "none";

    // Recent activity scoped to user's teams
    const recentActivity = activity
      .filter(a => !a.teamId || myTeamIds.has(a.teamId))
      .slice(0, 15)
      .map(a => `${a.user} ${a.action} "${a.taskTitle}" in ${teams.find(t=>t.id===a.teamId)?.name||"?"} (${timeAgo(a.at)})`)
      .join("\n") || "none";

    const myStatusLabel = MEMBER_STATUSES.find(s=>s.id===(myMember?.status||"present"))?.label || "Present";
    const myShiftData   = SHIFTS.find(s=>s.id===(myMember?.shift||"flexible"))||SHIFTS[4];
    const myShiftLabel  = `${myShiftData.label} (${myMember?.shiftStart||"?"} – ${myMember?.shiftEnd||"?"}), days: ${(myMember?.days||[]).join(", ")||"not set"}`;
    const allMembersInfo = myTeams.flatMap(t=>(t.members||[]).map(m=>{
      const sm=MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
      const sh=SHIFTS.find(s=>s.id===(m.shift||"flexible"))||SHIFTS[4];
      return `  - ${m.name} [${t.name}] role:${m.role} status:${sm.label} shift:${sh.label} ${m.shiftStart||""}–${m.shiftEnd||""} days:${(m.days||[]).join(",")}${m.leaveUntil?` leave-until:${m.leaveUntil}`:""}`;
    })).join("\n")||"  none";

    return `Current user: ${currentUser.name} (${currentUser.role})
Teams: ${myTeams.map(t=>t.name).join(", ")}
Today: ${new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
My status: ${myStatusLabel}
My shift: ${myShiftLabel}

TEAM MEMBERS — STATUS & SHIFT:
${allMembersInfo}

MY TASKS (${myTasks.length} total):
- Overdue (${overdue.length}): ${overdue.map(t=>`"${t.title}" [${t.priority}] due ${t.dueDate}`).join("; ") || "none"}
- In Progress (${inProg.length}): ${inProg.map(t=>`"${t.title}" [${t.priority}]${t.dueDate?` due ${t.dueDate}`:""}`).join("; ") || "none"}
- To Do (${todo.length}): ${todo.map(t=>`"${t.title}" [${t.priority}]${t.dueDate?` due ${t.dueDate}`:""}`).join("; ") || "none"}
- Done (${done.length}): ${done.map(t=>`"${t.title}"`).join("; ") || "none"}

ALL TEAM TASKS (${visibleTasks.length} total):
${visibleTasks.map(t=>`- "${t.title}" [${t.status}][${t.priority}] assigned to ${getAssignees(t).join(",")||"nobody"} team:${getTeam(t.team)?.name||"?"}`).join("\n")}

MY NOTIFICATIONS (${myNotifList.length} total, ${unread.length} unread):
${notifText}

RECENT TEAM ACTIVITY:
${recentActivity}

MY CALENDAR — UPCOMING MEETINGS (next 14 days):
${(()=>{
  const now = new Date();
  const todayIso = now.toISOString().split("T")[0];
  const twoWeeks = new Date(now.getTime()+14*86400000).toISOString().split("T")[0];
  const upcoming = (meetings||[]).filter(m => {
    if (!m.date || m.date < todayIso || m.date > twoWeeks) return false;
    const teamIds = m.teamIds && m.teamIds.length ? m.teamIds : (m.teamId ? [m.teamId] : []);
    const invitees = m.invitees || [];
    return m.createdBy === currentUser.name ||
           invitees.includes(currentUser.name) ||
           teamIds.some(tid => myTeamIds.has(tid));
  }).sort((a,b)=> a.date===b.date ? (a.startTime||"").localeCompare(b.startTime||"") : a.date.localeCompare(b.date));
  if (!upcoming.length) return "  none in the next 14 days";
  return upcoming.map(m => {
    const teamIds = m.teamIds && m.teamIds.length ? m.teamIds : (m.teamId ? [m.teamId] : []);
    const teamNames = teamIds.map(tid => { const t=teams.find(x=>x.id===tid); return t?t.name:""; }).filter(Boolean).join(", ");
    const notified = teamNames || (m.invitees||[]).join(", ") || "just you";
    return `  - "${m.title}" on ${m.date} ${m.startTime||"?"}–${m.endTime||"?"}${m.notes ? " | notes: "+m.notes : ""} | notified: ${notified}`;
  }).join("\n");
})()}`;
  }

  // Send a message to Claude AI chat
  // Execute actions returned by Claude
  function executeActions(actions) {
    const results = [];
    actions.forEach(action => {
      try {
        if (action.type === "UPDATE_TASK_STATUS") {
          const task = tasks.find(t => t.title.toLowerCase().includes(action.taskTitle.toLowerCase()));
          if (task) {
            handleStatusChange(task.id, action.status);
            results.push(`✅ Changed "${task.title}" status to ${action.status}`);
          } else results.push(`❌ Could not find task matching "${action.taskTitle}"`);
        }
        else if (action.type === "UPDATE_TASK_PRIORITY") {
          const task = tasks.find(t => t.title.toLowerCase().includes(action.taskTitle.toLowerCase()));
          if (task) {
            setTasks(ts=>ts.map(t=>t.id===task.id?{...t,priority:action.priority}:t));
            results.push(`✅ Changed "${task.title}" priority to ${action.priority}`);
          } else results.push(`❌ Could not find task matching "${action.taskTitle}"`);
        }
        else if (action.type === "UPDATE_MY_STATUS") {
          const input = action.status.toLowerCase().replace(/\s+/g,"_");
          const statusId = MEMBER_STATUSES.map(s=>s.id).find(s=>s===input||s.includes(input)||input.includes(s)) || input;
          const sm = MEMBER_STATUSES.find(s=>s.id===statusId);
          if (sm) {
            setUserStatus(statusId);
            const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,status:statusId}:m)}));
            setTeams(updatedTeams);
            updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
            supabase.from('profiles').update({status:statusId}).eq('id',currentUser.id).then(()=>{});
            results.push(`✅ Your status updated to ${sm.label}`);
          } else results.push(`❌ Unknown status "${action.status}". Valid: ${MEMBER_STATUSES.map(s=>s.label).join(", ")}`);
        }
        else if (action.type === "UPDATE_MY_SHIFT") {
          const shiftId = action.shift?.toLowerCase().replace(/\s+/g,"_") || "flexible";
          const sh = SHIFTS.find(s=>s.id===shiftId || s.label.toLowerCase()===shiftId);
          if (sh) {
            const start = action.start || sh.time?.split(" – ")[0] || "09:00";
            const end   = action.end   || sh.time?.split(" – ")[1] || "17:00";
            const days  = action.days  || ["Mon","Tue","Wed","Thu","Fri"];
            setUserShift({shift:sh.id, start, end, days});
            const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,shift:sh.id,shiftStart:start,shiftEnd:end,days}:m)}));
            setTeams(updatedTeams);
            updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
            supabase.from('profiles').update({shift:sh.id,shift_start:start,shift_end:end,days}).eq('id',currentUser.id).then(()=>{});
            results.push(`✅ Your shift updated to ${sh.label} (${start}–${end})`);
          } else results.push(`❌ Unknown shift "${action.shift}". Valid: ${SHIFTS.map(s=>s.label).join(", ")}`);
        }
        else if (action.type === "CREATE_TASK") {
          const team = teams.find(t=>t.name.toLowerCase().includes((action.team||"").toLowerCase())) || myTeams[0];
          if (team) {
            const newTask = { id:genId(), title:action.title, description:action.description||"", team:team.id, assignees:[currentUser.name], priority:action.priority||"Medium", status:"To Do", dueDate:action.dueDate||"", links:[], comments:[] };
            setTasks(ts=>[...ts,newTask]);
            addActivity(currentUser.name, "created", action.title, team.id);
            results.push(`✅ Created task "${action.title}" in ${team.name}`);
          } else results.push(`❌ Could not find team "${action.team}"`);
        }
        else if (action.type === "REASSIGN_TASK") {
          const task = tasks.find(t=>t.title.toLowerCase().includes(action.taskTitle.toLowerCase()));
          if (task) {
            handleReassign(task.id, action.assignees, task.team);
            results.push(`✅ Reassigned "${task.title}" to ${action.assignees.join(", ")}`);
          } else results.push(`❌ Could not find task matching "${action.taskTitle}"`);
        }
        else if (action.type === "CREATE_EVENT") {
          if (!action.title?.trim()) { results.push("❌ Event needs a title"); return; }
          // Resolve date — support "today", "tomorrow", or YYYY-MM-DD
          // Use local date parts to avoid UTC timezone shifting the date
          function localDateStr(d) {
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          }
          let date = action.date || localDateStr(new Date());
          if (date === "today")    date = localDateStr(new Date());
          if (date === "tomorrow") { const d=new Date(); d.setDate(d.getDate()+1); date=localDateStr(d); }
          // Resolve teams by name
          const eventTeamIds = action.teams
            ? action.teams.map(tn => teams.find(t=>t.name.toLowerCase().includes(tn.toLowerCase()))?.id).filter(Boolean)
            : [];
          // Resolve individual invitees by name
          const invitees = action.invitees
            ? action.invitees.map(n => {
                const all = teams.flatMap(t=>(t.members||[]).map(m=>m.name));
                return all.find(name=>name.toLowerCase().includes(n.toLowerCase()));
              }).filter(Boolean)
            : [];
          const newEvent = {
            id: genId(),
            title: action.title.trim(),
            date,
            startTime: action.startTime || "09:00",
            endTime:   action.endTime   || "10:00",
            notes:     action.notes     || "",
            color:     action.color     || "#0065FF",
            teamIds:   eventTeamIds,
            teamId:    eventTeamIds[0]  || null,
            invitees,
            createdBy: currentUser.name,
          };
          setMeetings(cur => [...cur, newEvent]);
          upsertMeeting(newEvent);
          // Notify team members
          const from = currentUser.name;
          const label = `${newEvent.title} on ${newEvent.date} ${newEvent.startTime}–${newEvent.endTime}`;
          eventTeamIds.forEach(tid => {
            const team = teams.find(t=>t.id===tid);
            if (team) (team.members||[]).filter(m=>m.name!==from).forEach(m=>addNotif(m.name,"meeting",label,from,tid));
          });
          invitees.filter(n=>n!==from).forEach(n=>addNotif(n,"meeting",label,from,null));
          const teamNames = eventTeamIds.map(tid=>teams.find(t=>t.id===tid)?.name).filter(Boolean).join(", ");
          const whoNotified = teamNames || (invitees.length ? invitees.join(", ") : "just you");
          results.push(`✅ Created event "${newEvent.title}" on ${date} ${newEvent.startTime}–${newEvent.endTime} | notified: ${whoNotified}`);
        }
      } catch(e) { results.push(`❌ Error: ${e.message}`); }
    });
    return results;
  }

  async function sendChatMessage() {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    const userMsg = { role:"user", text:msg };
    setChatMessages(m=>[...m, userMsg]);
    setChatInput("");
    setChatLoading(true);
    try {
      const context = buildTaskContext();
      const history = chatMessages.filter((m,idx)=>idx>0).map(m=>({
        role: m.role==="user"?"user":"assistant",
        content: m.text
      }));
      const systemPrompt = `You are a helpful work management AI assistant for MyTask with the ability to make real changes.

TASK DATA:
${context}

CURRENT USER: ${currentUser.name}
VALID STATUSES: To Do, In Progress, Review, Done
VALID PRIORITIES: Low, Medium, High, Critical
VALID MEMBER STATUSES: present, busy, away, rest_day, on_leave, sick_leave, long_weekend, training, offline
VALID SHIFTS: morning, afternoon, mid, night, flexible

When the user asks you to make a change, include a JSON block at the END of your response in this format:
<actions>
[
  {"type":"UPDATE_TASK_STATUS","taskTitle":"partial task title","status":"Done"},
  {"type":"UPDATE_TASK_PRIORITY","taskTitle":"partial task title","priority":"High"},
  {"type":"UPDATE_MY_STATUS","status":"busy"},
  {"type":"UPDATE_MY_SHIFT","shift":"morning","start":"07:00","end":"16:00","days":["Mon","Tue","Wed","Thu","Fri"]},
  {"type":"CREATE_TASK","title":"task title","description":"desc","team":"team name","priority":"Medium","dueDate":"2026-05-01"},
  {"type":"REASSIGN_TASK","taskTitle":"partial task title","assignees":["Full Name"]},
  {"type":"CREATE_EVENT","title":"event title","date":"2026-05-02","startTime":"10:00","endTime":"11:00","notes":"optional notes","teams":["team name"],"invitees":["Full Name"],"color":"#0065FF"}
]
</actions>

For CREATE_EVENT: "date" must be YYYY-MM-DD format — derive it from today's date already provided in the context. "teams" and "invitees" are optional — omit both to create a personal event. "color" is optional. Always confirm the date, time and attendees back to the user.

You can answer questions about team members' status and shift — e.g. "Who is on leave?", "Who is working today?", "What shift is John on?", "Who is available now?". Use the TEAM MEMBERS — STATUS & SHIFT data provided.
You can also update the current user's own status or shift when asked.
You can answer questions about the user's calendar — e.g. "What meetings do I have today?", "What's on my calendar this week?", "Do I have anything tomorrow?", "What's my next meeting?". Use the MY CALENDAR — UPCOMING MEETINGS data provided.

Only include the <actions> block if making changes. For questions, just answer normally. Be conversational and confirm what you did.

Keep responses concise — 2 to 4 sentences for most answers. Use bullet points only when listing 3 or more items. If the user wants more detail they will ask.`;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "x-api-key":ANTHROPIC_KEY, "anthropic-version":"2023-06-01", "anthropic-dangerous-direct-browser-access":"true" },
        body: JSON.stringify({
          model:"claude-haiku-4-5-20251001",
          max_tokens:450,
          system: systemPrompt,
          messages:[...history, { role:"user", content:msg }]
        })
      });
      const data = await response.json();
      let reply = data.content?.[0]?.text || "Sorry, I couldn't get a response.";

      // Extract and execute actions if present
      const actionMatch = reply.match(/<actions>([\s\S]*?)<\/actions>/);
      let actionResults = [];
      if (actionMatch) {
        try {
          const actions = JSON.parse(actionMatch[1].trim());
          actionResults = executeActions(actions);
        } catch(e) { console.error("Action parse error:", e); }
        // Remove the actions block from the displayed reply
        reply = reply.replace(/<actions>[\s\S]*?<\/actions>/g, "").trim();
      }

      // Append action results to reply
      if (actionResults.length > 0) {
        reply = reply + (reply ? "\n\n" : "") + actionResults.join("\n");
      }

      setChatMessages(m=>[...m, { role:"assistant", text:reply }]);
    } catch(e) {
      setChatMessages(m=>[...m, { role:"assistant", text:"Error: " + e.message }]);
    }
    setChatLoading(false);
  }
  function handleResetData() {
    if (window.confirm("Reset all data to defaults? This cannot be undone.")) {
      setTasks([]); setTeams(makeDefaultTeams()); setNotifications([]); setActivity([]);
      setShowProfileMenu(false);
    }
  }

  return (
    <div onClick={()=>{ if(showProfileMenu) setShowProfileMenu(false); }} style={{ minHeight:"100vh", background:"var(--page-bg)", color:"var(--text-primary)", fontFamily:"'Inter', 'Segoe UI', sans-serif", transition:"background 0.2s, color 0.2s" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>

      {/* ── Top navigation bar ── */}
      <div style={{ background:AC.navy, padding:"0 20px", display:"flex", alignItems:"center", height:56, gap:0, position:"relative", zIndex:500, minWidth:0 }}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginRight:24, flexShrink:0 }}>
          <div style={{ width:26, height:26, background:AC.purple, borderRadius:2, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ color:"#fff", fontSize:14, fontWeight:900, lineHeight:1 }}>{">"}</span>
          </div>
          <span style={{ color:"#fff", fontSize:14, fontWeight:700, letterSpacing:"0.04em", whiteSpace:"nowrap" }}>MYTASK</span>
        </div>

        {/* Nav tabs */}
        <div style={{ display:"flex", alignItems:"stretch", alignSelf:"stretch", gap:0, flexShrink:0 }}>
          {[["dashboard","Dashboard"],["mytasks","My Sprint"],["teams","Teams"],["reports","Reports"]].map(([id,lbl]) => {
            const active = navPage===id;
            return (
              <div key={id} onClick={()=>setNavPage(id)}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"0 14px", color:active?"#fff":"rgba(255,255,255,0.55)", fontSize:13, fontWeight:active?600:400, borderBottom:active?`2px solid ${AC.purple}`:"2px solid transparent", cursor:"pointer", whiteSpace:"nowrap" }}>
                {lbl}
                {id==="mytasks" && myTaskCount>0 && (
                  <span style={{ background:AC.purple, color:"#fff", fontSize:9, fontWeight:700, borderRadius:10, padding:"1px 5px", minWidth:14, textAlign:"center" }}>{myTaskCount}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Right: search + profile — pushed to far right, never overflow */}
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          {/* AI Chat button */}
          <button onClick={()=>setShowAIChat(v=>!v)}
            title="AI Assistant"
            style={{ background:showAIChat?"rgba(161,0,255,0.3)":"rgba(255,255,255,0.08)", border:`1px solid ${showAIChat?"rgba(161,0,255,0.6)":"rgba(255,255,255,0.15)"}`, borderRadius:4, color:"rgba(255,255,255,0.85)", padding:"5px 10px", fontSize:13, cursor:"pointer", fontWeight:showAIChat?600:400 }}>
            ✨ AI
          </button>

          {/* Dark mode toggle */}
          <button onClick={()=>setDarkMode(v=>!v)}
            title={darkMode?"Light mode":"Dark mode"}
            style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:4, color:"rgba(255,255,255,0.85)", padding:"5px 10px", fontSize:14, cursor:"pointer" }}>
            {darkMode ? "☀️" : "🌙"}
          </button>

          {/* Search */}
          <div style={{ position:"relative" }}>
            <button onClick={()=>{setShowSearch(v=>!v);setSearchQuery("");}}
              style={{ background:showSearch?"rgba(255,255,255,0.2)":"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:4, color:"rgba(255,255,255,0.8)", padding:"5px 12px", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap" }}>
              🔍 Search
            </button>
            {showSearch && (
              <div style={{ position:"fixed", top:64, right:32, width:400, background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, boxShadow:"0 12px 40px rgba(26,5,51,0.18)", zIndex:499 }}>
                <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--border)" }}>
                  <input autoFocus placeholder="Search tasks, events, people, teams…"
                    value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                    onKeyDown={e=>e.key==="Escape"&&(setShowSearch(false),setSearchQuery(""))}
                    style={{ width:"100%", border:"none", fontSize:14, color:"var(--text-primary)", outline:"none", fontFamily:"inherit", background:"transparent", boxSizing:"border-box" }}/>
                </div>
                {searchQuery.length>1 ? (
                  <div style={{ maxHeight:400, overflowY:"auto" }}>
                    {searchResults.length===0 && eventSearchResults.length===0
                      ? <div style={{ padding:"20px 16px", textAlign:"center", color:"var(--text-muted)", fontSize:13 }}>No results for "{searchQuery}"</div>
                      : <>
                          {/* Events section */}
                          {eventSearchResults.length>0 && (
                            <>
                              <div style={{ padding:"7px 14px", fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.07em", textTransform:"uppercase", background:"var(--bg-off)", borderBottom:"1px solid var(--border)" }}>
                                🗓 Events ({eventSearchResults.length})
                              </div>
                              {eventSearchResults.map(m => {
                                const color = m.color || "#0065FF";
                                const teamNames = (m.teamIds||[]).map(tid=>teams.find(t=>t.id===tid)?.name).filter(Boolean).join(", ");
                                return (
                                  <div key={m.id} onClick={()=>{
                                    setSelectedMeeting(m);
                                    setShowSearch(false);
                                    setSearchQuery("");
                                    setNavPage("dashboard");
                                    setView("calendar");
                                  }}
                                    style={{ padding:"10px 14px", borderBottom:"1px solid var(--border)", cursor:"pointer", display:"flex", gap:10, alignItems:"flex-start" }}
                                    onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
                                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                    <div style={{ width:28, height:28, borderRadius:6, background:color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, flexShrink:0 }}>🗓</div>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:3 }}>{m.title}</div>
                                      <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                        <span style={{ fontSize:11, color:"var(--text-muted)", fontFamily:"monospace" }}>{m.date} {m.startTime&&`${m.startTime}–${m.endTime}`}</span>
                                        {teamNames && <span style={{ fontSize:11, color:color, fontWeight:500 }}>{teamNames}</span>}
                                        {m.notes && <span style={{ fontSize:11, color:"var(--text-muted)", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", maxWidth:120 }}>{m.notes}</span>}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          {/* Tasks section */}
                          {searchResults.length>0 && (
                            <>
                              <div style={{ padding:"7px 14px", fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.07em", textTransform:"uppercase", background:"var(--bg-off)", borderBottom:"1px solid var(--border)" }}>
                                ✅ Tasks ({searchResults.length})
                              </div>
                              {searchResults.map(t=>{
                                const team=getTeam(t.team),pm=PRIORITY_META[t.priority],sm=STATUS_META[t.status];
                                return (
                                  <div key={t.id} onClick={()=>{
                                    setSelectedTask(t);
                                    setShowSearch(false);
                                    setSearchQuery("");
                                  }}
                                    style={{ padding:"10px 14px", borderBottom:"1px solid var(--border)", cursor:"pointer" }}
                                    onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
                                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                    <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:4 }}>{t.title}</div>
                                    <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                      <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"1px 6px", textTransform:"uppercase" }}>{t.priority}</span>
                                      <span style={{ fontSize:11, fontWeight:600, color:sm.color }}>{t.status}</span>
                                      {team&&<span style={{ fontSize:11, color:team.color, fontWeight:500 }}>{team.name}</span>}
                                      <span style={{ fontSize:11, color:"var(--text-muted)" }}>→ {getAssignees(t).join(", ")||"Unassigned"}</span>
                                      <DeadlineBadge date={t.dueDate}/>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </>
                    }
                  </div>
                ) : (
                  <div style={{ padding:"14px 16px", color:"var(--text-muted)", fontSize:12 }}>Type to search tasks, events, people or teams…</div>
                )}
              </div>
            )}
          </div>

          {/* Notification bell */}
          <div style={{ position:"relative", flexShrink:0 }} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>{setShowNotifPanel(v=>!v); setShowSearch(false); setShowProfileMenu(false);}}
              style={{ position:"relative", background:showNotifPanel?"rgba(255,255,255,0.2)":"rgba(255,255,255,0.08)", border:`1px solid ${showNotifPanel?"rgba(255,255,255,0.35)":"rgba(255,255,255,0.15)"}`, borderRadius:4, color:"rgba(255,255,255,0.8)", padding:"5px 10px", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center" }}>
              🔔
              {unreadCount>0 && <span style={{ position:"absolute", top:2, right:2, background:AC.danger, color:"#fff", fontSize:9, fontWeight:700, borderRadius:8, padding:"0 4px", minWidth:14, textAlign:"center", lineHeight:"14px" }}>{unreadCount}</span>}
            </button>
            {showNotifPanel && (
              <div onClick={e=>e.stopPropagation()}
                style={{ position:"fixed", top:64, right:20, width:340, background:"var(--bg)", border:"1px solid var(--border)", borderRadius:10, boxShadow:"0 16px 48px rgba(26,5,51,0.22)", zIndex:499, maxHeight:"calc(100vh - 80px)", overflowY:"auto" }}>
                <div style={{ padding:"14px 16px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)" }}>Notifications {unreadCount>0 && <span style={{ fontSize:11, background:AC.danger, color:"#fff", borderRadius:8, padding:"1px 7px", marginLeft:6 }}>{unreadCount} new</span>}</div>
                  {unreadCount>0 && <button onClick={markAllRead} style={{ background:"transparent", border:"none", color:AC.purple, fontSize:12, fontWeight:600, cursor:"pointer" }}>Mark all read</button>}
                </div>
                {myNotifs.length===0
                  ? <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--text-muted)", fontSize:13 }}>
                      <div style={{ fontSize:28, marginBottom:8 }}>🔔</div>You're all caught up!
                    </div>
                  : myNotifs.map(n=>{
                      const icons = { assigned:"✅", overdue:"⚠️", due:"⏰", status:"🔄", reassigned:"🔀", mention:"💬", meeting:"🗓" };
                      const colors = { assigned:AC.success, overdue:AC.danger, due:AC.warning, status:AC.info, reassigned:AC.purple, mention:AC.purple, meeting:AC.info };
                      const labels = { assigned:"Task assigned to you", overdue:"Due tomorrow", due:"Due today", status:"Status updated", reassigned:"Task reassigned to you", mention:"You were mentioned in a comment", meeting:"Meeting / event invite" };
                       return (
                          <div key={n.id} onClick={async ()=>{
                            setNotifications(ns=>ns.map(x=>x.id===n.id?{...x,read:true}:x));
                            await markNotificationRead(n.id);
                            setShowNotifPanel(false);

                            // Meeting notification
                            if (n.type==="meeting") {
                              setNavPage("dashboard");
                              setView("calendar");
                              const meeting = (meetings||[]).find(m=>n.taskTitle&&n.taskTitle.includes(m.title));
                              if (meeting) setSelectedMeeting(meeting);
                              return;
                            }

                             // For team-membership notifs (e.g. "Added to X Team") → go to Teams
                             if (n.taskTitle?.startsWith("Added to")) {
                               setNavPage("teams");
                               return;
                             }

                             // Find task — search ALL tasks (not just visible ones)
                             // Priority: taskId match → exact title → partial title (case-insensitive)
                             const titleLower = (n.taskTitle||"").toLowerCase().trim();
                             const task = (n.taskId && tasks.find(t=>t.id===n.taskId))
                                       || tasks.find(t=>t.title===n.taskTitle)
                                       || tasks.find(t=>t.title.toLowerCase()===titleLower)
                                       || tasks.find(t=>t.title.toLowerCase().includes(titleLower))
                                       || tasks.find(t=>titleLower.includes(t.title.toLowerCase()));
                             if (task) {
                               setNavPage("dashboard");
                               setSelectedTask(task);
                             }
                          }}
                          style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)", cursor:"pointer", background:n.read?"transparent":AC.purpleBg+"88", display:"flex", gap:12 }}
                          onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
                          onMouseLeave={e=>e.currentTarget.style.background=n.read?"transparent":AC.purpleBg+"88"}>
                          <div style={{ width:36, height:36, borderRadius:"50%", background:colors[n.type]+"22", border:`1.5px solid ${colors[n.type]}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>{icons[n.type]||"🔔"}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:12, fontWeight:n.read?400:600, color:"var(--text-primary)", marginBottom:3 }}>{labels[n.type]||n.type}</div>
                            <div style={{ fontSize:12, color:"var(--text-second)", marginBottom:3, fontWeight:500 }}>"{n.taskTitle}"</div>
                            {n.from&&n.from!=="System" && <div style={{ fontSize:11, color:"var(--text-muted)" }}>from {n.from}</div>}
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
                              <div style={{ fontSize:10, color:"var(--text-muted)" }}>{timeAgo(n.at)}</div>
                              <div style={{ fontSize:10, color:AC.purple, fontWeight:600 }}>
                                {n.type==="meeting" ? "View event →"
                                  : n.type==="assigned" && n.taskTitle?.startsWith("Added to") ? "View teams →"
                                  : "View task →"}
                              </div>
                            </div>
                          </div>
                          {!n.read && <div style={{ width:7, height:7, borderRadius:"50%", background:AC.purple, flexShrink:0, marginTop:4 }}/>}
                        </div>
                      );
                    })
                }
              </div>
            )}
          </div>
          <div style={{ position:"relative", flexShrink:0 }} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setShowProfileMenu(v=>!v)}
              style={{ display:"flex", alignItems:"center", gap:6, background:showProfileMenu?"rgba(255,255,255,0.18)":"rgba(255,255,255,0.08)", border:`1px solid ${showProfileMenu?"rgba(255,255,255,0.35)":"rgba(255,255,255,0.15)"}`, borderRadius:20, padding:"4px 10px 4px 4px", cursor:"pointer" }}>
              <div style={{ width:26, height:26, borderRadius:"50%", background:AC.purple, border:"2px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:10, fontWeight:700, userSelect:"none", flexShrink:0 }}>
                {currentUser.name.split(" ").map(n=>n[0]).join("")}
              </div>
              <span style={{ color:"rgba(255,255,255,0.85)", fontSize:12, fontWeight:500, whiteSpace:"nowrap" }}>{currentUser.name.split(" ")[0]}</span>
              <span style={{ color:"rgba(255,255,255,0.4)", fontSize:9 }}>▾</span>
            </button>

            {showProfileMenu && (
              <div onClick={e=>e.stopPropagation()}
                style={{ position:"fixed", top:64, right:20, width:300, background:"var(--bg)", border:"1px solid var(--border)", borderRadius:10, boxShadow:"0 16px 48px rgba(26,5,51,0.22)", zIndex:499, maxHeight:"calc(100vh - 80px)", overflowY:"auto", overflowX:"hidden" }}>

                {/* Header — user card */}
                <div style={{ background:AC.navy, padding:"18px 18px 14px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:10 }}>
                    <div style={{ width:44, height:44, borderRadius:"50%", background:AC.purple, border:"2px solid rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:15, fontWeight:700, flexShrink:0 }}>
                      {currentUser.name.split(" ").map(n=>n[0]).join("")}
                    </div>
                    <div style={{ flex:1, overflow:"hidden" }}>
                      <ProfileNameEditor
                        name={currentUser.name}
                        onSave={async n=>{
                          const oldName = currentUser.name;
                          setCurrentUser(u=>({...u, name:n}));
                          setTeams(ts=>ts.map(t=>({...t,members:(t.members||[]).map(m=>m.name===oldName?{...m,name:n}:m)})));
                          setTasks(tk=>tk.map(t=>({...t,assignees:(t.assignees||[]).map(a=>a===oldName?n:a),comments:(t.comments||[]).map(c=>c.author===oldName?{...c,author:n}:c)})));
                          setActivity(ac=>ac.map(a=>a.user===oldName?{...a,user:n}:a));
                          setNotifications(ns=>ns.map(no=>({...no,userId:no.userId===oldName?n:no.userId,from:no.from===oldName?n:no.from})));
                          await Promise.all([
                            updateProfile(currentUser.id, { name:n }),
                            renameUserEverywhere(oldName, n),
                          ]);
                        }}/>
                      <ProfileRoleEditor
                        role={userRole || currentUser.role}
                        onSave={async r=>{
                          setUserRole(r);
                          setCurrentUser(u=>({...u, role:r}));
                          // Persist to Supabase profiles table AND auth user_metadata
                          await Promise.all([
                            supabase.from('profiles').update({ role: r }).eq('id', currentUser.id),
                            supabase.auth.updateUser({ data: { role: r } }),
                          ]);
                          // Update role in all teams
                          const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,role:r}:m)}));
                          setTeams(updatedTeams);
                          updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
                         }}/>
                      {/* Teams chips */}
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                        {myTeams.slice(0,3).map(t=>(
                          <div key={t.id} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(255,255,255,0.1)", borderRadius:10, padding:"2px 8px" }}>
                            <span style={{ width:6, height:6, borderRadius:"50%", background:t.color, display:"block", flexShrink:0 }}/>
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.75)", fontWeight:500, whiteSpace:"nowrap" }}>{t.name}</span>
                          </div>
                        ))}
                        {myTeams.length>3 && (
                          <div style={{ display:"inline-flex", alignItems:"center", background:"rgba(255,255,255,0.08)", borderRadius:10, padding:"2px 8px" }}>
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.5)" }}>+{myTeams.length-3} more</span>
                          </div>
                        )}
                        {myTeams.length===0 && (
                          <span style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>No team assigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", letterSpacing:"0.01em" }}>{currentUser.email}</div>

                  {/* Current status badge */}
                  {(()=>{
                    const sm = myMember ? MEMBER_STATUSES.find(s=>s.id===(myMember.status||"present"))||MEMBER_STATUSES[0] : MEMBER_STATUSES[0];
                    return (
                      <div style={{ marginTop:10, display:"inline-flex", alignItems:"center", gap:6, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:6, padding:"4px 10px" }}>
                        <span style={{ width:7, height:7, borderRadius:"50%", background:sm.dot, display:"block" }}/>
                        <span style={{ fontSize:11, fontWeight:600, color:sm.color }}>{sm.label}</span>
                        {myMember?.status==="on_leave" && myMember?.leaveUntil && <span style={{ fontSize:10, color:sm.color, opacity:0.75 }}>until {myMember.leaveUntil}</span>}
                      </div>
                    );
                  })()}
                </div>

                {/* Nav actions */}
                <div style={{ padding:"6px 0", borderBottom:"1px solid var(--border)" }}>
                  {[
                    { icon:"📋", label:"Dashboard",   sub:"Project board",         action:()=>{ setNavPage("dashboard"); setShowProfileMenu(false); } },
                    { icon:"✅", label:"My Sprint",   sub:"Your personal task sprint", action:()=>{ setNavPage("mytasks");   setShowProfileMenu(false); } },
                    { icon:"👥", label:"Teams",       sub:"View all teams",         action:()=>{ setNavPage("teams");     setShowProfileMenu(false); } },
                    { icon:"📊", label:"Reports",     sub:"Analytics & calendar",   action:()=>{ setNavPage("reports");   setShowProfileMenu(false); } },
                  ].map(item=>(
                    <button key={item.label} onClick={item.action}
                      style={{ width:"100%", background: navPage===item.label.toLowerCase().replace(" ","")?"transparent":"transparent", border:"none", padding:"9px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left", transition:"background 0.1s" }}
                      onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <span style={{ fontSize:16, width:22, textAlign:"center" }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color:"var(--text-primary)" }}>{item.label}</div>
                        <div style={{ fontSize:11, color:"var(--text-muted)" }}>{item.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* My Status — dropdown */}
                <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--border)" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Set My Status</div>
                  {(() => {
                    const activeStatus = MEMBER_STATUSES.find(s => s.id === (myMember?.status || "present")) || MEMBER_STATUSES[0];
                    return (
                      <div style={{ position:"relative" }}>
                        <select
                          value={myMember?.status || "present"}
                          onChange={e => {
                            const s = MEMBER_STATUSES.find(x => x.id === e.target.value);
                            if (!s) return;
                            if (myMemberRaw) {
                              const updatedTeams = teams.map(t=>({
                                ...t,
                                members:(t.members||[]).map(m=> m.name===currentUser.name ? {...m, status:s.id, leaveUntil: s.id!=="on_leave"?"":m.leaveUntil} : m)
                              }));
                              setTeams(updatedTeams);
                              updatedTeams
                                .filter(t=>(t.members||[]).some(m=>m.name===currentUser.name))
                                .forEach(t=>supabaseUpdateTeam(t));
                            }
                            setUserStatus(s.id);
                            supabase.from('profiles').update({ status: s.id }).eq('id', currentUser.id).then(()=>{});
                          }}
                          style={{ width:"100%", appearance:"none", WebkitAppearance:"none", background:activeStatus.bg, border:`1.5px solid ${activeStatus.border}`, borderRadius:6, padding:"7px 32px 7px 30px", fontSize:12, fontWeight:700, color:activeStatus.color, cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                          {MEMBER_STATUSES.map(s => (
                            <option key={s.id} value={s.id}>{s.label} — {s.note}</option>
                          ))}
                        </select>
                        <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", width:8, height:8, borderRadius:"50%", background:activeStatus.dot, pointerEvents:"none", display:"block" }}/>
                        <span style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:10, color:activeStatus.color, pointerEvents:"none" }}>▾</span>
                      </div>
                    );
                  })()}
                  {/* On leave date picker */}
                  {myMember?.status==="on_leave" && (
                    <div style={{ marginTop:10, background:"#E6F0FF", border:"1px solid #B3CFFF", borderRadius:6, padding:"10px 12px" }}>
                      <div style={{ fontSize:10, fontWeight:700, color:AC.info, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>Leave until</div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="date" value={myMember?.leaveUntil||""}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={e=>{
                            const updatedTeams = teams.map(t=>({
                              ...t,
                              members:(t.members||[]).map(m=> m.id===myMember.id ? {...m, leaveUntil:e.target.value} : m)
                            }));
                            setTeams(updatedTeams);
                            const changedTeam = updatedTeams.find(t=>(t.members||[]).some(m=>m.id===myMember.id));
                            if (changedTeam) supabaseUpdateTeam(changedTeam);
                          }}
                          style={{ flex:1, background:"var(--bg)", border:"1px solid #B3CFFF", borderRadius:4, color:"var(--text-primary)", fontSize:12, padding:"6px 8px", outline:"none", fontFamily:"inherit", colorScheme:"light" }}/>
                        {myMember?.leaveUntil && (
                          <span style={{ fontSize:11, fontWeight:700, color:AC.info, flexShrink:0 }}>
                            {Math.max(1,Math.round((new Date(myMember.leaveUntil)-new Date().setHours(0,0,0,0))/86400000)+1)}d
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Shift info + inline editor */}
                {(()=>{
                  // Use real member if found, otherwise create a fallback for teamless users
                  const memberForShift = myMember || {
                    id: currentUser.id,
                    name: currentUser.name,
                    shift: "flexible",
                    shiftStart: "09:00",
                    shiftEnd: "17:00",
                    days: ["Mon","Tue","Wed","Thu","Fri"],
                    status: "present"
                  };
                  return (
                    <ShiftEditor
                      member={memberForShift}
                      teamColor={myTeamObj?.color||AC.purple}
                      onSave={(shift,start,end,days)=>{
                         if (myMemberRaw) {
                           // Update this user across ALL teams they belong to (match by name)
                           const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>
                             m.name===currentUser.name ? {...m,shift,shiftStart:start,shiftEnd:end,days} : m
                           )}));
                           setTeams(updatedTeams);
                           // Save all affected teams to Supabase
                           updatedTeams
                             .filter(t=>(t.members||[]).some(m=>m.name===currentUser.name))
                             .forEach(t=>supabaseUpdateTeam(t));
                         }
                        // Always save locally so UI updates immediately
                        setUserShift({ shift, start, end, days });
                        // Save to profile so it carries over when added to a team
                        supabase.from('profiles').update({ shift, shift_start:start, shift_end:end, days }).eq('id', currentUser.id).then(()=>{});
                      }}
                    />
                  );
                })()}

                {/* Reset + Sign out */}
                <div style={{ padding:"6px 0" }}>

                  <button onClick={handleLogout}
                    style={{ width:"100%", background:"transparent", border:"none", padding:"10px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, fontSize:13, color:AC.danger, textAlign:"left", transition:"background 0.1s" }}
                    onMouseEnter={e=>e.currentTarget.style.background=AC.dangerBg}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{ fontSize:16, width:22, textAlign:"center" }}>↩</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:AC.danger }}>Sign out</div>
                      <div style={{ fontSize:11, color:"var(--text-muted)" }}>Return to login screen</div>
                    </div>
                  </button>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search backdrop */}
      {showSearch && <div onClick={()=>{setShowSearch(false);setSearchQuery("");}} style={{ position:"fixed",inset:0,zIndex:99 }}/>}

      {/* ── Auto-away banner ── */}
      {awayBanner && (
        <div style={{ position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)", zIndex:9998, background:"#2D0A4E", border:"1px solid #FF8B0088", borderRadius:10, boxShadow:"0 4px 24px rgba(26,5,51,0.35)", padding:"12px 20px", display:"flex", alignItems:"center", gap:14, minWidth:320, maxWidth:460, animation:"slideUp 0.3s ease" }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:"#FF8B0022", border:"2px solid #FF8B00", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
            {awayBanner==="tab" ? "🔙" : "💤"}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:2 }}>
              {awayBanner==="tab" ? "Welcome back! Are you back?" : "You appear away"}
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", lineHeight:1.4 }}>
              {awayBanner==="tab"
                ? "You were away for over 30 minutes. Click to restore your status."
                : "No activity for 30 minutes. Move your mouse or press a key to come back."}
            </div>
          </div>
          <button
            onClick={() => {
              const restore = preAwayStatus.current || "present";
              preAwayStatus.current = null;
              currentStatusRef.current = restore;
              setUserStatus(restore);
              const updatedTeams = teams.map(t=>({...t,members:(t.members||[]).map(m=>m.name===currentUser.name?{...m,status:restore}:m)}));
              setTeams(updatedTeams);
              updatedTeams.filter(t=>(t.members||[]).some(m=>m.name===currentUser.name)).forEach(t=>supabaseUpdateTeam(t));
              supabase.from('profiles').update({status:restore}).eq('id',currentUser.id).then(()=>{});
              setAwayBanner(null);
            }}
            style={{ background:"#FF8B00", border:"none", borderRadius:6, color:"#fff", padding:"6px 14px", cursor:"pointer", fontSize:12, fontWeight:700, flexShrink:0, whiteSpace:"nowrap" }}>
            I'm back
          </button>
        </div>
      )}
      {showNotifPanel && <div onClick={()=>setShowNotifPanel(false)} style={{ position:"fixed",inset:0,zIndex:498 }}/>}

      {/* ── Page router ── */}
      {navPage==="dashboard" && (
        <>
          {/* Page header */}
          <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"18px 32px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:20, fontWeight:700, color:"var(--text-primary)", letterSpacing:"-0.02em", marginBottom:2 }}>
                  Good {new Date().getHours()<12?"morning":new Date().getHours()<17?"afternoon":"evening"}, {currentUser.name.split(" ")[0]} 👋
                </div>
                <div style={{ fontSize:13, color:"var(--text-muted)" }}>
                  {myTaskCount>0 ? `You have ${myTaskCount} active task${myTaskCount>1?"s":""} — ` : "All caught up! · "}
                  {totalTasks} total tasks · {doneTasks} completed
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:3 }}>Overall progress</div>
                    <div style={{ width:160, height:5, background:AC.border, borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${totalTasks?Math.round(doneTasks/totalTasks*100):0}%`, background:AC.purple, borderRadius:3, transition:"width 0.4s" }}/>
                    </div>
                  </div>
                  <div style={{ fontSize:20, fontWeight:700, color:AC.purple, minWidth:40, textAlign:"right" }}>{totalTasks?Math.round(doneTasks/totalTasks*100):0}%</div>
                </div>
                <div style={{ width:1, height:32, background:AC.border }}/>
                <button onClick={()=>{setEditTask(null);setShowForm(true);}} style={{ background:AC.purple, border:"none", borderRadius:4, color:"#fff", padding:"8px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ New Task</button>
              </div>
            </div>
          </div>

          {/* Deadline banner */}
          {(()=>{
            const myOverdue = visibleTasks.filter(t=>getAssignees(t).includes(currentUser.name)&&t.dueDate&&daysUntil(t.dueDate)<0&&t.status!=="Done");
            const myDueToday = visibleTasks.filter(t=>getAssignees(t).includes(currentUser.name)&&t.dueDate&&daysUntil(t.dueDate)===0&&t.status!=="Done");
            const myDueSoon  = visibleTasks.filter(t=>getAssignees(t).includes(currentUser.name)&&t.dueDate&&daysUntil(t.dueDate)>0&&daysUntil(t.dueDate)<=2&&t.status!=="Done");
            if (myOverdue.length===0&&myDueToday.length===0&&myDueSoon.length===0) return null;
            return (
              <div style={{ background: myOverdue.length>0?"#FFF1F0":"#FFFBEA", borderBottom:`1px solid ${myOverdue.length>0?"#FFCCC7":"#FFE58F"}`, padding:"10px 32px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
                <span style={{ fontSize:18 }}>{myOverdue.length>0?"🚨":"⏰"}</span>
                {myOverdue.length>0 && <span style={{ fontSize:13, fontWeight:700, color:AC.danger }}>{myOverdue.length} overdue task{myOverdue.length>1?"s":""}: {myOverdue.slice(0,2).map(t=>t.title).join(", ")}{myOverdue.length>2?` +${myOverdue.length-2} more`:""}</span>}
                {myDueToday.length>0 && <span style={{ fontSize:13, fontWeight:600, color:AC.warning }}>{myDueToday.length} due today: {myDueToday.slice(0,2).map(t=>t.title).join(", ")}</span>}
                {myDueSoon.length>0  && <span style={{ fontSize:13, color:AC.warning }}>{myDueSoon.length} due in 1–2 days</span>}
              </div>
            );
          })()}

          {/* Toolbar */}
          <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"8px 32px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>

            {/* Status filter dropdown */}
            <div style={{ position:"relative" }}>
              <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                style={{ appearance:"none", WebkitAppearance:"none", background:"var(--bg-off)", border:`1.5px solid ${filterStatus!=="All"?STATUS_META[filterStatus]?.border||AC.purple:AC.border}`, borderRadius:6, color:filterStatus!=="All"?STATUS_META[filterStatus]?.color||AC.purple:"var(--text-second)", fontSize:12, fontWeight:filterStatus!=="All"?700:400, padding:"6px 28px 6px 10px", cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                <option value="All">● Status: All</option>
                {STATUSES.map(s=><option key={s} value={s}>{s} ({statusCounts[s]})</option>)}
              </select>
              <span style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:10, color:"var(--text-muted)" }}>▾</span>
            </div>

            {/* Priority filter dropdown */}
            <div style={{ position:"relative" }}>
              <select value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}
                style={{ appearance:"none", WebkitAppearance:"none", background:"var(--bg-off)", border:`1.5px solid ${filterPriority!=="All"?PRIORITY_META[filterPriority]?.border||AC.purple:AC.border}`, borderRadius:6, color:filterPriority!=="All"?PRIORITY_META[filterPriority]?.color||AC.purple:"var(--text-second)", fontSize:12, fontWeight:filterPriority!=="All"?700:400, padding:"6px 28px 6px 10px", cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                <option value="All">◈ Priority: All</option>
                {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
              <span style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:10, color:"var(--text-muted)" }}>▾</span>
            </div>

            {/* Team / Assignee filter dropdown */}
            <div style={{ position:"relative" }}>
              <select value={filterAssignee} onChange={e=>setFilterAssignee(e.target.value)}
                style={{ appearance:"none", WebkitAppearance:"none", background:"var(--bg-off)", border:`1.5px solid ${filterAssignee!=="All"?AC.purple:AC.border}`, borderRadius:6, color:filterAssignee!=="All"?AC.purple:"var(--text-second)", fontSize:12, fontWeight:filterAssignee!=="All"?700:400, padding:"6px 28px 6px 10px", cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                <option value="All">👥 Assigned: All</option>
                <option value="me">👤 Me only</option>
                {myTeams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <span style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:10, color:"var(--text-muted)" }}>▾</span>
            </div>

            {/* Due date range */}
            <div style={{ display:"flex", alignItems:"center", gap:4, background:"var(--bg-off)", border:`1.5px solid ${(filterDateFrom||filterDateTo)?AC.purple:AC.border}`, borderRadius:6, padding:"3px 8px" }}>
              <span style={{ fontSize:11, color:"var(--text-muted)", flexShrink:0 }}>📅</span>
              <input type="date" value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)}
                style={{ background:"transparent", border:"none", fontSize:11, color:filterDateFrom?"var(--text-primary)":"var(--text-muted)", outline:"none", fontFamily:"inherit", cursor:"pointer", colorScheme:"light", width:100 }}/>
              <span style={{ fontSize:11, color:"var(--text-muted)" }}>→</span>
              <input type="date" value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)}
                min={filterDateFrom||undefined}
                style={{ background:"transparent", border:"none", fontSize:11, color:filterDateTo?"var(--text-primary)":"var(--text-muted)", outline:"none", fontFamily:"inherit", cursor:"pointer", colorScheme:"light", width:100 }}/>
              {(filterDateFrom||filterDateTo) && (
                <button onClick={()=>{setFilterDateFrom("");setFilterDateTo("");}}
                  style={{ background:"none", border:"none", color:AC.purple, cursor:"pointer", fontSize:12, padding:"0 0 0 2px", lineHeight:1 }}>✕</button>
              )}
            </div>

            {/* Active filter chips — show what's active */}
            {(filterStatus!=="All"||filterPriority!=="All"||filterAssignee!=="All"||(filterDateFrom||filterDateTo)) && (
              <button onClick={()=>{setFilterStatus("All");setFilterPriority("All");setFilterAssignee("All");setFilterDateFrom("");setFilterDateTo("");}}
                style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, color:"var(--text-muted)", fontSize:11, padding:"5px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                ✕ Clear filters
              </button>
            )}

            {/* Board / List / Calendar toggle — pushed to right */}
            <div style={{ marginLeft:"auto", display:"flex", border:"1.5px solid var(--border)", borderRadius:6, overflow:"hidden" }}>
              {[["board","⊞ Board"],["list","≡ List"],["calendar","📅 Calendar"]].map(([v,lbl])=>(
                <button key={v} onClick={()=>setView(v)} style={{ background:view===v?AC.navy:"transparent", border:"none", color:view===v?"#fff":"var(--text-second)", padding:"5px 14px", cursor:"pointer", fontSize:12, fontWeight:view===v?600:400 }}>{lbl}</button>
              ))}
            </div>
          </div>

          {/* Board + Activity side panel */}
          <div style={{ display:"flex", alignItems:"flex-start" }}>
            {/* Main board area */}
            <div style={{ flex:1, padding:"24px 32px", minWidth:0 }}>
              {view==="board"
                ? <BoardView tasks={filtered} getTeam={getTeam}
                    onEdit={t=>{setEditTask(t);setShowForm(true);}}
                    onDelete={async id=>{ setTasks(ts=>ts.filter(t=>t.id!==id)); await deleteTask(id); }}
                    onStatusChange={handleStatusChange}
                    onReassign={t=>setReassignTask(t)}
                    onSelect={t=>setSelectedTask(t)}
                    onAddComment={handleAddComment}
                    onDeleteComment={handleDeleteComment}
                    teams={teams}
                    currentUser={currentUser.name}/>
                : view==="list"
                ? <ListView tasks={filtered} getTeam={getTeam}
                    onEdit={t=>{setEditTask(t);setShowForm(true);}}
                    onDelete={async id=>{ setTasks(ts=>ts.filter(t=>t.id!==id)); await deleteTask(id); }}
                    onStatusChange={handleStatusChange}
                    onReassign={t=>setReassignTask(t)}
                    onSelect={t=>setSelectedTask(t)}/>
                 : <DashboardCalendar
                    tasks={filtered}
                    getTeam={getTeam}
                    teams={teams}
                    currentUser={currentUser}
                    addNotif={addNotif}
                    addActivity={addActivity}
                    calMonth={calMonth}
                    setCalMonth={setCalMonth}
                    calSelected={calSelected}
                    setCalSelected={setCalSelected}
                    calView={calView}
                    setCalView={setCalView}
                    calWeekStart={calWeekStart}
                    setCalWeekStart={setCalWeekStart}
                    meetings={meetings}
                    setMeetings={setMeetings}
                    showMeetingForm={showMeetingForm}
                    setShowMeetingForm={setShowMeetingForm}
                    meetingFormData={meetingFormData}
                    setMeetingFormData={setMeetingFormData}
                    onSelectTask={t=>setSelectedTask(t)}
                    selectedMeeting={selectedMeeting}
                    setSelectedMeeting={setSelectedMeeting}/>
              }
              {filtered.length===0 && (
                <div style={{ textAlign:"center", padding:"80px 0", color:"var(--text-muted)" }}>
                  <div style={{ fontSize:36, marginBottom:12 }}>◌</div>
                  <div style={{ fontSize:15, fontWeight:500, color:"var(--text-second)", marginBottom:8 }}>
                    {filterAssignee==="me" ? "No tasks assigned to you match these filters" :
                     (filterDateFrom||filterDateTo) ? "No tasks due in this date range" :
                     "No tasks match your filters"}
                  </div>
                  {(filterDateFrom||filterDateTo) && (
                    <button onClick={()=>{setFilterDateFrom("");setFilterDateTo("");}}
                      style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:4, color:AC.purple, padding:"5px 14px", cursor:"pointer", fontSize:12 }}>
                      Clear date filter
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Activity feed side panel */}
            {showActivity && (
              <div style={{ width:300, flexShrink:0, borderLeft:"1px solid var(--border)", background:"var(--bg)", minHeight:"calc(100vh - 160px)", padding:"20px 18px" }}>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:4 }}>⚡ Team Activity</div>
                <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:16 }}>
                  {myTeams.map(t=>t.name).join(", ")||"Your team"} · recent actions
                </div>
                {(()=>{
                  const teamActivity = activity.filter(a=> !a.teamId || myTeamIds.has(a.teamId));
                  if (teamActivity.length===0) return (
                    <div style={{ textAlign:"center", padding:"40px 0", color:"var(--text-muted)", fontSize:13 }}>No activity yet</div>
                  );
                  return teamActivity.map(a=>{
                    const team = teams.find(t=>t.id===a.teamId);
                    return (
                      <div key={a.id} style={{ display:"flex", gap:10, marginBottom:14, paddingBottom:14, borderBottom:"1px solid var(--border)" }}>
                        <Avatar name={a.user} size={28}/>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, color:"var(--text-primary)", lineHeight:1.4 }}>
                            <span style={{ fontWeight:600 }}>{a.user.split(" ")[0]}</span>
                            <span style={{ color:"var(--text-muted)" }}> {a.action} </span>
                            <span style={{ fontWeight:500, color:"var(--text-second)" }}>"{a.taskTitle}"</span>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4 }}>
                            <span style={{ fontSize:10, color:"var(--text-muted)" }}>{timeAgo(a.at)}</span>
                            {team && <span style={{ fontSize:10, fontWeight:600, color:team.color, background:team.color+"18", borderRadius:4, padding:"1px 6px" }}>{team.name}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </>
      )}

      {navPage==="mytasks" && (
        <MyTasksPage tasks={tasks} teams={teams} getTeam={getTeam} currentUser={currentUser.name}
          onEdit={t=>{setEditTask(t);setShowForm(true);}}
          onStatusChange={handleStatusChange}
          onReassign={t=>setReassignTask(t)}
          onNewTask={()=>{setEditTask(null);setShowForm(true);}}
          onSelect={t=>setSelectedTask(t)}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}/>
      )}

      {navPage==="teams" && (
        <TeamsPage teams={myTeams} tasks={visibleTasks} currentUser={currentUser} onOpenManager={()=>setShowTeamMgr(true)} onUpdateTeam={updateTeamFn} onSelectTask={t=>setSelectedTask(t)}/>
      )}

      {navPage==="reports" && (
        <ReportsPage tasks={visibleTasks} teams={myTeams} getTeam={getTeam}/>
      )}

      {/* ── Event Reminder Popup ── */}
      {eventReminder && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, width:320, background:"var(--bg)", border:`2px solid ${AC.purple}`, borderRadius:10, boxShadow:"0 8px 32px rgba(161,0,255,0.18)", overflow:"hidden", animation:"slideUp 0.25s ease" }}>
          <style>{`@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
          <div style={{ background:AC.purple, padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:16 }}>🔔</span>
              <span style={{ color:"#fff", fontSize:12, fontWeight:700 }}>Upcoming Event</span>
            </div>
            <button onClick={()=>{ dismissedReminders.current.add(eventReminder.meeting.id); setEventReminder(null); }}
              style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:4, color:"#fff", width:22, height:22, cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
          </div>
          <div style={{ padding:"14px 16px" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:4 }}>{eventReminder.meeting.title}</div>
            <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:8 }}>
              🕐 {eventReminder.meeting.startTime} – {eventReminder.meeting.endTime}
              <span style={{ marginLeft:8, background:AC.purpleBg, color:AC.purple, borderRadius:4, padding:"1px 8px", fontWeight:700, fontSize:11 }}>in {eventReminder.minsLeft} min</span>
            </div>
            {eventReminder.meeting.notes && (
              <div style={{ fontSize:11, color:"var(--text-second)", background:"var(--bg-off)", borderRadius:4, padding:"6px 10px", marginBottom:10, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{eventReminder.meeting.notes}</div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>{ setSelectedMeeting(eventReminder.meeting); dismissedReminders.current.add(eventReminder.meeting.id); setEventReminder(null); }}
                style={{ flex:1, background:AC.purple, border:"none", borderRadius:5, color:"#fff", padding:"7px 0", fontSize:12, fontWeight:600, cursor:"pointer" }}>View Event</button>
              <button onClick={()=>{ dismissedReminders.current.add(eventReminder.meeting.id); setEventReminder(null); }}
                style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-second)", padding:"7px 12px", fontSize:12, cursor:"pointer" }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {showForm && <TaskModal task={editTask} teams={myTeams} onSave={saveTask} onClose={()=>{setShowForm(false);setEditTask(null);}} defaultAssignee={currentUser.name}/>}
      {reassignTask && <ReassignModal task={reassignTask} allTasks={tasks} teams={myTeams}
        onReassign={handleReassign}
        onClose={()=>setReassignTask(null)}/>}
      {showTeamMgr && <TeamManager teams={myTeams} tasks={tasks} onAdd={addTeam} onUpdate={updateTeamFn} onDelete={deleteTeamFn} onClose={()=>setShowTeamMgr(false)} currentUser={currentUser}
        userStatus={userStatus} userShift={userShift}
        onMemberAdded={(memberName, teamName, teamId)=>addNotif(memberName, "assigned", `Added to ${teamName}`, "Team Manager", teamId)}/>}
      {selectedTask && (
        <TaskDetailModal
          task={tasks.find(t=>t.id===selectedTask.id)||selectedTask}
          team={getTeam(selectedTask.team)}
          currentUser={currentUser.name}
          teams={teams}
          onClose={()=>setSelectedTask(null)}
          onStatusChange={handleStatusChange}
          onEdit={t=>{setEditTask(t);setShowForm(true);setSelectedTask(null);}}
          onReassign={t=>{setReassignTask(t);setSelectedTask(null);}}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
        />
      )}

      {selectedMeeting && (
        <MeetingDetailModal
          meeting={selectedMeeting}
          onClose={()=>setSelectedMeeting(null)}
          onEdit={()=>{setMeetingFormData(selectedMeeting);setShowMeetingForm(true);setSelectedMeeting(null);}}
        />
      )}

      {/* ── AI Chat Panel ── */}
      {showAIChat && (
        chatMinimized ? (
          /* Minimized bubble */
          <div onClick={()=>setChatMinimized(false)}
            style={{ position:"fixed", bottom:20, right:20, width:56, height:56, background:AC.purple, borderRadius:"50%", boxShadow:"0 4px 20px rgba(161,0,255,0.4)", zIndex:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}
            title="Open AI Assistant">
            ✨
            {chatMessages.filter(m=>m.role==="assistant").length > 1 && (
              <div style={{ position:"absolute", top:0, right:0, width:16, height:16, background:AC.danger, borderRadius:"50%", border:"2px solid var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:700 }}>
                {chatMessages.filter(m=>m.role==="assistant").length - 1}
              </div>
            )}
          </div>
        ) : (
          /* Expanded panel */
          <div style={{ position:"fixed", bottom:20, right:20, width:390, maxHeight:"75vh", background:"var(--bg)", border:`1px solid ${AC.purpleDim}`, borderRadius:12, boxShadow:"0 12px 48px rgba(26,5,51,0.25)", zIndex:600, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            {/* Header */}
            <div style={{ background:AC.navy, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
              onClick={()=>setChatMinimized(true)}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:28, height:28, background:AC.purple, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>✨</div>
                <div>
                  <div style={{ color:"#fff", fontSize:13, fontWeight:700 }}>MyTask AI</div>
                  <div style={{ color:"rgba(255,255,255,0.45)", fontSize:10 }}>Can answer questions & make changes · click header to minimize</div>
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={e=>{e.stopPropagation();setChatMinimized(true);}} title="Minimize"
                  style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:4, color:"rgba(255,255,255,0.7)", width:24, height:24, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>—</button>
                <button onClick={e=>{e.stopPropagation();setShowAIChat(false);setChatMinimized(false);}} title="Close"
                  style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:4, color:"rgba(255,255,255,0.7)", width:24, height:24, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>
            </div>

            {/* Quick action buttons */}
            <div style={{ padding:"8px 12px", borderBottom:`1px solid ${AC.border}`, display:"flex", gap:5, flexWrap:"wrap" }}>
              {[
                { label:"📋 Standup", prompt:"Generate my daily standup update. Format: Yesterday, Today, Blockers." },
                { label:"⚠️ Overdue", prompt:"What tasks are overdue? Give me a quick summary." },
                { label:"🎯 Focus", prompt:"What should I focus on today? Consider priorities and due dates." },
                { label:"📊 Team", prompt:"Give me a summary of the team's overall workload and progress." },
                { label:"🔔 Notifications", prompt:"What are my unread notifications? Summarise what I need to action." },
                { label:"⚡ Activity", prompt:"What has been happening recently in my team? Summarise the recent activity." },
                { label:"👥 Who's in?", prompt:"Who is available and working right now? Show me each team member's status and shift." },
                { label:"🌴 On leave?", prompt:"Who is currently on leave, sick leave, or unavailable?" },
                { label:"📅 Today's meetings", prompt:"What meetings or events do I have today?" },
                { label:"🗓 This week", prompt:"What's on my calendar this week? List all my meetings." },

              ].map(({label, prompt}) => (
                <button key={label} onClick={()=>setChatInput(prompt)}
                  style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:20, padding:"3px 10px", cursor:"pointer", fontSize:11, color:"var(--text-second)", fontWeight:500, whiteSpace:"nowrap" }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Messages */}
            <ChatMessages messages={chatMessages} chatLoading={chatLoading} currentUser={currentUser}/>

            {/* Input */}
            <div style={{ padding:"10px 12px", borderTop:`1px solid ${AC.border}`, display:"flex", gap:8 }}>
              <input
                value={chatInput}
                onChange={e=>setChatInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),sendChatMessage())}
                placeholder='Ask anything or say "Change X status to Done"…'
                style={{ flex:1, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text-primary)", outline:"none", fontFamily:"inherit" }}
              />
              <button onClick={sendChatMessage} disabled={!chatInput.trim()||chatLoading}
                style={{ background:chatInput.trim()&&!chatLoading?AC.purple:"#ccc", border:"none", borderRadius:6, color:"#fff", padding:"0 14px", cursor:chatInput.trim()&&!chatLoading?"pointer":"default", fontSize:16 }}>
                ↑
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MY TASKS PAGE  — Personal sprint view
══════════════════════════════════════════════════════════════ */

function TaskRow({ t, getTeam, openComments, setOpenComments, onSelect, onStatusChange, onEdit, onAddComment, onDeleteComment, currentUser, teams }) {
  const team = getTeam(t.team);
  const pm   = PRIORITY_META[t.priority];
  const sm   = STATUS_META[t.status];
  const commentsOpen = openComments === t.id;
  const commentCount = (t.comments||[]).length;
  return (
    <div onClick={()=>onSelect&&onSelect(t)}
      style={{ background:"var(--bg)", border:"1.5px solid var(--border)", borderLeft:`4px solid ${pm.color}`, borderRadius:6, padding:"12px 16px", cursor:"pointer", transition:"all 0.12s" }}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 12px rgba(26,5,51,0.09)";e.currentTarget.style.borderColor=AC.purpleDim;}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor=AC.border;}}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:3, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{t.title}</span>
            <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"1px 6px", textTransform:"uppercase" }}>{t.priority}</span>
            {team && <span style={{ fontSize:11, color:team.color, fontWeight:500 }}>{team.name}</span>}
          </div>
          {t.description && <div style={{ fontSize:11, color:"var(--text-muted)" }}>{t.description.slice(0,80)}{t.description.length>80?"…":""}</div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
          {commentCount>0 && (
            <button onClick={()=>setOpenComments(commentsOpen?null:t.id)}
              style={{ background:commentsOpen?AC.purpleBg:"transparent", border:`1px solid ${commentsOpen?AC.purple:AC.border}`, borderRadius:4, color:commentsOpen?AC.purple:AC.textMuted, padding:"3px 8px", cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", gap:3 }}>
              💬 {commentCount}
            </button>
          )}
          <DeadlineBadge date={t.dueDate}/>
          <select value={t.status} onChange={e=>onStatusChange(t.id,e.target.value)}
            style={{ background:sm.bg, border:`1.5px solid ${sm.border}`, borderRadius:4, color:sm.color, fontSize:11, fontWeight:600, padding:"4px 8px", cursor:"pointer", fontFamily:"inherit" }}>
            {STATUSES.map(s=><option key={s} style={{ background:"var(--bg)", color:"var(--text-primary)" }}>{s}</option>)}
          </select>
          <button onClick={()=>onEdit(t)} style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"4px 8px", cursor:"pointer", fontSize:11 }}>✎</button>
        </div>
      </div>
      {commentsOpen && (
        <div onClick={e=>e.stopPropagation()}>
          <CommentPanel task={t} currentUser={currentUser} onAddComment={onAddComment} onDeleteComment={onDeleteComment} teams={teams}/>
        </div>
      )}
    </div>
  );
}

function Section({ label, color, bg, border, icon, tasks: list, emptyMsg, getTeam, openComments, setOpenComments, onSelect, onStatusChange, onEdit, onAddComment, onDeleteComment, currentUser, teams, applyPriority, priorityFilter }) {
  const filtered = applyPriority(list);
  if (filtered.length === 0 && priorityFilter !== "All") return null;
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <span style={{ fontSize:14 }}>{icon}</span>
        <span style={{ fontSize:13, fontWeight:700, color }}>{label}</span>
        <span style={{ fontSize:11, fontWeight:700, color, background:bg, border:`1px solid ${border}`, borderRadius:10, padding:"1px 8px" }}>{filtered.length}</span>
      </div>
      {filtered.length === 0
        ? <div style={{ fontSize:12, color:"var(--text-muted)", padding:"12px 0", paddingLeft:4 }}>{emptyMsg}</div>
        : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {filtered.map(t=><TaskRow key={t.id} t={t} getTeam={getTeam} openComments={openComments} setOpenComments={setOpenComments} onSelect={onSelect} onStatusChange={onStatusChange} onEdit={onEdit} onAddComment={onAddComment} onDeleteComment={onDeleteComment} currentUser={currentUser} teams={teams}/>)}
          </div>
      }
    </div>
  );
}

function MyTasksPage({ tasks, teams, getTeam, currentUser, onEdit, onStatusChange, onReassign, onNewTask, onAddComment, onDeleteComment, onSelect }) {
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [openComments,   setOpenComments]   = useState(null);

  const myTasks   = tasks.filter(t => getAssignees(t).includes(currentUser) && t.status !== "Done");
  const doneTasks = tasks.filter(t => getAssignees(t).includes(currentUser) && t.status === "Done");
  const allMine   = tasks.filter(t => getAssignees(t).includes(currentUser));

  // Urgency buckets
  const overdueTasks  = myTasks.filter(t => t.dueDate && daysUntil(t.dueDate) < 0);
  const todayTasks    = myTasks.filter(t => t.dueDate && daysUntil(t.dueDate) === 0);
  const thisWeekTasks = myTasks.filter(t => t.dueDate && daysUntil(t.dueDate) > 0 && daysUntil(t.dueDate) <= 7);
  const laterTasks    = myTasks.filter(t => !t.dueDate || daysUntil(t.dueDate) > 7);

  // Apply priority filter across all buckets
  const PRIORITY_ORDER = { Critical:0, High:1, Medium:2, Low:3 };
  function sortTasks(list) {
    return [...list].sort((a,b) => {
      const pd = (PRIORITY_ORDER[a.priority]??99) - (PRIORITY_ORDER[b.priority]??99);
      if (pd !== 0) return pd;
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
    });
  }

  function applyPriority(list) {
    const filtered = priorityFilter === "All" ? list : list.filter(t => t.priority === priorityFilter);
    return sortTasks(filtered);
  }

  // Workload heatmap — tasks due each day of current week (Mon–Sun)
  const today = new Date(); today.setHours(0,0,0,0);
  const dayOfWeek = today.getDay() === 0 ? 6 : today.getDay() - 1; // Mon=0
  const weekDays = Array.from({length:7}, (_,i) => {
    const d = new Date(today); d.setDate(today.getDate() - dayOfWeek + i);
    return d;
  });
  const heatmapCounts = weekDays.map(d => {
    const iso = d.toISOString().split("T")[0];
    return allMine.filter(t => t.dueDate === iso).length;
  });
  const maxHeat = Math.max(...heatmapCounts, 1);

  // Completion rate — last 30 days
  const thirtyDaysAgo = new Date(Date.now() - 30*24*60*60*1000);
  const completedRecent = doneTasks.filter(t => t.dueDate && new Date(t.dueDate) >= thirtyDaysAgo).length;
  const totalRecent     = allMine.filter(t => t.dueDate && new Date(t.dueDate) >= thirtyDaysAgo).length;
  const completionRate  = totalRecent ? Math.round(completedRecent / totalRecent * 100) : 0;

  const initials = currentUser.split(" ").map(n=>n[0]).join("");



  return (
    <>
      {/* Header */}
      <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"20px 32px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:"50%", background:AC.purple, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:16, fontWeight:700 }}>{initials}</div>
            <div>
              <div style={{ fontSize:20, fontWeight:700, color:"var(--text-primary)", letterSpacing:"-0.02em" }}>My Sprint</div>
              <div style={{ fontSize:13, color:"var(--text-muted)" }}>{currentUser} · {myTasks.length} active · {doneTasks.length} done</div>
            </div>
          </div>
          <button onClick={onNewTask} style={{ ...BTN_PRIMARY, display:"flex", alignItems:"center", gap:6 }}>+ New Task</button>
        </div>

        {/* Stats row */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>

          {/* Completion tracker */}
          <div style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Completion rate · 30d</div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:10, marginBottom:8 }}>
              <div style={{ fontSize:28, fontWeight:700, color:completionRate>=70?AC.success:completionRate>=40?AC.warning:AC.danger }}>{completionRate}%</div>
              <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:4 }}>{completedRecent}/{totalRecent} tasks</div>
            </div>
            <div style={{ height:6, background:AC.border, borderRadius:3, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${completionRate}%`, background:completionRate>=70?AC.success:completionRate>=40?AC.warning:AC.danger, borderRadius:3, transition:"width 0.5s" }}/>
            </div>
          </div>

          {/* Workload heatmap */}
          <div style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px", gridColumn:"span 2" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:10 }}>Tasks due this week</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:6 }}>
              {weekDays.map((d,i) => {
                const count = heatmapCounts[i];
                const isToday = d.getTime() === today.getTime();
                const isPast  = d < today;
                const intensity = count === 0 ? 0 : Math.ceil((count / maxHeat) * 4);
                const heatColors = ["transparent","#E6F0FF","#99C2FF","#4D94FF","#0065FF"];
                const textColor  = intensity >= 3 ? "#fff" : AC.textPrimary;
                return (
                  <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:10, fontWeight:600, color:isToday?AC.purple:AC.textMuted, marginBottom:4 }}>
                      {["M","T","W","T","F","S","S"][i]}
                    </div>
                    <div style={{
                      height:36, borderRadius:6, background:count>0?heatColors[intensity]:AC.white,
                      border:`1.5px solid ${isToday?AC.purple:count>0?"#99C2FF":AC.border}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:13, fontWeight:700, color:count>0?textColor:AC.textMuted,
                      opacity:isPast&&!isToday?0.5:1
                    }}>
                      {count > 0 ? count : <span style={{ fontSize:9, color:AC.border }}>—</span>}
                    </div>
                    <div style={{ fontSize:9, color:"var(--text-muted)", marginTop:3 }}>
                      {d.toLocaleDateString("en-US",{day:"numeric"})}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Priority filter bar */}
      <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"10px 32px", display:"flex", alignItems:"center", gap:6 }}>
        <span style={{ fontSize:11, color:"var(--text-muted)", marginRight:4 }}>Priority:</span>
        <button onClick={()=>setPriorityFilter("All")}
          style={{ background:priorityFilter==="All"?AC.navy:"transparent", border:`1.5px solid ${priorityFilter==="All"?AC.navy:AC.border}`, borderRadius:4, padding:"4px 12px", cursor:"pointer", fontSize:11, color:priorityFilter==="All"?"#fff":AC.textSecond, fontWeight:priorityFilter==="All"?700:400 }}>
          All
        </button>
        {PRIORITIES.map(p=>{
          const pm=PRIORITY_META[p], active=priorityFilter===p;
          const cnt=myTasks.filter(t=>t.priority===p).length;
          return (
            <button key={p} onClick={()=>setPriorityFilter(active?"All":p)}
              style={{ background:active?pm.bg:"transparent", border:`1.5px solid ${active?pm.border:AC.border}`, borderRadius:4, padding:"4px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:pm.color, display:"block" }}/>
              <span style={{ fontSize:11, color:active?pm.color:"var(--text-second)", fontWeight:active?700:400 }}>{p}</span>
              <span style={{ fontSize:10, color:active?pm.color:"var(--text-muted)", background:active?pm.border+"60":AC.offwhite, borderRadius:3, padding:"0 5px", fontWeight:600 }}>{cnt}</span>
            </button>
          );
        })}
        <div style={{ marginLeft:"auto", fontSize:11, color:"var(--text-muted)" }}>
          Click any task to view details and comments
        </div>
      </div>

      {/* Task groups */}
      <div style={{ padding:"24px 32px" }}>
        {myTasks.length === 0 ? (
          <div style={{ textAlign:"center", padding:"80px 0", color:"var(--text-muted)" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
            <div style={{ fontSize:16, fontWeight:600, color:"var(--text-second)", marginBottom:6 }}>All caught up!</div>
            <div style={{ fontSize:13 }}>No active tasks assigned to you</div>
          </div>
        ) : (
          <>
            <Section label="Overdue" color={AC.danger} bg={AC.dangerBg} border={AC.danger+"44"} icon="🚨" tasks={overdueTasks} emptyMsg="No overdue tasks — great!" applyPriority={applyPriority} priorityFilter={priorityFilter} getTeam={getTeam} openComments={openComments} setOpenComments={setOpenComments} onSelect={onSelect} onStatusChange={onStatusChange} onEdit={onEdit} onAddComment={onAddComment} onDeleteComment={onDeleteComment} currentUser={currentUser} teams={teams}/>
            <Section label="Due today" color={AC.warning} bg={AC.warningBg} border={AC.warning+"44"} icon="⏰" tasks={todayTasks} emptyMsg="Nothing due today" applyPriority={applyPriority} priorityFilter={priorityFilter} getTeam={getTeam} openComments={openComments} setOpenComments={setOpenComments} onSelect={onSelect} onStatusChange={onStatusChange} onEdit={onEdit} onAddComment={onAddComment} onDeleteComment={onDeleteComment} currentUser={currentUser} teams={teams}/>
            <Section label="This week" color={AC.info} bg={AC.infoBg} border={AC.info+"44"} icon="📅" tasks={thisWeekTasks} emptyMsg="Nothing due this week" applyPriority={applyPriority} priorityFilter={priorityFilter} getTeam={getTeam} openComments={openComments} setOpenComments={setOpenComments} onSelect={onSelect} onStatusChange={onStatusChange} onEdit={onEdit} onAddComment={onAddComment} onDeleteComment={onDeleteComment} currentUser={currentUser} teams={teams}/>
            <Section label="Later" color={AC.textSecond} bg={AC.offwhite} border={AC.border} icon="🗓" tasks={laterTasks} emptyMsg="No tasks scheduled further out" applyPriority={applyPriority} priorityFilter={priorityFilter} getTeam={getTeam} openComments={openComments} setOpenComments={setOpenComments} onSelect={onSelect} onStatusChange={onStatusChange} onEdit={onEdit} onAddComment={onAddComment} onDeleteComment={onDeleteComment} currentUser={currentUser} teams={teams}/>
            {doneTasks.length > 0 && (
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:12, fontWeight:600, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                  <span>✅ Done</span>
                  <span style={{ background:AC.successBg, color:AC.success, border:`1px solid ${AC.success}44`, borderRadius:10, padding:"1px 8px", fontSize:10, fontWeight:700 }}>{doneTasks.length}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, opacity:0.65 }}>
                  {doneTasks.slice(0,5).map(t => {
                    const team=getTeam(t.team), pm=PRIORITY_META[t.priority];
                    return (
                      <div key={t.id} onClick={()=>onSelect&&onSelect(t)}
                        style={{ background:"var(--bg)", border:"1px solid var(--border)", borderLeft:`4px solid ${AC.success}`, borderRadius:6, padding:"10px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:14 }}>✓</span>
                        <span style={{ fontSize:12, fontWeight:500, color:"var(--text-second)", textDecoration:"line-through", flex:1 }}>{t.title}</span>
                        <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, borderRadius:3, padding:"1px 6px", textTransform:"uppercase" }}>{t.priority}</span>
                        {team && <span style={{ fontSize:11, color:team.color }}>{team.name}</span>}
                      </div>
                    );
                  })}
                  {doneTasks.length > 5 && <div style={{ fontSize:11, color:"var(--text-muted)", paddingLeft:4 }}>+{doneTasks.length-5} more completed tasks</div>}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   TEAMS PAGE
══════════════════════════════════════════════════════════════ */
function TeamsPage({ teams, tasks, currentUser, onOpenManager, onSelectTask }) {
  const [expanded,     setExpanded]     = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // All teams the current user is a member of
  const myTeamIds = new Set(teams.filter(t=>(t.members||[]).some(m=>m.name===currentUser?.name)).map(t=>t.id));

  const viewTeam   = teams.find(t=>t.id===selectedTeam);
  const viewTasks  = viewTeam ? tasks.filter(t=>t.team===viewTeam.id) : [];

  return (
    <>
      <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"20px 32px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, color:"var(--text-primary)", letterSpacing:"-0.02em", marginBottom:2 }}>My Teams</div>
          <div style={{ fontSize:13, color:"var(--text-muted)" }}>{teams.length} {teams.length===1?"team":"teams"} · {teams.reduce((a,t)=>a+(t.members||[]).length,0)} total members</div>
        </div>
        <button onClick={onOpenManager} style={BTN_PRIMARY}>Manage Teams</button>
      </div>

      {teams.length === 0 && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 32px", gap:12 }}>
          <div style={{ fontSize:40 }}>👥</div>
          <div style={{ fontSize:16, fontWeight:700, color:"var(--text-primary)" }}>You're not in any team yet</div>
          <div style={{ fontSize:13, color:"var(--text-muted)", textAlign:"center", maxWidth:340 }}>Ask your manager or team admin to add you to a team. You'll see your teams here once added.</div>
        </div>
      )}
      <div style={{ display:"flex", alignItems:"flex-start" }}>
        {/* Team cards grid */}
        <div style={{ flex:1, padding:"24px 32px", display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16, alignContent:"start" }}>
          {teams.map(team => {
            const teamTasks = tasks.filter(t=>t.team===team.id);
            const done = teamTasks.filter(t=>t.status==="Done").length;
            const pct  = teamTasks.length ? Math.round(done/teamTasks.length*100) : 0;
            const isOpen     = expanded===team.id;
            const isSelected = selectedTeam===team.id;
            const statusGroups = MEMBER_STATUSES.reduce((acc,s)=>{
              const cnt=(team.members||[]).filter(m=>(m.status||"present")===s.id).length;
              if(cnt>0) acc.push({...s,cnt}); return acc;
            },[]);

            return (
              <div key={team.id} style={{ background:"var(--bg)", border:`2px solid ${isSelected?team.color:AC.border}`, borderRadius:8, overflow:"hidden", boxShadow:isSelected?`0 4px 16px ${team.color}33`:"0 1px 4px rgba(26,5,51,0.06)", transition:"all 0.15s" }}>
                {/* Clickable team header */}
                <div style={{ borderTop:`4px solid ${team.color}`, padding:"16px 20px", cursor:"pointer" }}
                  onClick={()=>setSelectedTeam(isSelected?null:team.id)}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:36, height:36, borderRadius:6, background:team.color, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:15, fontWeight:700 }}>{team.name[0]}</div>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:2 }}>
                          <div style={{ fontSize:15, fontWeight:700, color:isSelected?team.color:"var(--text-primary)" }}>{team.name}</div>
                          {myTeamIds.has(team.id) && <span style={{ fontSize:10, fontWeight:700, color:AC.purple, background:AC.purpleBg, border:`1px solid ${AC.purpleDim}`, borderRadius:10, padding:"1px 7px" }}>Your team</span>}
                        </div>
                        <div style={{ fontSize:12, color:"var(--text-muted)" }}>{(team.members||[]).length} members · {teamTasks.length} tasks</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:18, fontWeight:700, color:team.color }}>{pct}%</span>
                      <span style={{ fontSize:12, color:isSelected?team.color:"var(--text-muted)", fontWeight:600 }}>{isSelected?"▼":"▶"}</span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height:4, background:AC.border, borderRadius:2, marginBottom:12, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:team.color, borderRadius:2, transition:"width 0.4s" }}/>
                  </div>
                  {/* Task status pills */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                    {STATUSES.map(s=>{
                      const cnt=teamTasks.filter(t=>t.status===s).length;
                      if(!cnt) return null;
                      const m=STATUS_META[s];
                      return <span key={s} style={{ fontSize:11, fontWeight:600, color:m.color, background:m.bg, border:`1px solid ${m.border}`, borderRadius:10, padding:"2px 9px" }}>{s} {cnt}</span>;
                    })}
                  </div>
                  {/* Member status summary */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                    {statusGroups.map(s=>(
                      <span key={s.id} style={{ fontSize:11, fontWeight:500, color:s.color, background:s.bg, border:`1px solid ${s.border}`, borderRadius:10, padding:"2px 9px", display:"inline-flex", alignItems:"center", gap:4 }}>
                        <span style={{ width:5, height:5, borderRadius:"50%", background:s.dot, display:"block" }}/>{s.label} {s.cnt}
                      </span>
                    ))}
                  </div>
                  {/* Member avatars */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ display:"flex" }}>
                      {(team.members||[]).slice(0,6).map((m,i)=>{
                        const sm=MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
                        return (
                          <div key={m.id} title={`${m.name} — ${sm.label}`} style={{ marginLeft:i===0?0:-8, position:"relative", zIndex:10-i }}>
                            <Avatar name={m.name} size={30}/>
                            <span style={{ position:"absolute", bottom:-1, right:-1, width:9, height:9, borderRadius:"50%", background:sm.dot, border:`2px solid ${AC.white}`, display:"block" }}/>
                          </div>
                        );
                      })}
                      {(team.members||[]).length>6 && <div style={{ marginLeft:-8, width:30, height:30, borderRadius:"50%", background:"var(--bg-off)", border:"2px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"var(--text-muted)" }}>+{(team.members||[]).length-6}</div>}
                    </div>
                    <button onClick={e=>{e.stopPropagation();setExpanded(isOpen?null:team.id);}}
                      style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"4px 12px", cursor:"pointer", fontSize:12, fontWeight:500 }}>
                      {isOpen?"Hide":"Members"} {isOpen?"▲":"▼"}
                    </button>
                  </div>
                </div>

                {/* Expanded member list */}
                {isOpen && (
                  <div style={{ borderTop:"1px solid var(--border)", padding:"12px 20px", background:"var(--bg-off)" }}>
                    {(team.members||[]).map(m=>{
                      const sm=MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
                      const shift=SHIFTS.find(s=>s.id===(m.shift||"flexible"))||SHIFTS[4];
                      return (
                        <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid var(--border)" }}>
                          <div style={{ position:"relative" }}>
                            <Avatar name={m.name} size={32}/>
                            <span style={{ position:"absolute", bottom:-1, right:-1, width:9, height:9, borderRadius:"50%", background:sm.dot, border:`2px solid ${AC.offwhite}`, display:"block" }}/>
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{m.name}</div>
                            <div style={{ fontSize:11, color:"var(--text-muted)" }}>{m.role} · {shift.icon} {m.shiftStart||shift.time.split(" – ")[0]} – {m.shiftEnd||shift.time.split(" – ")[1]}</div>
                          </div>
                          <span style={{ fontSize:11, fontWeight:600, color:sm.color, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:4, padding:"2px 8px", display:"inline-flex", alignItems:"center", gap:4 }}>
                            <span style={{ width:5, height:5, borderRadius:"50%", background:sm.dot, display:"block" }}/>{sm.label}
                            {m.status==="on_leave" && m.leaveUntil && <span style={{ fontSize:10, opacity:0.75 }}>until {m.leaveUntil}</span>}
                          </span>
                        </div>
                      );
                    })}
                    {(team.members||[]).length===0 && <div style={{ fontSize:12, color:"var(--text-muted)", textAlign:"center", padding:"12px 0" }}>No members yet</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Team task panel — slides in on right when a team is selected */}
        {viewTeam && (
          <div style={{ width:360, flexShrink:0, borderLeft:"1px solid var(--border)", background:"var(--bg)", minHeight:"calc(100vh - 140px)", display:"flex", flexDirection:"column" }}>
            {/* Panel header */}
            <div style={{ background:viewTeam.color, padding:"18px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#fff" }}>{viewTeam.name} — Tasks</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:3 }}>{viewTasks.length} task{viewTasks.length!==1?"s":""} · {viewTasks.filter(t=>t.status==="Done").length} completed</div>
              </div>
              <button onClick={()=>setSelectedTeam(null)} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:6, color:"#fff", width:28, height:28, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            </div>

            {/* Tasks grouped by status */}
            <div style={{ flex:1, overflowY:"auto", padding:"16px" }}>
              {viewTasks.length===0 ? (
                <div style={{ textAlign:"center", padding:"40px 0", color:"var(--text-muted)" }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
                  <div style={{ fontSize:13, fontWeight:500 }}>No tasks for this team yet</div>
                </div>
              ) : (
                STATUSES.map(status => {
                  const statusTasks = viewTasks.filter(t=>t.status===status);
                  if (!statusTasks.length) return null;
                  const sm = STATUS_META[status];
                  return (
                    <div key={status} style={{ marginBottom:18 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:8 }}>
                        <span style={{ width:7, height:7, borderRadius:"50%", background:sm.bar, display:"block" }}/>
                        <span style={{ fontSize:11, fontWeight:700, color:sm.color, textTransform:"uppercase", letterSpacing:"0.07em" }}>{status}</span>
                        <span style={{ fontSize:10, color:sm.color, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:8, padding:"0 6px", fontWeight:600 }}>{statusTasks.length}</span>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                        {statusTasks.map(t=>{
                          const pm = PRIORITY_META[t.priority];
                          const commentCount = (t.comments||[]).length;
                          return (
                            <div key={t.id} onClick={()=>onSelectTask&&onSelectTask(t)}
                              style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderLeft:`3px solid ${pm.color}`, borderRadius:6, padding:"10px 12px", cursor:"pointer", transition:"all 0.1s" }}
                              onMouseEnter={e=>{e.currentTarget.style.background=AC.purpleBg;e.currentTarget.style.borderColor=viewTeam.color;}}
                              onMouseLeave={e=>{e.currentTarget.style.background=AC.offwhite;e.currentTarget.style.borderColor=AC.border;}}>
                              <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:5, lineHeight:1.35 }}>{t.title}</div>
                              <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
                                <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, borderRadius:3, padding:"1px 6px", textTransform:"uppercase" }}>{t.priority}</span>
                                {(()=>{
                                  const assignees = getAssignees(t);
                                  if (!assignees.length) return null;
                                  return (
                                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                      <div style={{ display:"flex" }}>
                                        {assignees.slice(0,3).map((a,i)=>(
                                          <div key={a} title={a} style={{ marginLeft:i===0?0:-5, zIndex:3-i, position:"relative" }}>
                                            <Avatar name={a} size={16}/>
                                          </div>
                                        ))}
                                      </div>
                                      <span style={{ fontSize:11, color:"var(--text-second)" }}>
                                        {assignees.length===1 ? assignees[0].split(" ")[0] : `${assignees[0].split(" ")[0]} +${assignees.length-1}`}
                                      </span>
                                    </div>
                                  );
                                })()}
                                {commentCount>0 && <span style={{ fontSize:11, color:"var(--text-muted)" }}>💬 {commentCount}</span>}
                                <div style={{ marginLeft:"auto" }}><DeadlineBadge date={t.dueDate}/></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   REPORTS PAGE  (calendar + member inspector + charts)
══════════════════════════════════════════════════════════════ */

function Bar({ pct, color, height=18 }) {
  return (
    <div style={{ flex:1, background:"var(--bg-off)", borderRadius:3, height, overflow:"hidden", border:"1px solid var(--border)" }}>
      <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:3, transition:"width 0.5s" }}/>
    </div>
  );
}

function ReportsPage({ tasks, teams, getTeam }) {
  const [reportTab, setReportTab]       = useState("overview");
  const [memberPick, setMemberPick]     = useState(null);

  // ── KPIs ──
  const total    = tasks.length;
  const done     = tasks.filter(t=>t.status==="Done").length;
  const overdue  = tasks.filter(t=>t.dueDate&&daysUntil(t.dueDate)<0&&t.status!=="Done").length;
  const critical = tasks.filter(t=>t.priority==="Critical"&&t.status!=="Done").length;

  const byStatus   = STATUSES.map(s=>({ label:s, count:tasks.filter(t=>t.status===s).length, ...STATUS_META[s] }));
  const byPriority = PRIORITIES.map(p=>({ label:p, count:tasks.filter(t=>t.priority===p).length, ...PRIORITY_META[p] }));
  const byTeam     = teams.map(t=>({ ...t, count:tasks.filter(tk=>tk.team===t.id).length, done:tasks.filter(tk=>tk.team===t.id&&tk.status==="Done").length }));

  // ── All members flat list ──
  const allMembers = teams.flatMap(t=>(t.members||[]).map(m=>({...m, teamName:t.name, teamColor:t.color, teamId:t.id})));
  const pickedMember = allMembers.find(m=>m.id===memberPick);
  const memberTasks  = pickedMember ? tasks.filter(t=>getAssignees(t).includes(pickedMember.name)) : [];




  const TABS = [["overview","Overview"],["members","Members"]];

  return (
    <>
      <div style={{ background:"var(--bg)", borderBottom:"1px solid var(--border)", padding:"16px 32px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div>
            <div style={{ fontSize:20, fontWeight:700, color:"var(--text-primary)", letterSpacing:"-0.02em" }}>Reports & Analytics</div>
            <div style={{ fontSize:13, color:"var(--text-muted)" }}>Task progress, workload distribution and team performance</div>
          </div>
        </div>
        {/* Tab bar */}
        <div style={{ display:"flex", gap:0, borderBottom:"1px solid var(--border)", marginBottom:-17 }}>
          {TABS.map(([id,lbl])=>(
            <button key={id} onClick={()=>setReportTab(id)}
              style={{ background:"transparent", border:"none", borderBottom:reportTab===id?`3px solid ${AC.purple}`:"3px solid transparent", padding:"8px 20px", cursor:"pointer", fontSize:13, fontWeight:reportTab===id?700:400, color:reportTab===id?AC.purple:AC.textSecond, marginBottom:-1 }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:"24px 32px" }}>

        {/* ── OVERVIEW TAB ── */}
        {reportTab==="overview" && (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {/* KPI row */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
              {[
                { label:"Total Tasks",     value:total,    sub:`${done} completed`,         color:AC.purple,  bg:AC.purpleBg  },
                { label:"Completion Rate", value:`${total?Math.round(done/total*100):0}%`, sub:"tasks done", color:AC.success, bg:AC.successBg },
                { label:"Overdue",         value:overdue,  sub:"need immediate attention",  color:AC.danger,  bg:AC.dangerBg  },
                { label:"Critical Open",   value:critical, sub:"high priority pending",     color:AC.warning, bg:AC.warningBg },
              ].map(k=>(
                <div key={k.label} style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"18px 20px", borderTop:`4px solid ${k.color}`, boxShadow:"0 1px 4px rgba(26,5,51,0.05)" }}>
                  <div style={{ fontSize:28, fontWeight:700, color:k.color, marginBottom:4 }}>{k.value}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:2 }}>{k.label}</div>
                  <div style={{ fontSize:11, color:"var(--text-muted)" }}>{k.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"20px 24px" }}>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:16 }}>Tasks by Status</div>
                {byStatus.map(s=>(
                  <div key={s.label} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:s.color }}>{s.label}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:"var(--text-second)" }}>{s.count} <span style={{ color:"var(--text-muted)", fontWeight:400 }}>({total?Math.round(s.count/total*100):0}%)</span></span>
                    </div>
                    <Bar pct={total?s.count/total*100:0} color={s.bar}/>
                  </div>
                ))}
              </div>
              <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"20px 24px" }}>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:16 }}>Tasks by Priority</div>
                {byPriority.map(p=>(
                  <div key={p.label} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:p.color }}>{p.label}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:"var(--text-second)" }}>{p.count} <span style={{ color:"var(--text-muted)", fontWeight:400 }}>({total?Math.round(p.count/total*100):0}%)</span></span>
                    </div>
                    <Bar pct={total?p.count/total*100:0} color={p.color}/>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"20px 24px" }}>
              <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:16 }}>Workload by Team</div>
              {byTeam.map(t=>{
                const pct = t.count ? Math.round(t.done/t.count*100) : 0;
                return (
                  <div key={t.id} style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
                    <div style={{ width:100, flexShrink:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:t.color }}>{t.name}</div>
                      <div style={{ fontSize:10, color:"var(--text-muted)" }}>{(t.members||[]).length} members</div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", gap:2, height:14, borderRadius:3, overflow:"hidden", background:"var(--bg-off)", border:"1px solid var(--border)" }}>
                        {STATUSES.map(s=>{
                          const cnt=tasks.filter(tk=>tk.team===t.id&&tk.status===s).length;
                          const w=t.count?cnt/t.count*100:0;
                          if(!w) return null;
                          return <div key={s} title={`${s}: ${cnt}`} style={{ height:"100%", width:`${w}%`, background:STATUS_META[s].bar }}/>;
                        })}
                      </div>
                    </div>
                    <div style={{ width:60, flexShrink:0, textAlign:"right" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:t.color }}>{pct}%</div>
                      <div style={{ fontSize:10, color:"var(--text-muted)" }}>{t.done}/{t.count} done</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ display:"flex", gap:12, marginTop:8, paddingTop:12, borderTop:"1px solid var(--border)", flexWrap:"wrap" }}>
                {STATUSES.map(s=><div key={s} style={{ display:"flex", alignItems:"center", gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:STATUS_META[s].bar, display:"block" }}/><span style={{ fontSize:11, color:"var(--text-second)" }}>{s}</span></div>)}
              </div>
            </div>

            <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"20px 24px" }}>
              <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", marginBottom:16 }}>Team Availability Today</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10 }}>
                {teams.map(team=>{
                  const avail=(team.members||[]).filter(m=>["present","busy"].includes(m.status||"present")).length;
                  const tot=(team.members||[]).length;
                  const pct=tot?Math.round(avail/tot*100):0;
                  return (
                    <div key={team.id} style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"12px 14px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:team.color }}>{team.name}</span>
                        <span style={{ fontSize:13, fontWeight:700, color:pct>=80?AC.success:pct>=50?AC.warning:AC.danger }}>{pct}%</span>
                      </div>
                      <div style={{ height:5, background:AC.border, borderRadius:2, overflow:"hidden", marginBottom:6 }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:pct>=80?AC.success:pct>=50?AC.warning:AC.danger, borderRadius:2 }}/>
                      </div>
                      <div style={{ fontSize:11, color:"var(--text-muted)" }}>{avail} of {tot} available</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── MEMBERS TAB ── */}
        {reportTab==="members" && (
          <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:20 }}>
            {/* Member list */}
            <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
              <div style={{ padding:"14px 16px", borderBottom:"1px solid var(--border)", fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>
                Select a member
              </div>
              <div style={{ overflowY:"auto", maxHeight:600 }}>
                {teams.map(team=>(
                  <div key={team.id}>
                    <div style={{ padding:"8px 16px", background:"var(--bg-off)", borderBottom:"1px solid var(--border)", fontSize:10, fontWeight:700, color:team.color, letterSpacing:"0.08em", textTransform:"uppercase" }}>{team.name}</div>
                    {(team.members||[]).map(m=>{
                      const sm=MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
                      const mTasks=tasks.filter(t=>getAssignees(t).includes(m.name));
                      const isActive=memberPick===m.id;
                      return (
                        <div key={m.id} onClick={()=>setMemberPick(isActive?null:m.id)}
                          style={{ padding:"10px 16px", borderBottom:"1px solid var(--border)", cursor:"pointer", display:"flex", alignItems:"center", gap:10, background:isActive?AC.purpleBg:"transparent", borderLeft:isActive?`3px solid ${AC.purple}`:"3px solid transparent" }}
                          onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=AC.offwhite;}} onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                          <div style={{ position:"relative", flexShrink:0 }}>
                            <Avatar name={m.name} size={30}/>
                            <span style={{ position:"absolute", bottom:-1, right:-1, width:8, height:8, borderRadius:"50%", background:sm.dot, border:`2px solid ${AC.white}`, display:"block" }}/>
                          </div>
                          <div style={{ flex:1, overflow:"hidden" }}>
                            <div style={{ fontSize:12, fontWeight:600, color:isActive?AC.purple:"var(--text-primary)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{m.name}</div>
                            <div style={{ fontSize:10, color:"var(--text-muted)" }}>{m.role} · {mTasks.length} tasks</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Member detail */}
            <div>
              {pickedMember ? (
                <>
                  {/* Member header card */}
                  <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, padding:"20px 24px", marginBottom:16 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
                      <div style={{ position:"relative" }}>
                        <Avatar name={pickedMember.name} size={52}/>
                        {(()=>{const sm=MEMBER_STATUSES.find(s=>s.id===(pickedMember.status||"present"))||MEMBER_STATUSES[0]; return <span style={{ position:"absolute", bottom:0, right:0, width:13, height:13, borderRadius:"50%", background:sm.dot, border:`2px solid ${AC.white}`, display:"block" }}/>;})()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:18, fontWeight:700, color:"var(--text-primary)", marginBottom:2 }}>{pickedMember.name}</div>
                        <div style={{ fontSize:13, color:"var(--text-second)", marginBottom:6 }}>{pickedMember.role} · <span style={{ color:pickedMember.teamColor, fontWeight:600 }}>{pickedMember.teamName}</span></div>
                        {(()=>{const sm=MEMBER_STATUSES.find(s=>s.id===(pickedMember.status||"present"))||MEMBER_STATUSES[0]; const shift=SHIFTS.find(s=>s.id===(pickedMember.shift||"flexible"))||SHIFTS[4]; return (
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            <span style={{ fontSize:11, fontWeight:600, color:sm.color, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:4, padding:"3px 10px", display:"inline-flex", alignItems:"center", gap:4 }}><span style={{ width:6, height:6, borderRadius:"50%", background:sm.dot, display:"block" }}/>{sm.label}</span>
                            <span style={{ fontSize:11, color:"var(--text-second)", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, padding:"3px 10px" }}>{shift.icon} {pickedMember.shiftStart||"?"} – {pickedMember.shiftEnd||"?"}</span>
                          </div>
                        );})()}
                      </div>
                      {/* Mini stats */}
                      <div style={{ display:"flex", gap:12 }}>
                        {[
                          { label:"Total", value:memberTasks.length, color:AC.purple },
                          { label:"Active", value:memberTasks.filter(t=>t.status!=="Done").length, color:AC.info },
                          { label:"Done",   value:memberTasks.filter(t=>t.status==="Done").length,  color:AC.success },
                          { label:"Overdue",value:memberTasks.filter(t=>t.dueDate&&daysUntil(t.dueDate)<0&&t.status!=="Done").length, color:AC.danger },
                        ].map(s=>(
                          <div key={s.label} style={{ textAlign:"center", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"10px 14px", minWidth:56 }}>
                            <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
                            <div style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Working days */}
                    <div style={{ display:"flex", gap:4 }}>
                      {DAYS.map(d=><span key={d} style={{ width:30, height:26, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, background:(pickedMember.days||[]).includes(d)?pickedMember.teamColor:AC.offwhite, color:(pickedMember.days||[]).includes(d)?"#fff":AC.textMuted, border:`1px solid ${(pickedMember.days||[]).includes(d)?pickedMember.teamColor:AC.border}` }}>{d}</span>)}
                    </div>
                  </div>

                  {/* Task list */}
                  <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
                    <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)" }}>Ongoing Tasks</div>
                      <div style={{ fontSize:12, color:"var(--text-muted)" }}>{memberTasks.filter(t=>t.status!=="Done").length} active · {memberTasks.filter(t=>t.status==="Done").length} done</div>
                    </div>
                    {memberTasks.length===0
                      ? <div style={{ padding:"32px 0", textAlign:"center", color:"var(--text-muted)", fontSize:13 }}>No tasks assigned to {pickedMember.name}</div>
                      : memberTasks.map(t=>{
                          const team=getTeam(t.team), pm=PRIORITY_META[t.priority], sm=STATUS_META[t.status];
                          const isDone=t.status==="Done";
                          return (
                            <div key={t.id} style={{ padding:"12px 20px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:14, opacity:isDone?0.6:1 }}>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:4, textDecoration:isDone?"line-through":"none" }}>{t.title}</div>
                                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"1px 6px", textTransform:"uppercase" }}>{t.priority}</span>
                                  <span style={{ fontSize:11, fontWeight:600, color:sm.color, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:3, padding:"1px 6px" }}>{t.status}</span>
                                  {team && <span style={{ fontSize:11, color:team.color, fontWeight:500 }}>{team.name}</span>}
                                  {t.links?.length>0 && t.links.slice(0,1).map((url,i)=><LinkChip key={i} url={url}/>)}
                                </div>
                              </div>
                              <DeadlineBadge date={t.dueDate}/>
                            </div>
                          );
                        })
                    }
                  </div>
                </>
              ) : (
                <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", minHeight:300 }}>
                  <div style={{ textAlign:"center", color:"var(--text-muted)" }}>
                    <div style={{ fontSize:36, marginBottom:12 }}>👤</div>
                    <div style={{ fontSize:14, fontWeight:500, color:"var(--text-second)" }}>Select a member to view their tasks</div>
                    <div style={{ fontSize:12, marginTop:6 }}>Choose from the list on the left</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   MENTION HELPERS
══════════════════════════════════════════════════════════════ */
function extractMentions(text) {
  const matches = text.match(/@([A-Za-z]+)/g) || [];
  return matches.map(m => m.slice(1).toLowerCase());
}

function renderWithMentions(text) {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} style={{ color:AC.purple, fontWeight:700, background:AC.purpleBg, borderRadius:3, padding:"1px 4px" }}>
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/* ═══════════════════════════════════════════════════════════
   COMMENT PANEL  (reusable — used in TaskCard + MyTasksPage)
══════════════════════════════════════════════════════════════ */
const PROGRESS_TAGS = [
  { id:"update",   label:"Progress update", color:AC.info,    bg:AC.infoBg    },
  { id:"blocker",  label:"Blocker",         color:AC.danger,  bg:AC.dangerBg  },
  { id:"done",     label:"Completed",       color:AC.success, bg:AC.successBg },
  { id:"question", label:"Question",        color:AC.warning, bg:AC.warningBg },
  { id:"note",     label:"Note",            color:"var(--text-second)", bg:AC.offwhite },
];

function CommentPanel({ task, currentUser, onAddComment, onDeleteComment, teams }) {
  const [text, setText]       = useState("");
  const [tag,  setTag]        = useState("update");
  const [mentionQuery, setMentionQuery]   = useState(""); // text after @ 
  const [showMentions, setShowMentions]   = useState(false);
  const [mentionStart, setMentionStart]   = useState(-1); // cursor position where @ was typed
  const [hoveredComment,  setHoveredComment]  = useState(null); // comment id being hovered
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // comment id pending confirmation
  const textareaRef = React.useRef(null);
  const comments = task.comments || [];

  // Only show members from the team this task is assigned to
  const taskTeam = teams ? teams.find(t => t.id === task.team) : null;
  const allUsers = taskTeam
    ? (taskTeam.members || []).map(m => m.name)
    : teams
      ? [...new Set(teams.flatMap(t=>(t.members||[]).map(m=>m.name)))]
      : [];
  const filteredUsers = mentionQuery
    ? allUsers.filter(n => n.toLowerCase().startsWith(mentionQuery.toLowerCase()))
    : allUsers;

  function handleTextChange(e) {
    const val = e.target.value;
    const cursor = e.target.selectionStart;
    setText(val);

    // Detect if we're typing after an @
    const textUpToCursor = val.slice(0, cursor);
    const atMatch = textUpToCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionStart(cursor - atMatch[0].length);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery("");
    }
  }

  function insertMention(name) {
    const firstName = name.split(" ")[0];
    const before = text.slice(0, mentionStart);
    const after  = text.slice(textareaRef.current.selectionStart);
    const newText = before + "@" + firstName + " " + after;
    setText(newText);
    setShowMentions(false);
    setMentionQuery("");
    textareaRef.current.focus();
  }

  function submit() {
    const t = text.trim();
    if (!t) return;
    onAddComment(task.id, t, tag);
    setText("");
    setShowMentions(false);
  }

  function formatDate(iso) {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0,0,0,0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
    const dDay = new Date(d); dDay.setHours(0,0,0,0);
    const time = d.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"});
    if (dDay.getTime()===today.getTime())     return `Today at ${time}`;
    if (dDay.getTime()===yesterday.getTime()) return `Yesterday at ${time}`;
    return d.toLocaleDateString([], {day:"numeric", month:"short"}) + ` at ${time}`;
  }

  return (
    <div style={{ borderTop:"1px solid var(--border)", marginTop:10, paddingTop:12 }} onClick={e=>e.stopPropagation()}>
      {/* Header */}
      <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span>💬 Comments {comments.length>0 && <span style={{ background:"var(--border)", color:"var(--text-second)", borderRadius:8, padding:"1px 7px", fontSize:10, fontWeight:600, marginLeft:4 }}>{comments.length}</span>}</span>
      </div>

      {/* Existing comments */}
      {comments.length>0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:12, maxHeight:220, overflowY:"auto" }}>
          {comments.map(c=>{
            const tagMeta   = PROGRESS_TAGS.find(p=>p.id===c.tag) || PROGRESS_TAGS[0];
            const isAuthor  = c.author === currentUser;
            const isHovered = hoveredComment === c.id;
            const isPending = confirmDeleteId === c.id;
            return (
              <div key={c.id}
                onMouseEnter={()=>{ if(isAuthor) setHoveredComment(c.id); }}
                onMouseLeave={()=>{ if(!isPending) setHoveredComment(null); }}
                style={{ display:"flex", gap:8 }}>
                <Avatar name={c.author} size={26}/>
                <div style={{ flex:1 }}>
                  <div style={{
                    background: isPending ? AC.dangerBg+"88" : "var(--bg-off)",
                    border:`1px solid ${isPending ? AC.danger+"66" : "var(--border)"}`,
                    borderRadius:8, borderTopLeftRadius:2, padding:"8px 12px",
                    transition:"all 0.15s"
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5, flexWrap:"wrap" }}>
                      <span style={{ fontSize:12, fontWeight:700, color:"var(--text-primary)" }}>{c.author.split(" ")[0]}</span>
                      <span style={{ fontSize:10, fontWeight:700, color:tagMeta.color, background:tagMeta.bg, borderRadius:4, padding:"1px 7px" }}>{tagMeta.label}</span>
                      <span style={{ fontSize:10, color:"var(--text-muted)", marginLeft:"auto" }}>{formatDate(c.at)}</span>
                      {/* Delete button — only on hover for the author */}
                      {isAuthor && isHovered && !isPending && (
                        <button
                          onClick={e=>{ e.stopPropagation(); setConfirmDeleteId(c.id); }}
                          title="Delete comment"
                          style={{ background:"transparent", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:12, padding:"0 2px", lineHeight:1, marginLeft:4, borderRadius:3, transition:"color 0.1s" }}
                          onMouseEnter={e=>e.currentTarget.style.color=AC.danger}
                          onMouseLeave={e=>e.currentTarget.style.color="var(--text-muted)"}>
                          🗑
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:"var(--text-primary)", lineHeight:1.55, whiteSpace:"pre-wrap" }}>
                      {renderWithMentions(c.text)}
                    </div>
                    {/* Inline confirmation */}
                    {isPending && (
                      <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8, paddingTop:8, borderTop:`1px solid ${AC.danger}44` }}>
                        <span style={{ fontSize:11, color:AC.danger, fontWeight:600, flex:1 }}>Delete this comment?</span>
                        <button
                          onClick={e=>{
                            e.stopPropagation();
                            onDeleteComment && onDeleteComment(task.id, c.id);
                            setConfirmDeleteId(null);
                            setHoveredComment(null);
                          }}
                          style={{ background:AC.danger, border:"none", borderRadius:4, color:"#fff", padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                          Delete
                        </button>
                        <button
                          onClick={e=>{
                            e.stopPropagation();
                            setConfirmDeleteId(null);
                            setHoveredComment(null);
                          }}
                          style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"3px 8px", fontSize:11, cursor:"pointer" }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {comments.length===0 && (
        <div style={{ textAlign:"center", padding:"14px 0 10px", color:"var(--text-muted)", fontSize:12 }}>
          No comments yet — type @ to mention a teammate
        </div>
      )}

      {/* Tag selector */}
      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
        {PROGRESS_TAGS.map(p=>(
          <button key={p.id} onClick={()=>setTag(p.id)}
            style={{ background:tag===p.id?p.bg:"transparent", border:`1.5px solid ${tag===p.id?p.color:AC.border}`, borderRadius:5, padding:"3px 10px", cursor:"pointer", fontSize:11, fontWeight:tag===p.id?700:400, color:tag===p.id?p.color:"var(--text-second)", transition:"all 0.1s" }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Input row */}
      <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
        <Avatar name={currentUser||"?"} size={26}/>
        <div style={{ flex:1, position:"relative" }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={e=>{
              if (showMentions && (e.key==="Escape")) { setShowMentions(false); return; }
              if (showMentions && e.key==="Enter") { e.preventDefault(); if(filteredUsers.length>0) insertMention(filteredUsers[0]); return; }
              if (e.key==="Enter"&&!e.shiftKey&&!showMentions) { e.preventDefault(); submit(); }
            }}
            placeholder="Write a comment… type @ to mention someone"
            rows={2}
            style={{ width:"100%", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, fontSize:12, padding:"8px 10px", outline:"none", fontFamily:"inherit", color:"var(--text-primary)", resize:"none", boxSizing:"border-box", lineHeight:1.5 }}/>

          {/* @ mention dropdown */}
          {showMentions && filteredUsers.length>0 && (
            <div style={{ position:"absolute", bottom:"calc(100% + 4px)", left:0, background:"var(--bg)", border:"1px solid var(--border)", borderRadius:6, boxShadow:"0 4px 16px rgba(26,5,51,0.15)", zIndex:200, minWidth:200, maxHeight:180, overflowY:"auto" }}>
              <div style={{ padding:"6px 10px", fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", borderBottom:"1px solid var(--border)" }}>
                Mention a teammate
              </div>
              {filteredUsers.map(name=>(
                <div key={name} onClick={()=>insertMention(name)}
                  style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", cursor:"pointer", transition:"background 0.1s" }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg-off)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <Avatar name={name} size={22}/>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{name}</div>
                    <div style={{ fontSize:10, color:"var(--text-muted)" }}>{(teams||[]).flatMap(t=>(t.members||[])).find(m=>m.name===name)?.role||""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:6 }}>
            <span style={{ fontSize:10, color:"var(--text-muted)" }}>Type @ to mention · Enter to send</span>
            <button onClick={submit} disabled={!text.trim()}
              style={{ background:text.trim()?AC.purple:"#ddd", border:"none", borderRadius:5, color:"#fff", padding:"6px 16px", cursor:text.trim()?"pointer":"default", fontSize:12, fontWeight:600, transition:"background 0.15s" }}>
              Post
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TASK DETAIL MODAL
══════════════════════════════════════════════════════════════ */
function TaskDetailModal({ task, team, onClose, onStatusChange, onEdit, onReassign, onAddComment, onDeleteComment, currentUser, teams }) {
  const pm = PRIORITY_META[task.priority];
  const sm = STATUS_META[task.status];
  const tc = team?.color || AC.textMuted;
  const comments = task.comments || [];
  const [aiSummary, setAiSummary]   = useState("");
  const [aiSumLoading, setAiSumLoading] = useState(false);

  async function summarizeComments() {
    if (!comments.length) return;
    setAiSumLoading(true);
    setAiSummary("");
    try {
      const commentText = comments.map(c=>`${c.author} (${c.tag}): ${c.text}`).join("\n");
      const result = await callClaude(
        `You are a work management assistant. Summarize the following task comment thread in 1-2 sentences. Focus on current status, any blockers, and next steps.\n\nTask: "${task.title}"\n\nComments:\n${commentText}\n\nReply with only the summary, no labels or preamble.`
      );
      setAiSummary(result);
    } catch(e) { setAiSummary("Could not generate summary: " + e.message); }
    finally { setAiSumLoading(false); }
  }

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{ position:"fixed", inset:0, background:"rgba(26,5,51,0.5)", zIndex:1000, display:"flex", justifyContent:"flex-end", backdropFilter:"blur(2px)" }}>
      <div style={{ width:520, maxWidth:"96vw", background:"var(--bg)", height:"100%", overflowY:"auto", boxShadow:"-8px 0 40px rgba(26,5,51,0.18)", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ background:AC.navy, padding:"20px 24px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ flex:1, marginRight:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"2px 8px", textTransform:"uppercase", letterSpacing:"0.06em" }}>{task.priority}</span>
                {team && <span style={{ fontSize:11, fontWeight:600, color:tc, background:tc+"22", borderRadius:4, padding:"2px 8px" }}>{team.name}</span>}
              </div>
              <div style={{ fontSize:17, fontWeight:700, color:"#fff", lineHeight:1.35 }}>{task.title}</div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:6, color:"rgba(255,255,255,0.7)", width:30, height:30, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
          </div>
          {/* Status + actions row */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <select value={task.status} onChange={e=>onStatusChange(task.id,e.target.value)}
              style={{ background:sm.bg, border:`1.5px solid ${sm.border}`, borderRadius:5, color:sm.color, fontSize:12, fontWeight:600, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit" }}>
              {STATUSES.map(s=><option key={s} style={{ background:"var(--bg)", color:"var(--text-primary)" }}>{s}</option>)}
            </select>
            <button onClick={()=>{onEdit(task);onClose();}} style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:5, color:"rgba(255,255,255,0.8)", padding:"5px 12px", cursor:"pointer", fontSize:12 }}>✎ Edit</button>
            <button onClick={()=>{onReassign(task);onClose();}} style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:5, color:"rgba(255,255,255,0.8)", padding:"5px 12px", cursor:"pointer", fontSize:12 }}>⇄ Reassign</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, padding:"24px", display:"flex", flexDirection:"column", gap:20 }}>

          {/* Meta grid */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {/* Assignees */}
            <div style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"10px 14px" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>
                Assignee{getAssignees(task).length!==1?"s":""}
              </div>
              {(()=>{
                const assignees = getAssignees(task);
                if (!assignees.length) return <span style={{ fontSize:13, color:"var(--text-muted)" }}>Unassigned</span>;
                return (
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {assignees.map(a=>(
                      <div key={a} style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <Avatar name={a} size={22}/>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{a}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            {/* Due date */}
            <div style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"10px 14px" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>Due date</div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13, fontWeight:600, color:task.dueDate&&daysUntil(task.dueDate)<0?AC.danger:"var(--text-primary)" }}>
                  {task.dueDate ? new Date(task.dueDate+"T00:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) : "No due date"}
                </span>
                <DeadlineBadge date={task.dueDate}/>
              </div>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Description</div>
              <div style={{ fontSize:13, color:"var(--text-primary)", lineHeight:1.65, whiteSpace:"pre-wrap", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"12px 14px" }}>{task.description}</div>
            </div>
          )}

          {/* Links */}
          {task.links?.length>0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>References</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                {task.links.map((url,i)=><LinkChip key={i} url={url}/>)}
              </div>
            </div>
          )}

          {/* History */}
          {task.history?.length>0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Assignment history</div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {task.history.map((h,i)=>(
                  <div key={i} style={{ fontSize:11, color:"var(--text-second)", display:"flex", alignItems:"center", gap:6 }}>
                    <Avatar name={h.from||"?"} size={16}/>
                    <span>{h.from}</span>
                    <span style={{ color:"var(--text-muted)" }}>→</span>
                    <Avatar name={h.to||"?"} size={16}/>
                    <span>{h.to}</span>
                    <span style={{ color:"var(--text-muted)", marginLeft:"auto" }}>{new Date(h.at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div style={{ flex:1 }}>
            {/* AI Summary button — only show if there are comments */}
            {comments.length>=2 && (
              <div style={{ marginBottom:12 }}>
                <button onClick={summarizeComments} disabled={aiSumLoading}
                  style={{ background:AC.purpleBg, border:`1px solid ${AC.purple}`, borderRadius:4, color:AC.purple, padding:"5px 14px", cursor:aiSumLoading?"default":"pointer", fontSize:12, fontWeight:600, display:"flex",alignItems:"center",gap:6 }}>
                  {aiSumLoading ? "⏳ Summarising…" : "✨ AI Summarise comments"}
                </button>
                {aiSummary && (
                  <div style={{ marginTop:8, background:AC.purpleBg, border:`1px solid ${AC.purpleDim}`, borderRadius:6, padding:"10px 14px", fontSize:12, color:"var(--text-primary)", lineHeight:1.6 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:AC.purple, textTransform:"uppercase", letterSpacing:"0.06em", display:"block", marginBottom:4 }}>✨ AI Summary</span>
                    {aiSummary}
                    <button onClick={()=>setAiSummary("")} style={{ background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:11,marginLeft:8 }}>✕ dismiss</button>
                  </div>
                )}
              </div>
            )}
            <CommentPanel task={task} currentUser={currentUser} onAddComment={onAddComment} onDeleteComment={onDeleteComment} teams={teams}/>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   BOARD VIEW  — with drag & drop
══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   DASHBOARD CALENDAR VIEW
══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   DASHBOARD CALENDAR  — Outlook-style week/month view
══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   MEETING DETAIL MODAL
══════════════════════════════════════════════════════════════ */
function MeetingDetailModal({ meeting, onClose, onEdit }) {
  const m = meeting;
  const color = m.color || "#0065FF";

  function formatDate(iso) {
    if (!iso) return "—";
    const [y,mo,d] = iso.split("-").map(Number);
    return new Date(y, mo-1, d).toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" });
  }

  const inviteeSummary = m.teamId
    ? null // team shown separately
    : (m.invitees||[]).length > 0
    ? m.invitees
    : null;

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{ position:"fixed",inset:0,background:"rgba(26,5,51,0.5)",zIndex:1000,display:"flex",justifyContent:"flex-end",backdropFilter:"blur(2px)" }}>
      <div style={{ width:420,maxWidth:"96vw",background:"var(--bg)",height:"100%",overflowY:"auto",boxShadow:"-8px 0 40px rgba(26,5,51,0.18)",display:"flex",flexDirection:"column" }}>

        {/* Header */}
        <div style={{ background:color,padding:"24px 24px 20px",flexShrink:0 }}>
          <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12 }}>
            <div style={{ flex:1,marginRight:12 }}>
              <div style={{ fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>🗓 Meeting / Event</div>
              <div style={{ fontSize:20,fontWeight:700,color:"#fff",lineHeight:1.3 }}>{m.title}</div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:6,color:"#fff",width:30,height:30,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>✕</button>
          </div>
          <button onClick={onEdit}
            style={{ background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.35)",borderRadius:5,color:"#fff",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5 }}>
            ✎ Edit Event
          </button>
        </div>

        {/* Body */}
        <div style={{ flex:1,padding:"24px",display:"flex",flexDirection:"column",gap:18 }}>

          {/* Date + Time */}
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
            <div style={{ background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:"12px 14px" }}>
              <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6 }}>Date</div>
              <div style={{ fontSize:13,fontWeight:600,color:"var(--text-primary)" }}>{formatDate(m.date)}</div>
            </div>
            <div style={{ background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:"12px 14px" }}>
              <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6 }}>Time</div>
              <div style={{ fontSize:13,fontWeight:600,color:"var(--text-primary)",fontFamily:"monospace" }}>
                {m.startTime||"–"} → {m.endTime||"–"}
              </div>
              {m.startTime && m.endTime && (()=>{
                const [sh,sm] = m.startTime.split(":").map(Number);
                const [eh,em] = m.endTime.split(":").map(Number);
                const mins = (eh*60+em)-(sh*60+sm);
                if (mins<=0) return null;
                return <div style={{ fontSize:11,color:"var(--text-muted)",marginTop:3 }}>{mins>=60?`${Math.floor(mins/60)}h${mins%60>0?` ${mins%60}m`:""}`:` ${mins}m`} duration</div>;
              })()}
            </div>
          </div>

          {/* Notes */}
          {m.notes && (
            <div>
              <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:8 }}>Notes</div>
              <div style={{ fontSize:13,color:"var(--text-primary)",lineHeight:1.65,whiteSpace:"pre-wrap",background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:"12px 14px" }}>{m.notes}</div>
            </div>
          )}

          {/* Notified */}
          {(m.teamId || inviteeSummary) && (
            <div>
              <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:8 }}>Notified</div>
              {m.teamId && (
                <div style={{ fontSize:13,color:"var(--text-primary)",background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:"10px 14px",display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ fontSize:16 }}>👥</span>
                  <span>Entire team was notified</span>
                </div>
              )}
              {inviteeSummary && (
                <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                  {inviteeSummary.map(name=>(
                    <div key={name} style={{ display:"flex",alignItems:"center",gap:5,background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:20,padding:"4px 10px 4px 6px" }}>
                      <Avatar name={name} size={18}/>
                      <span style={{ fontSize:12,fontWeight:500,color:"var(--text-primary)" }}>{name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Color swatch */}
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ width:14,height:14,borderRadius:"50%",background:color,display:"block",flexShrink:0 }}/>
            <span style={{ fontSize:12,color:"var(--text-muted)" }}>Event colour</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardCalendar({ tasks, getTeam, teams, currentUser, addNotif, addActivity,
  calMonth, setCalMonth, calSelected, setCalSelected,
  calView, setCalView, calWeekStart, setCalWeekStart,
  meetings, setMeetings, showMeetingForm, setShowMeetingForm, meetingFormData, setMeetingFormData,
  selectedMeeting, setSelectedMeeting,
  onSelectTask }) {

  const HOURS = Array.from({length:24}, (_,i) => i);
  const today = new Date(); today.setHours(0,0,0,0);
  const timeGridRef = React.useRef(null);
  const [expandedDay, setExpandedDay] = React.useState(null); // date ISO string

  // Auto-scroll to current time on mount / view change
  React.useEffect(() => {
    if (calView !== "week") return;
    const timer = setTimeout(() => {
      if (timeGridRef.current) {
        const now = new Date();
        const ROW_H = 56; // minHeight of each hour row
        const scrollTo = Math.max(0, (now.getHours() - 1) * ROW_H);
        timeGridRef.current.scrollTop = scrollTo;
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [calView, calWeekStart]);

  // ── Week helpers ──────────────────────────────────────────
  const weekDays = Array.from({length:7}, (_,i) => {
    const d = new Date(calWeekStart); d.setDate(calWeekStart.getDate()+i); return d;
  });

  function prevWeek() { const d=new Date(calWeekStart); d.setDate(d.getDate()-7); setCalWeekStart(d); }
  function nextWeek() { const d=new Date(calWeekStart); d.setDate(d.getDate()+7); setCalWeekStart(d); }
  function goToday()  {
    const d=new Date(); d.setHours(0,0,0,0);
    const day=d.getDay()===0?6:d.getDay()-1; d.setDate(d.getDate()-day); setCalWeekStart(d);
  }

  const weekLabel = `${weekDays[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDays[6].toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`;

  // ── Tasks per day ─────────────────────────────────────────
  function tasksOnDate(date) {
    // Use local date parts to avoid UTC timezone shift
    const iso = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
    return tasks.filter(t => t.dueDate===iso);
  }

  function meetingsOnDate(date) {
    const iso = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
    const userName = currentUser?.name;
    // Find all team IDs the current user belongs to
    const myTeamIds = new Set((teams||[]).filter(t=>(t.members||[]).some(m=>m.name===userName)).map(t=>t.id));
    return (meetings||[]).filter(m => {
      if (m.date !== iso) return false;
      const teamIds = m.teamIds?.length ? m.teamIds : (m.teamId ? [m.teamId] : []);
      const invitees = m.invitees || [];
      const hasTeams = teamIds.length > 0;
      const hasInvitees = invitees.length > 0;
      // "Just me" — no teams, no invitees
      if (!hasTeams && !hasInvitees) return m.createdBy === userName;
      // Check if user is in any of the notified teams
      if (hasTeams && teamIds.some(tid => myTeamIds.has(tid))) return true;
      // Check if user is individually invited or is creator
      if (hasInvitees && (invitees.includes(userName) || m.createdBy === userName)) return true;
      // Creator always sees their own event
      if (m.createdBy === userName) return true;
      return false;
    });
  }

  async function saveMeeting(meeting) {
    setMeetings(cur => cur.some(m=>m.id===meeting.id) ? cur.map(m=>m.id===meeting.id?meeting:m) : [...cur, meeting]);
    await upsertMeeting(meeting);
  }

  function openNewMeeting(date, hour) {
    const iso = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
    setMeetingFormData({ date:iso, startTime:`${String(hour).padStart(2,"0")}:00`, endTime:`${String(hour+1).padStart(2,"0")}:00`, title:"", notes:"", color:"#0065FF" });
    setShowMeetingForm(true);
  }

  async function deleteMeeting(id) {
    setMeetings(cur => cur.filter(m=>m.id!==id));
    await deleteMeetingById(id);
  }

  // ── Month view helpers ────────────────────────────────────
  function calDays(y,m) {
    const first=new Date(y,m,1).getDay(); const total=new Date(y,m+1,0).getDate();
    return { first:first===0?6:first-1, total };
  }
  function tasksOnDay(y,m,d) {
    const iso=`${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    return tasks.filter(t=>t.dueDate===iso);
  }
  const { first, total:daysInMonth } = calDays(calMonth.y, calMonth.m);
  const calCells = Array.from({length:first+daysInMonth}, (_,i)=>i<first?null:i-first+1);
  const monthName = new Date(calMonth.y,calMonth.m,1).toLocaleString("default",{month:"long",year:"numeric"});

  const MEETING_COLORS = ["#0065FF","#A100FF","#00875A","#FF8B00","#DE350B","#6554C0","#00B8D9"];

  // ── Meeting form modal ────────────────────────────────────
  function MeetingForm() {
    const [form, setForm] = React.useState(meetingFormData || {});
    // Multi-team: array of teamIds; extra individuals outside those teams
    const [selTeamIds, setSelTeamIds] = React.useState(
      meetingFormData?.teamIds ? meetingFormData.teamIds
      : meetingFormData?.teamId ? [meetingFormData.teamId] : []
    );
    const [selInvitees, setSelInvitees] = React.useState(meetingFormData?.invitees || []);

    // Only teams the current user belongs to, with at least 1 member
    const myTeamsForForm = (teams||[]).filter(t =>
      (t.members||[]).length > 0 &&
      (t.members||[]).some(m => m.name === currentUser?.name)
    );
    // All members across the user's own teams only
    const allMembers = myTeamsForForm.flatMap(t=>(t.members||[]).map(m=>({...m, teamName:t.name, teamId:t.id})));
    // Members already covered by selected teams
    const teamMemberNames = new Set(
      myTeamsForForm.filter(t=>selTeamIds.includes(t.id)).flatMap(t=>(t.members||[]).map(m=>m.name))
    );
    // Members NOT in any selected team (for extra individual picks), excluding self
    const nonTeamMembers = allMembers.filter(m => !teamMemberNames.has(m.name) && m.name !== currentUser?.name);

    function toggleTeam(id) {
      setSelTeamIds(cur => cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id]);
      // Remove invitees now covered by newly added team
      const newTeam = myTeamsForForm.find(t=>t.id===id);
      if (newTeam) {
        const names = (newTeam.members||[]).map(m=>m.name);
        setSelInvitees(cur => cur.filter(n => !names.includes(n)));
      }
    }
    function toggleInvitee(name) {
      setSelInvitees(cur => cur.includes(name) ? cur.filter(x=>x!==name) : [...cur, name]);
    }

    const totalNotified = [...new Set([
      ...myTeamsForForm.filter(t=>selTeamIds.includes(t.id)).flatMap(t=>(t.members||[]).map(m=>m.name)),
      ...selInvitees,
    ])].filter(n => n !== currentUser?.name);

    async function save() {
      if (!form.title?.trim()) return;
      const saved = {
        ...form,
        id: form.id || genId(),
        teamIds:  selTeamIds,
        teamId:   selTeamIds[0] || null,
        invitees: selInvitees,
        createdBy: form.createdBy || currentUser?.name || "",
      };
      await saveMeeting(saved);

      if (!form.id) {
        const from = currentUser?.name || "Someone";
        const label = `${saved.title} on ${saved.date} ${saved.startTime}–${saved.endTime}`;
        selTeamIds.forEach(tid => {
          const team = (teams||[]).find(t=>t.id===tid);
          if (team) {
            (team.members||[]).filter(m=>m.name!==from).forEach(m =>
              addNotif && addNotif(m.name, "meeting", label, from, tid)
            );
            addActivity && addActivity(from, `scheduled meeting "${saved.title}"`, saved.title, tid);
          }
        });
        selInvitees.filter(n=>n!==from).forEach(n =>
          addNotif && addNotif(n, "meeting", label, from, null)
        );
        if (selInvitees.length) {
          addActivity && addActivity(from, `invited ${selInvitees.join(", ")} to "${saved.title}"`, saved.title, null);
        }
      }

      setShowMeetingForm(false);
    }

    return (
      <div onClick={e=>e.target===e.currentTarget&&setShowMeetingForm(false)}
        style={{ position:"fixed",inset:0,background:"rgba(26,5,51,0.55)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(3px)" }}>
        <div style={{ background:"var(--bg)",borderRadius:8,width:480,maxWidth:"96vw",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(26,5,51,0.25)",overflow:"hidden" }}>
          <div style={{ background:AC.navy,padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
            <div style={{ color:"#fff",fontSize:15,fontWeight:700 }}>{form.id?"Edit Event":"New Event"}</div>
            <button onClick={()=>setShowMeetingForm(false)} style={{ background:"rgba(255,255,255,0.1)",border:"none",borderRadius:4,color:"rgba(255,255,255,0.7)",width:26,height:26,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
          </div>
          <div style={{ padding:"20px",display:"flex",flexDirection:"column",gap:14,overflowY:"auto",maxHeight:"calc(90vh - 60px)" }}>
            {/* Title */}
            <div>
              <label style={LBL}>Title *</label>
              <input style={INP} value={form.title||""} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Event title" autoFocus/>
            </div>

            {/* Date + Time */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10 }}>
              <div>
                <label style={LBL}>Date</label>
                <input type="date" style={{...INP,colorScheme:"light"}} value={form.date||""} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
              </div>
              <div>
                <label style={LBL}>Start</label>
                <input type="time" style={{...INP,colorScheme:"light"}} value={form.startTime||""} onChange={e=>setForm(f=>({...f,startTime:e.target.value}))}/>
              </div>
              <div>
                <label style={LBL}>End</label>
                <input type="time" style={{...INP,colorScheme:"light"}} value={form.endTime||""} onChange={e=>setForm(f=>({...f,endTime:e.target.value}))}/>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={LBL}>Notes (optional)</label>
              <textarea style={{...INP,resize:"vertical",minHeight:56}} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Agenda, location, link…"/>
            </div>

            {/* Notify — multi-team + extra individuals */}
            <div>
              <label style={LBL}>Notify</label>

              {/* Teams — multi-select checkboxes */}
              <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:6 }}>Teams</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                {myTeamsForForm.map(t => {
                  const sel = selTeamIds.includes(t.id);
                  return (
                    <button key={t.id} onClick={()=>toggleTeam(t.id)}
                      style={{ display:"flex", alignItems:"center", gap:6, background:sel?t.color+"18":"var(--bg-off)", border:`1.5px solid ${sel?t.color:"var(--border)"}`, borderRadius:20, padding:"4px 12px 4px 8px", cursor:"pointer", fontSize:12, fontWeight:sel?700:400, color:sel?t.color:"var(--text-second)", transition:"all 0.1s" }}>
                      <span style={{ width:8, height:8, borderRadius:"50%", background:t.color, display:"block", flexShrink:0 }}/>
                      {t.name}
                      <span style={{ fontSize:10, opacity:0.65 }}>({(t.members||[]).length})</span>
                      {sel && <span style={{ fontSize:11, color:t.color }}>✓</span>}
                    </button>
                  );
                })}
              </div>

              {/* Extra individuals — only people NOT already in a selected team */}
              <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:6 }}>
                Additional Individuals {nonTeamMembers.length===0 && selTeamIds.length>0 && <span style={{ fontWeight:400, textTransform:"none", fontSize:10 }}>— all members covered by selected teams</span>}
              </div>
              {nonTeamMembers.length > 0 ? (
                <div style={{ position:"relative", marginBottom:8 }}>
                  <select onChange={e=>{ if(e.target.value) toggleInvitee(e.target.value); e.target.value=""; }}
                    style={{ ...INP, appearance:"none", WebkitAppearance:"none", paddingRight:28, cursor:"pointer" }}>
                    <option value="">Add person…</option>
                    {nonTeamMembers.filter(m=>!selInvitees.includes(m.name))
                      .map(m=><option key={m.id} value={m.name}>{m.name} — {m.role} ({m.teamName})</option>)}
                  </select>
                  <span style={{ position:"absolute", right:9, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:10, color:"var(--text-muted)" }}>▾</span>
                </div>
              ) : (
                <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:8 }}>
                  {selTeamIds.length===0 ? "Select a team above, or add individuals below." : ""}
                </div>
              )}
              {selInvitees.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
                  {selInvitees.map(name=>(
                    <div key={name} style={{ display:"flex", alignItems:"center", gap:5, background:"var(--purple-bg)", border:`1px solid var(--purple-dim)`, borderRadius:20, padding:"3px 8px 3px 6px" }}>
                      <Avatar name={name} size={16}/>
                      <span style={{ fontSize:11, fontWeight:600, color:AC.purple }}>{name.split(" ")[0]}</span>
                      <button onClick={()=>toggleInvitee(name)} style={{ background:"none", border:"none", color:AC.purple, cursor:"pointer", fontSize:11, padding:0, lineHeight:1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              {totalNotified.length > 0 && (
                <div style={{ fontSize:11, color:"var(--text-muted)", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"7px 12px", display:"flex", alignItems:"center", gap:6 }}>
                  <span>🔔</span>
                  <span><strong style={{ color:"var(--text-primary)" }}>{totalNotified.length} {totalNotified.length===1?"person":"people"}</strong> will be notified
                    {selTeamIds.length > 0 && <span style={{ color:"var(--text-muted)" }}> ({myTeamsForForm.filter(t=>selTeamIds.includes(t.id)).map(t=>t.name).join(", ")}{selInvitees.length>0?` + ${selInvitees.length} individual${selInvitees.length>1?"s":""}`:""} )</span>}
                  </span>
                </div>
              )}
              {totalNotified.length === 0 && (
                <div style={{ fontSize:11, color:"var(--text-muted)", fontStyle:"italic" }}>Only visible to you</div>
              )}
            </div>

            {/* Color */}
            <div>
              <label style={LBL}>Color</label>
              <div style={{ display:"flex",gap:7 }}>
                {MEETING_COLORS.map(c=>(
                  <button key={c} onClick={()=>setForm(f=>({...f,color:c}))}
                    style={{ width:24,height:24,borderRadius:"50%",background:c,border:form.color===c?`3px solid var(--text-primary)`:"3px solid transparent",cursor:"pointer",outline:form.color===c?`2px solid ${c}`:"none",outlineOffset:2 }}/>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",paddingTop:4 }}>
              {form.id && <button onClick={()=>{deleteMeeting(form.id);setShowMeetingForm(false);}} style={{ background:AC.dangerBg,border:`1px solid ${AC.danger}33`,borderRadius:4,color:AC.danger,padding:"8px 14px",cursor:"pointer",fontSize:12,marginRight:"auto" }}>Delete</button>}
              <button onClick={()=>setShowMeetingForm(false)} style={BTN_GHOST}>Cancel</button>
              <button onClick={save} disabled={!form.title?.trim()} style={{...BTN_PRIMARY,opacity:form.title?.trim()?1:0.4}}>
                {form.id ? "Save Changes" : "Create Event"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showMeetingForm && <MeetingForm/>}

      {/* ── Toolbar ── */}
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap" }}>
        <button onClick={goToday} style={{ background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600,color:"var(--text-second)" }}>Today</button>
        <div style={{ display:"flex",gap:2 }}>
          <button onClick={calView==="week"?prevWeek:()=>setCalMonth(c=>c.m===0?{y:c.y-1,m:11}:{y:c.y,m:c.m-1})}
            style={{ background:"var(--bg)",border:"1px solid var(--border)",borderRadius:"6px 0 0 6px",width:30,height:30,cursor:"pointer",fontSize:14,color:"var(--text-second)",display:"flex",alignItems:"center",justifyContent:"center" }}>‹</button>
          <button onClick={calView==="week"?nextWeek:()=>setCalMonth(c=>c.m===11?{y:c.y+1,m:0}:{y:c.y,m:c.m+1})}
            style={{ background:"var(--bg)",border:"1px solid var(--border)",borderLeft:"none",borderRadius:"0 6px 6px 0",width:30,height:30,cursor:"pointer",fontSize:14,color:"var(--text-second)",display:"flex",alignItems:"center",justifyContent:"center" }}>›</button>
        </div>
        <div style={{ fontSize:16,fontWeight:700,color:"var(--text-primary)",flex:1 }}>
          {calView==="week"?weekLabel:monthName}
        </div>
        <button onClick={()=>setShowMeetingForm(true)||setMeetingFormData({date:new Date().toISOString().split("T")[0],startTime:"09:00",endTime:"10:00",title:"",notes:"",color:"#0065FF"})}
          style={{ ...BTN_PRIMARY,fontSize:12,padding:"6px 14px",display:"flex",alignItems:"center",gap:5 }}>
          + New Event
        </button>
        <div style={{ display:"flex",border:"1.5px solid var(--border)",borderRadius:6,overflow:"hidden" }}>
          {[["week","Week"],["month","Month"]].map(([v,l])=>(
            <button key={v} onClick={()=>setCalView(v)}
              style={{ background:calView===v?AC.navy:"transparent",border:"none",color:calView===v?"#fff":"var(--text-second)",padding:"5px 14px",cursor:"pointer",fontSize:12,fontWeight:calView===v?600:400 }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ── WEEK VIEW ── */}
      {calView==="week" && (
        <div style={{ background:"var(--bg)",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden" }}>
          {/* Day headers */}
          <div style={{ display:"grid",gridTemplateColumns:"56px repeat(7,1fr)",borderBottom:"1px solid var(--border)" }}>
            <div style={{ background:"var(--bg-off)" }}/>
            {weekDays.map((d,i) => {
              const isToday = d.getTime()===today.getTime();
              const dayTasks = tasksOnDate(d);
              const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
              const isExpanded = expandedDay===iso;
              return (
                <div key={i}
                  onClick={()=>setExpandedDay(isExpanded?null:iso)}
                  style={{ borderLeft:"1px solid var(--border)",background:isExpanded?AC.purpleBg:"var(--bg-off)",padding:"8px 10px",textAlign:"center",cursor:"pointer",transition:"background 0.1s" }}
                  onMouseEnter={e=>{ if(!isExpanded) e.currentTarget.style.background="var(--bg-off)"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=isExpanded?AC.purpleBg:"var(--bg-off)"; }}>
                  <div style={{ fontSize:11,fontWeight:600,color:isToday?AC.purple:isExpanded?AC.purple:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em" }}>
                    {d.toLocaleDateString("en-US",{weekday:"short"})}
                  </div>
                  <div style={{ fontSize:22,fontWeight:700,color:isToday?"#fff":isExpanded?AC.purple:"var(--text-primary)",background:isToday?AC.purple:"transparent",width:36,height:36,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"2px auto" }}>
                    {d.getDate()}
                  </div>
                  {/* Task count badge */}
                  {dayTasks.length>0 && (
                    <div style={{ fontSize:10,fontWeight:700,color:isExpanded?AC.purple:"var(--text-muted)",marginTop:2 }}>
                      {dayTasks.length} task{dayTasks.length!==1?"s":""}
                      <span style={{ marginLeft:4 }}>{isExpanded?"▲":"▼"}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Expanded day task panel */}
          {expandedDay && (()=>{
            const [ey,em,ed] = expandedDay.split("-").map(Number);
            const date = new Date(ey, em-1, ed);
            const dayTasks = tasksOnDate(date);
            const dayMeetings = meetingsOnDate(date);
            if (dayTasks.length===0 && dayMeetings.length===0) return null;
            return (
              <div style={{ borderBottom:"1px solid var(--border)",background:AC.purpleBg,padding:"12px 16px 14px" }}>
                <div style={{ fontSize:11,fontWeight:700,color:AC.purple,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:10 }}>
                  {date.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} — {dayTasks.length} task{dayTasks.length!==1?"s":""}{dayMeetings.length>0?` · ${dayMeetings.length} meeting${dayMeetings.length!==1?"s":""}`:""} 
                </div>
                <div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>
                  {dayMeetings.map(m=>(
                    <div key={m.id} onClick={()=>setSelectedMeeting(m)}
                      style={{ background:m.color||"#0065FF",borderRadius:6,padding:"8px 12px",cursor:"pointer",minWidth:140,maxWidth:220,flexShrink:0 }}>
                      <div style={{ fontSize:12,fontWeight:700,color:"#fff" }}>🗓 {m.title}</div>
                      <div style={{ fontSize:11,color:"rgba(255,255,255,0.8)",marginTop:2 }}>{m.startTime} – {m.endTime}</div>
                      {m.notes && <div style={{ fontSize:10,color:"rgba(255,255,255,0.65)",marginTop:2,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis" }}>{m.notes}</div>}
                    </div>
                  ))}
                  {dayTasks.map(t=>{
                    const pm=PRIORITY_META[t.priority];
                    const sm=STATUS_META[t.status];
                    const team=getTeam(t.team);
                    return (
                      <div key={t.id} onClick={()=>onSelectTask(t)}
                        style={{ background:"var(--bg)",border:`1px solid var(--border)`,borderLeft:`4px solid ${pm.color}`,borderRadius:6,padding:"8px 12px",cursor:"pointer",minWidth:160,maxWidth:240,flexShrink:0,transition:"all 0.1s" }}
                        onMouseEnter={e=>{e.currentTarget.style.boxShadow=`0 2px 8px ${pm.color}33`;}}
                        onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";}}>
                        <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)",marginBottom:5,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical" }}>{t.title}</div>
                        <div style={{ display:"flex",gap:5,flexWrap:"wrap" }}>
                          <span style={{ fontSize:10,fontWeight:700,color:pm.color,background:pm.bg,borderRadius:3,padding:"1px 6px",textTransform:"uppercase" }}>{t.priority}</span>
                          <span style={{ fontSize:10,fontWeight:600,color:sm.color,background:sm.bg,borderRadius:3,padding:"1px 6px" }}>{t.status}</span>
                          {team && <span style={{ fontSize:10,color:team.color,fontWeight:600 }}>{team.name}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Time grid — scrollable */}
          <div ref={timeGridRef} style={{ overflowY:"auto",maxHeight:"calc(100vh - 340px)",position:"relative" }}>
            {/* Current time indicator */}
            {(()=>{
              const now = new Date();
              const ROW_H = 56;
              const px = (now.getHours() + now.getMinutes()/60) * ROW_H;
              const dayIdx = weekDays.findIndex(d=>d.getTime()===today.getTime());
              if (dayIdx < 0) return null;
              // Position over today's column only: 56px gutter + dayIdx * (1/7 of remaining width)
              const colW = `calc((100% - 56px) / 7)`;
              const colLeft = `calc(56px + ${dayIdx} * (100% - 56px) / 7)`;
              return (
                <div style={{ position:"absolute", top:`${px}px`, left:colLeft, width:colW, zIndex:10, pointerEvents:"none" }}>
                  <div style={{ height:2, background:"#DE350B", position:"relative", display:"flex", alignItems:"center" }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:"#DE350B", position:"absolute", left:0, top:-4, flexShrink:0 }}/>
                  </div>
                </div>
              );
            })()}

            {HOURS.map(hour => (
              <div key={hour} style={{ display:"grid",gridTemplateColumns:"56px repeat(7,1fr)",minHeight:56,borderBottom:"1px solid var(--border)" }}>
                {/* Hour label */}
                <div style={{ padding:"2px 8px",fontSize:10,color:"var(--text-muted)",fontWeight:600,fontFamily:"monospace",borderRight:"1px solid var(--border)",background:"var(--bg-off)",flexShrink:0,userSelect:"none" }}>
                  {hour===0?"":hour<12?`${hour} AM`:hour===12?"12 PM":`${hour-12} PM`}
                </div>
                {/* Day columns */}
                {weekDays.map((d,di) => {
                  const isToday = d.getTime()===today.getTime();
                  const dayMeetings = meetingsOnDate(d).filter(m=>{
                    const mh = parseInt((m.startTime||"00:00").split(":")[0]);
                    return mh===hour;
                  });
                  return (
                    <div key={di}
                      onClick={()=>openNewMeeting(d,hour)}
                      style={{ borderLeft:"1px solid var(--border)",padding:"2px 3px",background:isToday?"rgba(161,0,255,0.02)":"transparent",cursor:"pointer",position:"relative",minHeight:56,transition:"background 0.1s",overflow:"hidden",boxSizing:"border-box" }}
                      onMouseEnter={e=>e.currentTarget.style.background=isToday?"rgba(161,0,255,0.05)":"var(--bg-off)"}
                      onMouseLeave={e=>e.currentTarget.style.background=isToday?"rgba(161,0,255,0.02)":"transparent"}>
                      {dayMeetings.map(m=>{
                        const [sh,sm2]=( m.startTime||"00:00").split(":").map(Number);
                        const [eh,em2]=(m.endTime||"01:00").split(":").map(Number);
                        const duration = Math.max(0.5, (eh*60+em2-sh*60-sm2)/60);
                        const cardH = Math.max(1, duration) * 56 - 6;
                        return (
                          <div key={m.id}
                            onClick={e=>{e.stopPropagation();setSelectedMeeting(m);}}
                            style={{ background:m.color||"#0065FF",borderRadius:4,padding:"3px 7px",marginBottom:2,cursor:"pointer",position:"relative",zIndex:1,overflow:"hidden",height:`${cardH}px`,width:"100%",boxSizing:"border-box",display:"flex",flexDirection:"column",justifyContent:"flex-start" }}
                            title={`${m.title} (${m.startTime}–${m.endTime})`}>
                            <div style={{ fontSize:11,fontWeight:700,color:"#fff",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",width:"100%",flexShrink:0 }}>{m.title}</div>
                            {cardH >= 32 && <div style={{ fontSize:10,color:"rgba(255,255,255,0.75)",whiteSpace:"nowrap",overflow:"hidden",flexShrink:0 }}>{m.startTime} – {m.endTime}</div>}
                            {m.notes && cardH >= 52 && <div style={{ fontSize:10,color:"rgba(255,255,255,0.6)",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",width:"100%",flexShrink:0 }}>{m.notes}</div>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MONTH VIEW ── */}
      {calView==="month" && (
        <div style={{ background:"var(--bg)",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden" }}>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid var(--border)" }}>
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d,i)=>(
              <div key={d} style={{ textAlign:"center",fontSize:11,fontWeight:700,color:i>=5?AC.purple:"var(--text-muted)",padding:"10px 0",letterSpacing:"0.06em",textTransform:"uppercase",borderRight:i<6?`1px solid var(--border)`:"none",background:"var(--bg-off)" }}>{d}</div>
            ))}
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)" }}>
            {calCells.map((day,i) => {
              const isLastInRow=(i+1)%7===0;
              const totalRows=Math.ceil(calCells.length/7);
              const isLastRow=Math.floor(i/7)===totalRows-1;
              const isWeekend=i%7>=5;
              if (!day) return <div key={i} style={{ minHeight:110,background:"var(--bg-off)",borderRight:!isLastInRow?`1px solid var(--border)`:"none",borderBottom:!isLastRow?`1px solid var(--border)`:"none" }}/>;
              const dayTasks=tasksOnDay(calMonth.y,calMonth.m,day);
              const dayDate=new Date(calMonth.y,calMonth.m,day);
              const dayMeetings=meetingsOnDate(dayDate);
              const isSelected=calSelected===day;
              const isToday=day===today.getDate()&&calMonth.m===today.getMonth()&&calMonth.y===today.getFullYear();
              return (
                <div key={i} onClick={()=>setCalSelected(isSelected?null:day)}
                  style={{ minHeight:110,padding:"6px 8px",cursor:"pointer",background:isSelected?AC.purpleBg:isToday?"rgba(161,0,255,0.04)":isWeekend?"var(--bg-off)":"var(--bg)",borderRight:!isLastInRow?`1px solid var(--border)`:"none",borderBottom:!isLastRow?`1px solid var(--border)`:"none",boxSizing:"border-box",overflow:"hidden" }}
                  onMouseEnter={e=>{ if(!isSelected&&!isToday) e.currentTarget.style.background="var(--bg-off)"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=isSelected?AC.purpleBg:isToday?"rgba(161,0,255,0.04)":isWeekend?"var(--bg-off)":"var(--bg)"; }}>
                  <div style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",width:22,height:22,borderRadius:"50%",marginBottom:3,background:isToday?AC.purple:isSelected?AC.purple:"transparent",color:isToday||isSelected?"#fff":isWeekend?AC.purple:"var(--text-second)",fontSize:11,fontWeight:isToday||isSelected?700:500 }}>{day}</div>
                  <div style={{ display:"flex",flexDirection:"column",gap:2 }}>
                    {dayMeetings.slice(0,1).map(m=>(
                      <div key={m.id} onClick={e=>{e.stopPropagation();setSelectedMeeting(m);}}
                        style={{ fontSize:9,fontWeight:600,color:"#fff",background:m.color||"#0065FF",borderRadius:2,padding:"1px 5px",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",cursor:"pointer" }}>
                        🗓 {m.title}
                      </div>
                    ))}
                    {dayTasks.slice(0,dayMeetings.length>0?1:2).map(t=>{
                      const pm=PRIORITY_META[t.priority];
                      return (
                        <div key={t.id} onClick={e=>{e.stopPropagation();onSelectTask(t);}} title={t.title}
                          style={{ fontSize:9,fontWeight:600,color:pm.color,background:pm.bg,borderLeft:`2px solid ${pm.color}`,borderRadius:2,padding:"1px 4px",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",cursor:"pointer" }}>
                          {t.title}
                        </div>
                      );
                    })}
                    {(dayTasks.length+dayMeetings.length)>2 && <div style={{ fontSize:9,fontWeight:700,color:"var(--text-muted)" }}>+{dayTasks.length+dayMeetings.length-2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Selected day expansion panel - month view */}
          {calSelected && (()=>{
            const dayTasks = tasksOnDay(calMonth.y, calMonth.m, calSelected);
            const dayDate  = new Date(calMonth.y, calMonth.m, calSelected);
            const dayMeetings = meetingsOnDate(dayDate);
            if (dayTasks.length===0 && dayMeetings.length===0) return (
              <div style={{ borderTop:"1px solid var(--border)",padding:"14px 20px",background:"var(--bg-off)",color:"var(--text-muted)",fontSize:13,textAlign:"center" }}>
                No tasks or events on {dayDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
              </div>
            );
            return (
              <div style={{ borderTop:`2px solid ${AC.purple}`,background:AC.purpleBg,padding:"14px 20px" }}>
                <div style={{ fontSize:12,fontWeight:700,color:AC.purple,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:12 }}>
                  {dayDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
                  {dayTasks.length>0 && ` — ${dayTasks.length} task${dayTasks.length!==1?"s":""}`}
                  {dayMeetings.length>0 && ` · ${dayMeetings.length} meeting${dayMeetings.length!==1?"s":""}`}
                </div>
                <div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>
                  {dayMeetings.map(m=>(
                    <div key={m.id} onClick={()=>setSelectedMeeting(m)}
                      style={{ background:m.color||"#0065FF",borderRadius:6,padding:"8px 12px",cursor:"pointer",minWidth:140 }}>
                      <div style={{ fontSize:12,fontWeight:700,color:"#fff" }}>🗓 {m.title}</div>
                      <div style={{ fontSize:11,color:"rgba(255,255,255,0.8)",marginTop:2 }}>{m.startTime} – {m.endTime}</div>
                      {m.notes && <div style={{ fontSize:10,color:"rgba(255,255,255,0.65)",marginTop:2 }}>{m.notes}</div>}
                    </div>
                  ))}
                  {dayTasks.map(t=>{
                    const pm=PRIORITY_META[t.priority], sm=STATUS_META[t.status], team=getTeam(t.team);
                    return (
                      <div key={t.id} onClick={()=>onSelectTask(t)}
                        style={{ background:"var(--bg)",border:"1px solid var(--border)",borderLeft:`4px solid ${pm.color}`,borderRadius:6,padding:"8px 12px",cursor:"pointer",minWidth:160,maxWidth:220,transition:"all 0.1s" }}
                        onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 2px 10px ${pm.color}33`}
                        onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                        <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)",marginBottom:5 }}>{t.title}</div>
                        <div style={{ display:"flex",gap:5,flexWrap:"wrap" }}>
                          <span style={{ fontSize:10,fontWeight:700,color:pm.color,background:pm.bg,borderRadius:3,padding:"1px 6px",textTransform:"uppercase" }}>{t.priority}</span>
                          <span style={{ fontSize:10,fontWeight:600,color:sm.color,background:sm.bg,borderRadius:3,padding:"1px 6px" }}>{t.status}</span>
                          {team && <span style={{ fontSize:10,color:team.color,fontWeight:600 }}>{team.name}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function BoardView({ tasks, getTeam, onEdit, onDelete, onStatusChange, onReassign, onAddComment, onDeleteComment, onSelect, currentUser, teams }) {
  const PRIORITY_ORDER = { Critical:0, High:1, Medium:2, Low:3 };
  const [dragOverCol, setDragOverCol] = useState(null); // which column is being hovered
  const [draggingId,  setDraggingId]  = useState(null); // id of card being dragged

  function sortCol(list) {
    return [...list].sort((a,b) => {
      const pd = (PRIORITY_ORDER[a.priority]??99) - (PRIORITY_ORDER[b.priority]??99);
      if (pd !== 0) return pd;
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
    });
  }

  function handleDrop(e, status) {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("taskId");
    if (taskId) onStatusChange(taskId, status);
    setDragOverCol(null);
    setDraggingId(null);
  }

  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16 }}>
      {STATUSES.map(status => {
        const col = sortCol(tasks.filter(t=>t.status===status));
        const m = STATUS_META[status];
        const isOver = dragOverCol === status;
        return (
          <div key={status}
            onDragOver={e=>{ e.preventDefault(); setDragOverCol(status); }}
            onDragLeave={e=>{ if(!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
            onDrop={e=>handleDrop(e, status)}
            style={{ transition:"all 0.15s" }}>
            {/* Column header */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12, paddingBottom:10, borderBottom:`2px solid ${isOver ? m.color : m.bar}`, transition:"border-color 0.15s" }}>
              <span style={{ fontSize:12, fontWeight:700, color:m.color, textTransform:"uppercase", letterSpacing:"0.08em" }}>{status}</span>
              <span style={{ fontSize:11, color:m.color, background:m.bg, border:`1px solid ${m.border}`, borderRadius:10, padding:"1px 7px", fontWeight:600 }}>{col.length}</span>
              {isOver && draggingId && <span style={{ fontSize:10, color:m.color, fontWeight:600, marginLeft:"auto", opacity:0.8 }}>Drop here</span>}
            </div>
            {/* Drop zone */}
            <div style={{
              display:"flex", flexDirection:"column", gap:10,
              minHeight:80, borderRadius:8, padding: isOver ? "6px" : "0",
              background: isOver ? m.bg+"88" : "transparent",
              border: isOver ? `2px dashed ${m.color}` : "2px dashed transparent",
              transition:"all 0.15s"
            }}>
              {col.map(t=>(
                <TaskCard key={t.id} task={t} team={getTeam(t.team)}
                  onEdit={onEdit} onDelete={onDelete} onStatusChange={onStatusChange}
                  onReassign={onReassign} onAddComment={onAddComment} onDeleteComment={onDeleteComment} onSelect={onSelect}
                  currentUser={currentUser}
                  onDragStart={id=>setDraggingId(id)}
                  onDragEnd={()=>{ setDraggingId(null); setDragOverCol(null); }}
                  isDragging={draggingId===t.id}
                  teams={teams}/>
              ))}
              {/* Empty column drop hint */}
              {col.length===0 && !isOver && (
                <div style={{ textAlign:"center", padding:"20px 0", color:m.color, fontSize:11, opacity:0.4, fontWeight:500 }}>
                  Drop tasks here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TaskCard({ task, team, onEdit, onDelete, onStatusChange, onReassign, onAddComment, onDeleteComment, onSelect, currentUser, onDragStart, onDragEnd, isDragging, teams }) {
  const pm = PRIORITY_META[task.priority];
  const tc = team?.color || AC.textMuted;
  const [hov, setHov]                   = useState(false);
  const [showComments, setShowComments] = useState(false);
  const comments = task.comments || [];

  return (
    <div
      draggable
      onDragStart={e=>{ e.dataTransfer.setData("taskId", task.id); e.dataTransfer.effectAllowed="move"; onDragStart&&onDragStart(task.id); }}
      onDragEnd={()=>onDragEnd&&onDragEnd()}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:"var(--bg)", border:`1px solid ${hov?tc:"var(--border)"}`, borderRadius:6, padding:"14px 16px", transition:"all 0.15s", boxShadow:hov?"0 4px 16px rgba(26,5,51,0.10)":"0 1px 3px rgba(26,5,51,0.04)", borderLeft:`3px solid ${tc}`, opacity:isDragging?0.4:1, cursor:"grab", userSelect:"none" }}>
      {/* Clickable card body — opens detail modal */}
      <div onClick={()=>onSelect&&onSelect(task)} style={{ cursor:"pointer" }}>
        {/* Priority + Team */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"2px 8px", letterSpacing:"0.06em", textTransform:"uppercase" }}>{task.priority}</span>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {hov && <span style={{ fontSize:10, color:"var(--text-muted)", fontStyle:"italic" }}>drag to move · click to open</span>}
            {team && <span style={{ fontSize:11, color:tc, fontWeight:600 }}>{team.name}</span>}
          </div>
        </div>
        {/* Title */}
        <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:6, lineHeight:1.45 }}>{task.title}</div>
        {/* Description */}
        {task.description && <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:8, lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{task.description}</div>}
        {/* Links */}
        {task.links?.length>0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:10 }} onClick={e=>e.stopPropagation()}>
            {task.links.map((url,i)=><LinkChip key={i} url={url}/>)}
          </div>
        )}
      </div>
      {/* Footer */}
      <div onClick={e=>e.stopPropagation()} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:10, borderTop:"1px solid var(--border)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {(()=>{
            const assignees = getAssignees(task);
            if (assignees.length===0) return <span style={{ fontSize:11, color:"var(--text-muted)" }}>Unassigned</span>;
            return (
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ display:"flex" }}>
                  {assignees.slice(0,3).map((a,i)=>(
                    <div key={a} title={a} style={{ marginLeft:i===0?0:-6, zIndex:3-i, position:"relative" }}>
                      <Avatar name={a} size={20}/>
                    </div>
                  ))}
                </div>
                <span style={{ fontSize:11, color:"var(--text-second)", fontWeight:500 }}>
                  {assignees.length===1 ? assignees[0].split(" ")[0] : `${assignees[0].split(" ")[0]} +${assignees.length-1}`}
                </span>
              </div>
            );
          })()}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <button onClick={e=>{e.stopPropagation();setShowComments(v=>!v);}}
            style={{ background:showComments?AC.purpleBg:"transparent", border:`1px solid ${showComments?AC.purple:"transparent"}`, borderRadius:4, color:showComments?AC.purple:AC.textMuted, cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", gap:3, padding:"2px 6px", transition:"all 0.1s" }}>
            💬 {comments.length>0 ? comments.length : ""}
          </button>
          <DeadlineBadge date={task.dueDate}/>
        </div>
      </div>
      {/* Hover actions */}
      {hov && (
        <div onClick={e=>e.stopPropagation()} style={{ display:"flex", gap:6, marginTop:10 }}>
          <select value={task.status} onChange={e=>onStatusChange(task.id,e.target.value)}
            style={{ flex:1, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", fontSize:11, padding:"5px 6px", cursor:"pointer", fontFamily:"inherit" }}>
            {STATUSES.map(s=><option key={s}>{s}</option>)}
          </select>
          <button onClick={()=>onReassign(task)} title="Reassign" style={{ background:AC.infoBg, border:`1px solid ${AC.info}33`, borderRadius:4, color:AC.info, padding:"5px 8px", cursor:"pointer", fontSize:11, fontWeight:600 }}>⇄</button>
          <button onClick={()=>onEdit(task)} style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"5px 8px", cursor:"pointer", fontSize:11 }}>✎</button>
          <button onClick={()=>onDelete(task.id)} style={{ background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, color:AC.danger, padding:"5px 8px", cursor:"pointer", fontSize:11 }}>✕</button>
        </div>
      )}
      {/* Comments */}
      {showComments && (
        <CommentPanel task={task} currentUser={currentUser} onDeleteComment={onDeleteComment} onAddComment={(taskId, text, tag)=>{
          onAddComment && onAddComment(taskId, text, tag);
        }} teams={teams}/>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LIST VIEW
══════════════════════════════════════════════════════════════ */
function ListView({ tasks, getTeam, onEdit, onDelete, onStatusChange, onReassign, onSelect }) {
  const PRIORITY_ORDER = { Critical:0, High:1, Medium:2, Low:3 };

  // Sort: urgency first (overdue → due today → due in 1-2d → due this week → later → no date)
  // then by priority within each urgency tier
  const sorted = [...tasks].sort((a, b) => {
    const dA = a.dueDate ? daysUntil(a.dueDate) : 999;
    const dB = b.dueDate ? daysUntil(b.dueDate) : 999;

    function urgencyTier(days, status) {
      if (status === "Done") return 10;
      if (days === null || days === 999) return 6;
      if (days < 0)  return 0; // overdue
      if (days === 0) return 1; // due today
      if (days <= 2)  return 2; // 1-2 days
      if (days <= 7)  return 3; // this week
      if (days <= 14) return 4; // next 2 weeks
      return 5;
    }

    const tA = urgencyTier(dA, a.status);
    const tB = urgencyTier(dB, b.status);
    if (tA !== tB) return tA - tB;

    // Within same tier: sort by priority
    const pA = PRIORITY_ORDER[a.priority] ?? 99;
    const pB = PRIORITY_ORDER[b.priority] ?? 99;
    if (pA !== pB) return pA - pB;

    // Within same priority: earliest due date first
    if (dA !== dB) return dA - dB;
    return 0;
  });

  return (
    <div style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:6, overflow:"hidden" }}>
      {/* Header row */}
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 120px 90px", gap:8, padding:"10px 16px", background:"var(--bg-off)", borderBottom:"1px solid var(--border)", fontSize:11, color:"var(--text-muted)", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase" }}>
        <span>Task</span><span>Team</span><span>Assignee</span><span>Priority</span><span>Status</span><span>Due</span><span/>
      </div>
      {sorted.map((t,i) => {
        const team=getTeam(t.team), pm=PRIORITY_META[t.priority], sm=STATUS_META[t.status], tc=team?.color||AC.textMuted;
        return (
          <div key={t.id}
            style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 120px 90px", gap:8, padding:"12px 16px", borderBottom:i<sorted.length-1?`1px solid ${AC.border}`:"none", alignItems:"center", transition:"background 0.1s", cursor:"pointer" }}
            onClick={()=>onSelect&&onSelect(t)}
            onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", marginBottom:2, display:"flex", alignItems:"center", gap:6 }}>
                {t.title}
                {(t.comments||[]).length>0 && <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:400 }}>💬 {(t.comments||[]).length}</span>}
              </div>
              {t.description && <div style={{ fontSize:11, color:"var(--text-muted)" }}>{t.description.slice(0,60)}{t.description.length>60?"…":""}</div>}
              {t.links?.length>0 && <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:4 }} onClick={e=>e.stopPropagation()}>{t.links.map((url,i)=><LinkChip key={i} url={url}/>)}</div>}
            </div>
            {team
              ? <span style={{ fontSize:11, color:tc, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}><span style={{ width:7, height:7, borderRadius:"50%", background:tc, display:"block" }}/>{team.name}</span>
              : <span style={{ fontSize:11, color:"var(--text-muted)" }}>—</span>}
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              {(()=>{
                const assignees = getAssignees(t);
                if (!assignees.length) return <span style={{ fontSize:11, color:"var(--text-muted)" }}>—</span>;
                return <>
                  <div style={{ display:"flex" }}>
                    {assignees.slice(0,3).map((a,i)=>(
                      <div key={a} title={a} style={{ marginLeft:i===0?0:-6, position:"relative", zIndex:3-i }}>
                        <Avatar name={a} size={20}/>
                      </div>
                    ))}
                  </div>
                  <span style={{ fontSize:11, color:"var(--text-second)", fontWeight:500 }}>
                    {assignees.length===1 ? assignees[0].split(" ")[0] : `${assignees[0].split(" ")[0]} +${assignees.length-1}`}
                  </span>
                </>;
              })()}
            </div>
            <span style={{ fontSize:11, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"3px 8px", display:"inline-block", textTransform:"uppercase", letterSpacing:"0.04em" }}>{t.priority}</span>
            <select value={t.status} onClick={e=>e.stopPropagation()} onChange={e=>{e.stopPropagation();onStatusChange(t.id,e.target.value);}}
              style={{ background:sm.bg, border:`1.5px solid ${sm.border}`, borderRadius:4, color:sm.color, fontSize:11, fontWeight:600, padding:"4px 8px", cursor:"pointer", fontFamily:"inherit" }}>
              {STATUSES.map(s=><option key={s} style={{ background:"var(--bg)", color:"var(--text-primary)" }}>{s}</option>)}
            </select>
            <DeadlineBadge date={t.dueDate}/>
            <div style={{ display:"flex", gap:4 }} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>onReassign(t)} title="Reassign" style={{ background:AC.infoBg, border:`1px solid ${AC.info}33`, borderRadius:4, color:AC.info, padding:"5px 7px", cursor:"pointer", fontSize:11, fontWeight:600 }}>⇄</button>
              <button onClick={()=>onEdit(t)} style={{ background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-second)", padding:"5px 7px", cursor:"pointer", fontSize:11 }}>✎</button>
              <button onClick={()=>onDelete(t.id)} style={{ background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, color:AC.danger, padding:"5px 7px", cursor:"pointer", fontSize:11 }}>✕</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   AI HELPERS  — calls Claude API via Anthropic
══════════════════════════════════════════════════════════════ */
const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_KEY || "";

async function callClaude(prompt) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": ANTHROPIC_KEY,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 400,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  const data = await res.json();
  return data.content[0].text.trim();
}

/* ═══════════════════════════════════════════════════════════
   TASK MODAL
══════════════════════════════════════════════════════════════ */
function TaskModal({ task, teams, onSave, onClose, defaultAssignee }) {

  const [form,setForm] = useState({
    title:task?.title||"", description:task?.description||"",
    team:task?.team||(teams[0]?.id||"personal"),
    assignees: getAssignees(task).length ? getAssignees(task) : (defaultAssignee ? [defaultAssignee] : []),
    priority:task?.priority||"Medium", status:task?.status||"To Do", dueDate:task?.dueDate||"", links:task?.links||[]
  });
  const [linkInput,setLinkInput]=useState(""); const [linkError,setLinkError]=useState("");

  const [aiLoading,setAiLoading]=useState(""); // "desc" | "priority" | "breakdown"
  const [breakdown, setBreakdown]=useState([]); // AI subtask suggestions

  async function generateBreakdown() {
    if (!form.title.trim()) return;
    setAiLoading("breakdown");
    setBreakdown([]);
    try {
      const result = await callClaude(
        `You are a work management assistant. Break down this task into 4-6 clear subtasks.\n\nTask: "${form.title}"\nDescription: "${form.description||"(none)"}"\n\nFor each subtask reply with one line in this exact format:\nSUBTASK: [title] | PRIORITY: [Low/Medium/High/Critical]\n\nReply with only the subtasks, one per line, no extra text.`
      );
      const lines = result.split("\n").filter(l=>l.includes("SUBTASK:"));
      const parsed = lines.map(l=>{
        const titleMatch = l.match(/SUBTASK:\s*(.+?)\s*\|/);
        const prioMatch  = l.match(/PRIORITY:\s*(\w+)/);
        return { title:(titleMatch?.[1]||l).trim(), priority:prioMatch?.[1]||"Medium" };
      });
      setBreakdown(parsed);
    } catch(e) { alert("AI error: " + e.message); }
    finally { setAiLoading(""); }
  }

  async function generateDescription() {
    if (!form.title.trim()) return;
    setAiLoading("desc");
    try {
      const result = await callClaude(
        `You are a work management assistant. Write a clear, concise task description (2-3 sentences) for a task titled: "${form.title}". Include what needs to be done, why it matters, and any key acceptance criteria. Be specific and actionable. Reply with only the description text, no labels or preamble.`
      );
      setForm(f=>({...f, description:result}));
    } catch(e) { alert("AI error: " + e.message); }
    finally { setAiLoading(""); }
  }

  async function suggestAssignee() {
    if (!form.title.trim()) return;
    setAiLoading("assignee");
    try {
      const teamMembers = (teams.find(t=>t.id===form.team)?.members)||[];
      if (!teamMembers.length) return;
      const memberInfo = teamMembers.map(m => {
        const statusMeta = MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
        return `${m.name} (${m.role}, ${statusMeta.label})`;
      }).join("; ");
      const result = await callClaude(
        `You are a work management assistant. Suggest the best person to assign this task based on their role and availability.\n\nTask: "${form.title}"\nDescription: "${form.description||"none"}"\n\nTeam members: ${memberInfo}\n\nReply with ONLY the full name of the best person, nothing else.`
      );
      const suggested = teamMembers.find(m =>
        result.toLowerCase().includes(m.name.toLowerCase()) ||
        m.name.toLowerCase().includes(result.trim().toLowerCase())
      );
      if (suggested && !form.assignees.includes(suggested.name)) {
        setForm(f=>({...f, assignees:[...f.assignees, suggested.name]}));
      }
    } catch(e) { alert("AI error: " + e.message); }
    finally { setAiLoading(""); }
  }


  function addLink() {
    const raw=linkInput.trim(); if(!raw) return;
    const url=raw.startsWith("http")?raw:"https://"+raw;
    try{new URL(url);}catch{setLinkError("Enter a valid URL");return;}
    if(form.links.includes(url)){setLinkError("Already added");return;}
    setForm(f=>({...f,links:[...f.links,url]})); setLinkInput(""); setLinkError("");
  }

  const selectedTeam = teams.find(t=>t.id===form.team);

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(26,5,51,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(3px)" }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--bg)",borderRadius:6,width:540,maxWidth:"96vw",boxShadow:"0 24px 60px rgba(26,5,51,0.25)",maxHeight:"92vh",overflowY:"auto",display:"flex",flexDirection:"column" }}>
        {/* Modal header */}
        <div style={{ background:AC.navy,padding:"18px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:"6px 6px 0 0" }}>
          <div>
            <div style={{ color:"#fff",fontSize:16,fontWeight:700,letterSpacing:"-0.01em" }}>{task?"Edit Task":"Create New Task"}</div>
            <div style={{ color:"rgba(255,255,255,0.5)",fontSize:11,marginTop:2 }}>Fill in the details below to {task?"update":"create"} a task</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.1)",border:"none",borderRadius:4,color:"rgba(255,255,255,0.7)",width:28,height:28,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding:"24px",display:"flex",flexDirection:"column",gap:16 }}>
          <div><label style={LBL}>Title *</label><input style={INP} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="What needs to be done?"/></div>
          <div>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5 }}>
              <label style={{...LBL,marginBottom:0}}>Description</label>
              <button onClick={generateDescription} disabled={!form.title.trim()||aiLoading==="desc"}
                style={{ background:aiLoading==="desc"?"#eee":AC.purpleBg, border:`1px solid ${AC.purple}`, borderRadius:4, color:AC.purple, padding:"3px 10px", cursor:form.title.trim()&&aiLoading!=="desc"?"pointer":"default", fontSize:11, fontWeight:600, display:"flex",alignItems:"center",gap:4, opacity:form.title.trim()?1:0.4 }}>
                {aiLoading==="desc" ? "⏳ Generating…" : "✨ AI Generate"}
              </button>
            </div>
            <textarea style={{...INP,resize:"vertical",minHeight:72}} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Write a description or click ✨ AI Generate to let Claude write it from your title…"/>
          </div>

          {/* AI Task Breakdown */}
          <div>
            <button onClick={generateBreakdown} disabled={!form.title.trim()||aiLoading==="breakdown"}
              style={{ background:aiLoading==="breakdown"?"#eee":AC.purpleBg, border:`1px solid ${AC.purple}`, borderRadius:4, color:AC.purple, padding:"5px 14px", cursor:form.title.trim()&&aiLoading!=="breakdown"?"pointer":"default", fontSize:11, fontWeight:600, display:"flex",alignItems:"center",gap:6, opacity:form.title.trim()?1:0.4 }}>
              {aiLoading==="breakdown" ? "⏳ Breaking down…" : "🔀 AI Break into subtasks"}
            </button>
            {breakdown.length>0 && (
              <div style={{ marginTop:10, background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:6, padding:"10px 12px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:AC.purple, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>✨ Suggested subtasks — click to use as title</div>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {breakdown.map((b,i)=>{
                    const pm=PRIORITY_META[b.priority]||PRIORITY_META.Medium;
                    return (
                      <div key={i} onClick={()=>{ setForm(f=>({...f,title:b.title,priority:b.priority})); setBreakdown([]); }}
                        style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:4, cursor:"pointer", border:"1px solid var(--border)", background:"var(--bg)", transition:"all 0.1s" }}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=AC.purple}
                        onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                        <span style={{ fontSize:10, fontWeight:700, color:pm.color, background:pm.bg, border:`1px solid ${pm.border}`, borderRadius:3, padding:"1px 6px", textTransform:"uppercase", flexShrink:0 }}>{b.priority}</span>
                        <span style={{ fontSize:12, color:"var(--text-primary)", flex:1 }}>{b.title}</span>
                        <span style={{ fontSize:10, color:AC.purple }}>use →</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Team */}
          <div>
            <label style={LBL}>Team</label>
            <select style={INP} value={form.team} onChange={e=>setForm(f=>({...f,team:e.target.value,assignees:e.target.value==="personal"?[defaultAssignee]:f.assignees}))}>
              <option value="personal">👤 Personal (just me)</option>
              {teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {form.team==="personal"
              ? <div style={{ marginTop:6,display:"flex",alignItems:"center",gap:6 }}><span style={{ fontSize:11,color:"var(--text-muted)",fontStyle:"italic" }}>Only visible to you</span></div>
              : selectedTeam && <div style={{ marginTop:6,display:"flex",alignItems:"center",gap:6 }}><span style={{ width:8,height:8,borderRadius:"50%",background:selectedTeam.color,display:"block" }}/><span style={{ fontSize:11,color:"var(--text-second)" }}>{(selectedTeam.members||[]).length} members</span></div>
            }
          </div>

          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
                <label style={{...LBL, marginBottom:0}}>Assignees</label>
                <button onClick={suggestAssignee} disabled={!form.title.trim()||aiLoading==="assignee"||form.team==="personal"||!(teams.find(t=>t.id===form.team)?.members||[]).length}
                  style={{ background:aiLoading==="assignee"?"#eee":AC.purpleBg, border:`1px solid ${AC.purple}`, borderRadius:4, color:AC.purple, padding:"2px 8px", cursor:"pointer", fontSize:10, fontWeight:600, opacity:form.title.trim()?1:0.4 }}>
                  {aiLoading==="assignee" ? "⏳…" : "✨ AI Suggest"}
                </button>
              </div>
              {(()=>{
                if (form.team === "personal") return (
                  <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", background:"var(--bg-off)", border:"1px solid var(--border)", borderRadius:4 }}>
                    <Avatar name={defaultAssignee} size={22}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{defaultAssignee}</div>
                      <div style={{ fontSize:10, color:"var(--text-muted)" }}>Assigned to you</div>
                    </div>
                  </div>
                );
                const teamMembers = (teams.find(t=>t.id===form.team)?.members)||[];
                if (teamMembers.length===0) return (
                  <div style={{ ...INP, color:"var(--text-muted)", display:"flex", alignItems:"center" }}>
                    No members in this team
                  </div>
                );
                return (
                  <>
                    {/* Selected assignees chips */}
                    {form.assignees.length>0 && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:6 }}>
                        {form.assignees.map(a=>(
                          <div key={a} style={{ display:"flex", alignItems:"center", gap:4, background:AC.purpleBg, border:`1px solid ${AC.purpleDim}`, borderRadius:20, padding:"3px 8px 3px 6px" }}>
                            <Avatar name={a} size={16}/>
                            <span style={{ fontSize:11, fontWeight:600, color:AC.purple }}>{a.split(" ")[0]}</span>
                            <button onClick={()=>setForm(f=>({...f,assignees:f.assignees.filter(x=>x!==a)}))}
                              style={{ background:"none", border:"none", color:AC.purple, cursor:"pointer", fontSize:11, padding:0, lineHeight:1, marginLeft:2 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Dropdown of remaining members */}
                    <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:160, overflowY:"auto", border:"1px solid var(--border)", borderRadius:4, background:"var(--bg)" }}>
                      {teamMembers.filter(m=>!form.assignees.includes(m.name)).map(m=>{
                        const sm = MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
                        return (
                          <button key={m.id} onClick={()=>setForm(f=>({...f,assignees:[...f.assignees,m.name]}))}
                            style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", background:"transparent", border:"none", cursor:"pointer", textAlign:"left", transition:"background 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background=AC.offwhite}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <Avatar name={m.name} size={22}/>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{m.name}</div>
                              <div style={{ fontSize:10, color:"var(--text-muted)" }}>{m.role}</div>
                            </div>
                            <span style={{ fontSize:10, fontWeight:600, color:sm.color, background:sm.bg, border:`1px solid ${sm.border}`, borderRadius:3, padding:"1px 6px" }}>{sm.label}</span>
                          </button>
                        );
                      })}
                      {teamMembers.filter(m=>!form.assignees.includes(m.name)).length===0 && (
                        <div style={{ fontSize:12, color:"var(--text-muted)", padding:"10px", textAlign:"center" }}>All members assigned</div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div><label style={LBL}>Due Date</label><input type="date" style={{...INP,colorScheme:"light"}} value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}/></div>
            <div>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5 }}>
                <label style={{...LBL,marginBottom:0}}>Priority</label>
              </div>
              <select style={INP} value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                {PRIORITIES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div><label style={LBL}>Status</label>
              <select style={INP} value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                {STATUSES.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Links */}
          <div>
            <label style={LBL}>Reference Links</label>
            <div style={{ display:"flex",gap:8 }}>
              <input style={{...INP,flex:1}} placeholder="Paste a URL (Figma, Notion, Jira…)" value={linkInput} onChange={e=>{setLinkInput(e.target.value);setLinkError("");}} onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),addLink())}/>
              <button onClick={addLink} style={{...BTN_GHOST,padding:"0 16px",flexShrink:0}}>Add</button>
            </div>
            {linkError && <div style={{ fontSize:11,color:AC.danger,marginTop:4 }}>{linkError}</div>}
            {form.links.length>0 && <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginTop:8 }}>{form.links.map((url,i)=><LinkChip key={i} url={url} onRemove={()=>setForm(f=>({...f,links:f.links.filter(l=>l!==url)}))}/>)}</div>}
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end",background:"var(--bg-off)",borderRadius:"0 0 6px 6px" }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={()=>form.title.trim()&&onSave(form)} style={{...BTN_PRIMARY,opacity:form.title.trim()?1:0.4}}>{task?"Save Changes":"Create Task"}</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TEAM MANAGER
══════════════════════════════════════════════════════════════ */
function TeamManager({ teams, tasks, onAdd, onUpdate, onDelete, onClose, onMemberAdded, currentUser, userStatus, userShift }) {
  const [newName,setNewName]=useState(""); const [newColor,setNewColor]=useState(TEAM_PALETTE[0]);
  const [activeTeam,setActiveTeam]=useState(teams[0]?.id||null);
  const [editingName,setEditingName]=useState(false); const [editName,setEditName]=useState(""); const [editColor,setEditColor]=useState("");
  const [confirmDelete,setConfirmDelete]=useState(false); // show inline confirmation
  const [memberInput,setMemberInput]=useState(""); const [memberRole,setMemberRole]=useState("");
  const [memberShift,setMemberShift]=useState("flexible"); const [memberShiftStart,setMemberShiftStart]=useState("09:00"); const [memberShiftEnd,setMemberShiftEnd]=useState("17:00");
  const [memberDays,setMemberDays]=useState(["Mon","Tue","Wed","Thu","Fri"]); const [memberError,setMemberError]=useState("");
  const [memberMode,setMemberMode]=useState("existing"); // "existing" | "new"
  const [userSearch,setUserSearch]=useState(""); const [selectedUser,setSelectedUser]=useState(null);
   const [registeredUsers,setRegisteredUsers]=useState(null); // null=loading

  // Invite state
  const [showInvite,setShowInvite]       = useState(false);
  const [inviteEmail,setInviteEmail]     = useState("");
  const [inviteError,setInviteError]     = useState("");
  const [inviteLoading,setInviteLoading] = useState(false);
  const [inviteDone,setInviteDone]       = useState(false);

  async function handleInvite() {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true); setInviteError("");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: inviteEmail.trim(),
        options: { shouldCreateUser: true }
      });
      if (error) throw error;
      setInviteDone(true);
    } catch(e) {
      setInviteError(e.message || "Could not send invite. Check the email address.");
    }
    setInviteLoading(false);
  }

  // Fetch last_seen for all members to show "last seen X ago" tooltip
  const [lastSeenMap, setLastSeenMap] = React.useState({});
  React.useEffect(() => {
    supabase.from('profiles').select('name, last_seen')
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(p => { if (p.name && p.last_seen) map[p.name] = p.last_seen; });
        setLastSeenMap(map);
      });
    // Refresh every 2 minutes
    const interval = setInterval(() => {
      supabase.from('profiles').select('name, last_seen')
        .then(({ data }) => {
          if (!data) return;
          const map = {};
          data.forEach(p => { if (p.name && p.last_seen) map[p.name] = p.last_seen; });
          setLastSeenMap(map);
        });
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // On mount: backfill createdBy for any team that's missing it
  // The creator is whoever opened Manage Teams and is a member of that team —
  // since only members can open their own teams, first-member is the safest fallback.
  useEffect(()=>{
    const teamsNeedingOwner = teams.filter(t => !t.createdBy);
    if (!teamsNeedingOwner.length) return;
    teamsNeedingOwner.forEach(t => {
      // Use first member as creator fallback, or currentUser if they're in the team
      const isMember = (t.members||[]).some(m => m.name === currentUser?.name);
      if (!isMember) return; // don't claim teams the user isn't part of
      const firstMember = (t.members||[])[0]?.name;
      const creator = isMember ? currentUser?.name : firstMember;
      if (!creator) return;
      const patched = { ...t, createdBy: creator };
      onUpdate(patched);
    });
  }, []);

  // Fetch all registered profiles from Supabase
   // Fetch all registered profiles from Supabase
   useEffect(()=>{
     // Try fetching with optional shift/status columns; fall back to basics if those columns don't exist yet
     supabase.from('profiles').select('id, name, role, email, status, shift, shift_start, shift_end, days')
       .then(({data, error})=>{
         if (error) {
           // Fallback: columns may not exist yet — fetch basics only
           supabase.from('profiles').select('id, name, role, email')
             .then(({data: d2})=>{ setRegisteredUsers(d2||[]); });
         } else {
           setRegisteredUsers(data||[]);
         }
       });
   }, []);
  // Merge live userStatus/userShift into the current user's member record
  // so the status shown in Manage Teams is always up to date
  const liveTeams = teams.map(t => ({
    ...t,
    members: (t.members||[]).map(m => {
      if (m.name !== currentUser?.name) return m;
      return {
        ...m,
        ...(userStatus ? { status: userStatus } : {}),
        ...(userShift  ? { shift: userShift.shift, shiftStart: userShift.start, shiftEnd: userShift.end, days: userShift.days } : {}),
      };
    })
  }));
  const team=liveTeams.find(t=>t.id===activeTeam);
  const taskCount=tasks.filter(t=>t.team===activeTeam).length;

  function saveEditName() { if(editName.trim()) onUpdate({...team,name:editName.trim(),color:editColor}); setEditingName(false); }
  function addMember() {
    if (memberMode==="existing" && !selectedUser) return;
    if (memberMode==="new" && !memberInput.trim()) return;
    if((team.members||[]).some(m=>m.name.toLowerCase()===(selectedUser?.name||memberInput).trim().toLowerCase())){setMemberError("Already in this team");return;}

    const defShift=SHIFTS.find(s=>s.id===memberShift);
    const [ds,de]=(defShift?.time||"09:00 – 17:00").split(" – ");

    // Priority: 1) existing team data, 2) profile data, 3) defaults
    const existingMemberData = selectedUser ? teams.flatMap(t=>t.members||[]).find(m=>m.name===selectedUser.name) : null;
    const profileData = selectedUser;

    const base = selectedUser ? {
      id:         genId(),
      name:       selectedUser.name,
      role:       existingMemberData?.role       || profileData.role        || "Member",
      shift:      existingMemberData?.shift      || profileData.shift       || "flexible",
      shiftStart: existingMemberData?.shiftStart || profileData.shift_start || "09:00",
      shiftEnd:   existingMemberData?.shiftEnd   || profileData.shift_end   || "17:00",
      days:       existingMemberData?.days       || (Array.isArray(profileData.days) ? profileData.days : (typeof profileData.days === "string" ? JSON.parse(profileData.days) : null)) || ["Mon","Tue","Wed","Thu","Fri"],
      status:     existingMemberData?.status     || profileData.status      || "present",
      leaveUntil: existingMemberData?.leaveUntil || "",
    } : {
      id:genId(), name:memberInput.trim(), role:memberRole.trim()||"Member",
      shift:memberShift, shiftStart:memberShiftStart||ds, shiftEnd:memberShiftEnd||de,
      days:memberDays, status:"present"
    };

    onUpdate({...team,members:[...(team.members||[]),base]});
    if (onMemberAdded) onMemberAdded(base.name, team.name, team.id);
    setMemberInput(""); setMemberRole(""); setMemberError("");
    setSelectedUser(null); setUserSearch("");
  }
  function removeMember(id) { onUpdate({...team,members:(team.members||[]).filter(m=>m.id!==id)}); }
  function updateMemberShift(id,shift,days,start,end) { onUpdate({...team,members:(team.members||[]).map(m=>m.id===id?{...m,shift,days,shiftStart:start,shiftEnd:end}:m)}); }
  function updateMemberStatus(id,status) { onUpdate({...team,members:(team.members||[]).map(m=>m.id===id?{...m,status}:m)}); }
  async function handleAddTeam() {
    if(!newName.trim()) return;
    const t = await onAdd({name:newName.trim(),color:newColor,members:[]});
    if (t?.id) setActiveTeam(t.id);
    setNewName(""); setNewColor(TEAM_PALETTE[Math.floor(Math.random()*TEAM_PALETTE.length)]);
  }

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(26,5,51,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(3px)" }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--bg)",borderRadius:6,width:720,maxWidth:"97vw",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(26,5,51,0.25)",overflow:"hidden" }}>
        {/* Header */}
        <div style={{ background:AC.navy,padding:"18px 24px",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div>
            <div style={{ color:"#fff",fontSize:16,fontWeight:700 }}>Manage Teams</div>
            <div style={{ color:"rgba(255,255,255,0.5)",fontSize:11,marginTop:2 }}>Configure teams, members and working schedules</div>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <button onClick={()=>{setShowInvite(v=>!v);setInviteDone(false);setInviteEmail("");setInviteError("");}}
              style={{ background:showInvite?"rgba(161,0,255,0.3)":"rgba(255,255,255,0.1)", border:`1px solid ${showInvite?"rgba(161,0,255,0.6)":"rgba(255,255,255,0.2)"}`, borderRadius:4, color:"rgba(255,255,255,0.85)", padding:"5px 12px", cursor:"pointer", fontSize:12, fontWeight:600, display:"flex",alignItems:"center",gap:6 }}>
              ✉️ Invite User
            </button>
            <button onClick={onClose} style={{ background:"rgba(255,255,255,0.1)",border:"none",borderRadius:4,color:"rgba(255,255,255,0.7)",width:28,height:28,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
          </div>
        </div>

        {/* Invite panel */}
        {showInvite && (
          <div style={{ background:"#0D0118",borderBottom:"1px solid rgba(255,255,255,0.08)",padding:"16px 24px" }}>
            {inviteDone ? (
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <span style={{ fontSize:20 }}>✅</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.9)" }}>Invite sent to <strong>{inviteEmail}</strong></div>
                  <div style={{ fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:3 }}>They'll receive a magic link to sign in. Once in, you can add them to a team.</div>
                </div>
                <button onClick={()=>{setInviteDone(false);setInviteEmail("");}}
                  style={{ background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:4,color:"rgba(255,255,255,0.6)",padding:"4px 12px",cursor:"pointer",fontSize:11 }}>
                  Send another
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8 }}>
                  Invite someone to MyTask
                </div>
                <div style={{ display:"flex",gap:8 }}>
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={e=>{setInviteEmail(e.target.value);setInviteError("");}}
                    onKeyDown={e=>e.key==="Enter"&&handleInvite()}
                    autoFocus
                    style={{ flex:1,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:4,color:"#fff",fontSize:13,padding:"8px 12px",outline:"none",fontFamily:"inherit" }}/>
                  <button onClick={handleInvite} disabled={inviteLoading||!inviteEmail.trim()}
                    style={{ background:AC.purple,border:"none",borderRadius:4,color:"#fff",padding:"8px 18px",cursor:"pointer",fontSize:12,fontWeight:600,opacity:inviteLoading||!inviteEmail.trim()?0.5:1,flexShrink:0,whiteSpace:"nowrap" }}>
                    {inviteLoading?"Sending…":"Send Invite"}
                  </button>
                </div>
                {inviteError && <div style={{ fontSize:11,color:"#FF8080",marginTop:6 }}>{inviteError}</div>}
                <div style={{ fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:8 }}>
                  They'll receive an email with a one-click sign-in link. After signing in they can set a password from their profile.
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ display:"flex",flex:1,overflow:"hidden" }}>
          {/* Sidebar */}
          <div style={{ width:210,borderRight:"1px solid var(--border)",overflowY:"auto",background:"var(--bg-off)",display:"flex",flexDirection:"column" }}>
            <div style={{ padding:"12px 12px 6px",fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase" }}>Teams</div>
            {liveTeams.map(t=>{
              const cnt=tasks.filter(tk=>tk.team===t.id).length, active=t.id===activeTeam;
              return (
                <button key={t.id} onClick={()=>{setActiveTeam(t.id);setEditingName(false);setMemberInput("");setMemberError("");setSelectedUser(null);setUserSearch("");setConfirmDelete(false);}}
                  style={{ width:"100%",background:active?AC.white:"transparent",border:"none",borderLeft:`3px solid ${active?t.color:"transparent"}`,padding:"10px 14px 10px 11px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid var(--border)" }}>
                  <span style={{ width:9,height:9,borderRadius:"50%",background:t.color,flexShrink:0,display:"block" }}/>
                  <div style={{ overflow:"hidden",flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:active?700:500,color:active?"var(--text-primary)":"var(--text-second)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{t.name}</div>
                    <div style={{ fontSize:10,color:"var(--text-muted)" }}>{(t.members||[]).length} members · {cnt} tasks</div>
                  </div>
                </button>
              );
            })}
            {/* Add team */}
            <div style={{ padding:"14px 12px",borderTop:"1px solid var(--border)",marginTop:"auto" }}>
              <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8 }}>New Team</div>
              <input style={{...INP,marginBottom:8,fontSize:12,padding:"6px 10px"}} placeholder="Team name…" value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleAddTeam()}/>
              <div style={{ display:"flex",gap:4,flexWrap:"wrap",marginBottom:8 }}>
                {TEAM_PALETTE.slice(0,10).map(c=><button key={c} onClick={()=>setNewColor(c)} style={{ width:18,height:18,borderRadius:"50%",background:c,border:newColor===c?`2px solid ${AC.navy}`:"2px solid transparent",cursor:"pointer",outline:newColor===c?`2px solid ${c}`:"none",outlineOffset:1 }}/>)}
              </div>
              <button onClick={handleAddTeam} disabled={!newName.trim()} style={{...BTN_PRIMARY,width:"100%",padding:"7px 0",fontSize:12,opacity:newName.trim()?1:0.4}}>+ Add Team</button>
            </div>
          </div>

          {/* Right panel */}
          {team ? (
            <div style={{ flex:1,overflowY:"auto",padding:"20px 24px" }}>
              {/* Team header */}
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,paddingBottom:16,borderBottom:"1px solid var(--border)" }}>
                {editingName ? (
                  <div style={{ display:"flex",gap:8,alignItems:"center",flex:1 }}>
                    <input style={{...INP,flex:1,fontSize:15,fontWeight:600,width:"auto",minWidth:0}} value={editName} onChange={e=>setEditName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveEditName()} autoFocus/>
                    <div style={{ display:"flex",gap:4 }}>{TEAM_PALETTE.map(c=><button key={c} onClick={()=>setEditColor(c)} style={{ width:18,height:18,borderRadius:"50%",background:c,border:editColor===c?`2px solid ${AC.navy}`:"2px solid transparent",cursor:"pointer" }}/>)}</div>
                    <button onClick={saveEditName} style={BTN_PRIMARY}>Save</button>
                    <button onClick={()=>setEditingName(false)} style={BTN_GHOST}>✕</button>
                  </div>
                ) : (
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <span style={{ width:12,height:12,borderRadius:"50%",background:team.color,display:"block" }}/>
                    <span style={{ fontSize:18,fontWeight:700,color:"var(--text-primary)" }}>{team.name}</span>
                    <span style={{ fontSize:11,color:"var(--text-muted)",background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:10,padding:"1px 8px" }}>{taskCount} task{taskCount!==1?"s":""}</span>
                  </div>
                )}
                {!editingName && (
                  <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                    <button onClick={()=>{setEditName(team.name);setEditColor(team.color);setEditingName(true);}} style={{...BTN_GHOST,padding:"5px 12px",fontSize:12}}>Rename</button>
                    {(()=>{
                      // Only the team creator (admin) can delete the team.
                      // If createdBy is missing (old teams), allow any member who is in the team —
                      // since only members see this panel, they are the de-facto owner.
                      const isCreator = !team.createdBy
                        ? (team.members||[]).some(m => m.name === currentUser?.name)
                        : team.createdBy === currentUser?.name;
                      if (!isCreator) return (
                        <div title={team.createdBy ? `Only ${team.createdBy} (team admin) can delete this team` : "Only the team admin can delete this team"}
                          style={{ background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text-muted)",padding:"5px 12px",fontSize:12,cursor:"not-allowed",display:"flex",alignItems:"center",gap:5,userSelect:"none" }}>
                          🔒 Delete
                        </div>
                      );
                      if (confirmDelete) return (
                        <div style={{ display:"flex",alignItems:"center",gap:6,background:AC.dangerBg,border:`1px solid ${AC.danger}55`,borderRadius:6,padding:"5px 10px" }}>
                          <span style={{ fontSize:11,fontWeight:600,color:AC.danger,whiteSpace:"nowrap" }}>Delete "{team.name}"?</span>
                          <button onClick={()=>{onDelete(team.id);setActiveTeam(teams.find(t=>t.id!==team.id)?.id||null);setConfirmDelete(false);}}
                            style={{ background:AC.danger,border:"none",borderRadius:4,color:"#fff",padding:"3px 10px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap" }}>
                            Yes, delete
                          </button>
                          <button onClick={()=>setConfirmDelete(false)}
                            style={{ background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text-second)",padding:"3px 8px",fontSize:11,cursor:"pointer" }}>
                            Cancel
                          </button>
                        </div>
                      );
                      return (
                        <button onClick={()=>setConfirmDelete(true)}
                          style={{ background:AC.dangerBg,border:`1px solid ${AC.danger}33`,borderRadius:4,color:AC.danger,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:500 }}>
                          Delete
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Members */}
              <div style={{ fontSize:11,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12 }}>Members · {(team.members||[]).length}</div>
              {(team.members||[]).length===0
                ? <div style={{ textAlign:"center",padding:"28px 0",color:"var(--text-muted)",border:`1px dashed ${AC.borderMid}`,borderRadius:6,marginBottom:16 }}>No members yet — add someone below</div>
                : <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                    {(liveTeams.find(t=>t.id===team.id)?.members||team.members||[]).map(m=><MemberCard key={m.id} member={m} teamColor={team.color} onRemove={()=>removeMember(m.id)} lastSeen={lastSeenMap[m.name]}/>)}
                  </div>
              }

              {/* Add member */}
              <div style={{ background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:16 }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase" }}>Add Member</div>
                  <div style={{ display:"flex",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:5,overflow:"hidden" }}>
                    {[["existing","Existing user"],["new","New user"]].map(([v,l])=>(
                      <button key={v} onClick={()=>{setMemberMode(v);setMemberInput("");setMemberRole("");setMemberError("");setSelectedUser(null);setUserSearch("");}}
                        style={{ background:memberMode===v?team.color:AC.white,border:"none",color:memberMode===v?"#fff":AC.textSecond,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:memberMode===v?700:400 }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {memberMode==="existing" ? (
                  <>
                    {/* Search existing users */}
                    <div style={{ position:"relative",marginBottom:10 }}>
                      <input
                        style={{...INP,paddingLeft:30}}
                        placeholder="Search by name or role…"
                        value={userSearch}
                        onChange={e=>{setUserSearch(e.target.value);setSelectedUser(null);}}
                      />
                      <span style={{ position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--text-muted)",fontSize:13 }}>🔍</span>
                    </div>
                    {/* User list */}
                    {(()=>{
                      const alreadyIn = new Set((team.members||[]).map(m=>m.name.toLowerCase()));
                      // Show all registered Supabase users not already in this team
                       const filtered = (registeredUsers||[]).filter(u=>
                         !alreadyIn.has(u.name.toLowerCase()) &&
                         (userSearch==='' || u.name.toLowerCase().includes(userSearch.toLowerCase()) || (u.role||'').toLowerCase().includes(userSearch.toLowerCase()) || (u.email||'').toLowerCase().includes(userSearch.toLowerCase()))
                       );
                       if (registeredUsers===null) return (
                         <div style={{ textAlign:"center",padding:"14px 0",color:"var(--text-muted)",fontSize:12 }}>Loading users…</div>
                       );
                       if (filtered.length===0 && (registeredUsers||[]).length>0) return (
                         <div style={{ textAlign:"center",padding:"14px 0",color:"var(--text-muted)",fontSize:12 }}>
                           {userSearch ? "No matching registered users" : "All registered users are already in this team"}
                         </div>
                       );
                       if ((registeredUsers||[]).length===0) return (
                         <div style={{ textAlign:"center",padding:"14px 0",color:"var(--text-muted)",fontSize:12 }}>No registered users found</div>
                       );
                       return (
                         <div style={{ display:"flex",flexDirection:"column",gap:5,marginBottom:10,maxHeight:200,overflowY:"auto",border:"1px solid var(--border)",borderRadius:4,background:"var(--bg)" }}>
                           {filtered.map(u=>{
                            const isSel = selectedUser?.id===u.id;
                            return (
                              <button key={u.id} onClick={()=>{
                                setSelectedUser(u);
                                setMemberInput(u.name);
                                setMemberRole(u.role||"Member");
                                const shiftId = u.shift || u.shift_type || "flexible";
                                const matched = SHIFTS.find(s=>s.id===shiftId) || SHIFTS.find(s=>s.id==="flexible");
                                setMemberShift(matched.id);
                                if (matched.id !== "flexible") {
                                  const [st,en] = matched.time.split(" – ");
                                  setMemberShiftStart(u.shift_start || u.shiftStart || st);
                                  setMemberShiftEnd(u.shift_end || u.shiftEnd || en);
                                } else {
                                  setMemberShiftStart(u.shift_start || u.shiftStart || "09:00");
                                  setMemberShiftEnd(u.shift_end || u.shiftEnd || "17:00");
                                }
                                if (u.days && Array.isArray(u.days) && u.days.length) setMemberDays(u.days);
                              }}
                                style={{ background:isSel?team.color+"18":"var(--bg)",border:`1.5px solid ${isSel?team.color:"var(--border)"}`,borderRadius:6,padding:"9px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,textAlign:"left",transition:"all 0.1s",width:"100%" }}
                                onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="var(--bg-off)"; }}
                                onMouseLeave={e=>{ e.currentTarget.style.background=isSel?team.color+"18":"var(--bg)"; }}>
                                <Avatar name={u.name} size={30}/>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontSize:13,fontWeight:isSel?700:500,color:isSel?team.color:"var(--text-primary)" }}>{u.name}</div>
                                  <div style={{ fontSize:11,color:"var(--text-muted)" }}>{u.role||"Member"} · {u.email}</div>
                                </div>
                                {isSel && <span style={{ color:team.color,fontWeight:700,fontSize:14 }}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {memberError && <div style={{ fontSize:11,color:AC.danger,marginBottom:8 }}>{memberError}</div>}
                     {/* Selected user summary — read only, uses their profile data */}
                     {selectedUser && (
                       <div style={{ background:"var(--bg-off)",border:`1.5px solid ${team.color}`,borderRadius:6,padding:"12px 14px",marginBottom:8 }}>
                         <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:10 }}>Will be added as</div>
                         <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                           <Avatar name={selectedUser.name} size={32}/>
                           <div>
                             <div style={{ fontSize:13,fontWeight:600,color:"var(--text-primary)" }}>{selectedUser.name}</div>
                             <div style={{ fontSize:11,color:"var(--text-muted)" }}>{selectedUser.role||"Member"} · {selectedUser.email}</div>
                           </div>
                         </div>
                         <div style={{ fontSize:11,color:"var(--text-muted)",marginTop:10,fontStyle:"italic" }}>
                           ℹ️ Role and shift will reflect what this user sets in their own profile.
                         </div>
                       </div>
                     )}
                    <button onClick={addMember} disabled={!selectedUser}
                      style={{...BTN_PRIMARY,width:"100%",opacity:selectedUser?1:0.4}}>
                      Add {selectedUser?selectedUser.name.split(" ")[0]:"Selected User"} to Team
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display:"flex",gap:8,marginBottom:10 }}>
                      <input style={{...INP,flex:2}} placeholder="Full name" value={memberInput} onChange={e=>{setMemberInput(e.target.value);setMemberError("");}} onKeyDown={e=>e.key==="Enter"&&addMember()}/>
                      <input style={{...INP,flex:1}} placeholder="Role" value={memberRole} onChange={e=>setMemberRole(e.target.value)}/>
                    </div>
                    <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>Shift</div>
                    <div style={{ display:"flex",flexWrap:"wrap",gap:5,marginBottom:8 }}>
                      {SHIFTS.map(s=><button key={s.id} onClick={()=>{setMemberShift(s.id);if(s.id!=="flexible"){const[st,en]=s.time.split(" – ");setMemberShiftStart(st);setMemberShiftEnd(en);}}} style={{ background:memberShift===s.id?team.color:AC.white,border:`1.5px solid ${memberShift===s.id?team.color:AC.border}`,borderRadius:4,padding:"4px 10px",cursor:"pointer",fontSize:11,color:memberShift===s.id?"#fff":AC.textSecond,fontWeight:memberShift===s.id?600:400 }}>{s.icon} {s.label}</button>)}
                    </div>
                    <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:10 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:6,flex:1 }}><span style={{ fontSize:10,color:"var(--text-muted)",fontWeight:700,flexShrink:0 }}>FROM</span><input type="time" value={memberShiftStart} onChange={e=>setMemberShiftStart(e.target.value)} style={{...INP,flex:1,padding:"6px 8px",fontSize:12}}/></div>
                      <span style={{ color:"var(--text-muted)" }}>→</span>
                      <div style={{ display:"flex",alignItems:"center",gap:6,flex:1 }}><span style={{ fontSize:10,color:"var(--text-muted)",fontWeight:700,flexShrink:0 }}>TO</span><input type="time" value={memberShiftEnd} onChange={e=>setMemberShiftEnd(e.target.value)} style={{...INP,flex:1,padding:"6px 8px",fontSize:12}}/></div>
                    </div>
                    <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>Working Days</div>
                    <div style={{ display:"flex",gap:4,marginBottom:12 }}>
                      {DAYS.map(d=><button key={d} onClick={()=>setMemberDays(ds=>ds.includes(d)?ds.filter(x=>x!==d):[...ds,d])} style={{ background:memberDays.includes(d)?team.color:AC.white,border:`1.5px solid ${memberDays.includes(d)?team.color:AC.border}`,borderRadius:4,padding:"4px 6px",cursor:"pointer",fontSize:11,color:memberDays.includes(d)?"#fff":AC.textSecond,fontWeight:memberDays.includes(d)?600:400,width:36 }}>{d}</button>)}
                    </div>
                    {memberError && <div style={{ fontSize:11,color:AC.danger,marginBottom:8 }}>{memberError}</div>}
                    <button onClick={addMember} disabled={!memberInput.trim()} style={{...BTN_PRIMARY,width:"100%",opacity:memberInput.trim()?1:0.4}}>Add Member</button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-muted)",fontSize:14 }}>Select or create a team</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MEMBER CARD
══════════════════════════════════════════════════════════════ */
function MemberCard({ member, teamColor, onRemove, lastSeen }) {
  const [hov, setHov] = useState(false);

  const shift      = SHIFTS.find(s=>s.id===(member.shift||"flexible"))||SHIFTS[4];
  const statusMeta = MEMBER_STATUSES.find(s=>s.id===(member.status||"present"))||MEMBER_STATUSES[0];
  const displayTime = `${member.shiftStart||"–"} – ${member.shiftEnd||"–"}`;

  // Format last seen as "X min ago", "X hrs ago", "X days ago"
  function formatLastSeen(iso) {
    if (!iso) return null;
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60)   return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)} hr${Math.floor(diff/3600)>1?"s":""} ago`;
    return `${Math.floor(diff/86400)} day${Math.floor(diff/86400)>1?"s":""} ago`;
  }
  const lastSeenLabel = formatLastSeen(lastSeen);

  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:"var(--bg)", border:`1.5px solid ${hov?teamColor:"var(--border)"}`, borderRadius:8, padding:"12px 14px", transition:"all 0.15s", display:"flex", flexDirection:"column", gap:8 }}>

      {/* Row 1: Avatar + name + remove button */}
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <Avatar name={member.name} size={34}/>
          <span style={{ position:"absolute", bottom:-1, right:-1, width:9, height:9, borderRadius:"50%", background:statusMeta.dot, border:`2px solid var(--bg)`, display:"block" }}/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{member.name}</div>
          <div style={{ fontSize:11, color:"var(--text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{member.role}</div>
        </div>
        {hov && (
          <button onClick={onRemove}
            style={{ background:AC.dangerBg, border:`1px solid ${AC.danger}33`, borderRadius:4, color:AC.danger, width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:10, padding:0, flexShrink:0 }}>✕</button>
        )}
      </div>

      {/* Row 2: Status badge + last seen */}
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <span style={{ background:statusMeta.bg, border:`1px solid ${statusMeta.border}`, borderRadius:4, padding:"3px 8px", display:"inline-flex", alignItems:"center", gap:4, flex:1, minWidth:0 }}>
          <span style={{ width:6, height:6, borderRadius:"50%", background:statusMeta.dot, flexShrink:0, display:"block" }}/>
          <span style={{ fontSize:11, fontWeight:600, color:statusMeta.color, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{statusMeta.label}</span>
          {member.status==="on_leave" && member.leaveUntil && (
            <span style={{ fontSize:10, color:statusMeta.color, opacity:0.7, flexShrink:0 }}>· {member.leaveUntil}</span>
          )}
        </span>
        {lastSeenLabel && (
          <span title={`Last active: ${new Date(lastSeen).toLocaleString()}`}
            style={{ fontSize:10, color:"var(--text-muted)", whiteSpace:"nowrap", flexShrink:0, cursor:"default" }}>
            🕐 {lastSeenLabel}
          </span>
        )}
      </div>

      {/* Row 3: Shift time */}
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <span style={{ fontSize:12 }}>{shift.icon}</span>
        <span style={{ fontSize:11, fontWeight:600, color:teamColor, flexShrink:0 }}>{shift.label}</span>
        <span style={{ fontSize:10, color:"var(--text-muted)", fontFamily:"monospace", marginLeft:"auto" }}>{displayTime}</span>
      </div>

      {/* Row 4: Working days */}
      <div style={{ display:"flex", gap:2 }}>
        {DAYS.map(d=>(
          <span key={d} title={d} style={{ flex:1, height:18, borderRadius:3, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, background:(member.days||[]).includes(d)?teamColor:"var(--bg-off)", color:(member.days||[]).includes(d)?"#fff":"var(--text-muted)", border:`1px solid ${(member.days||[]).includes(d)?teamColor:"var(--border)"}` }}>{d[0]}</span>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   REASSIGN MODAL
══════════════════════════════════════════════════════════════ */
function ReassignModal({ task, teams, allTasks, onReassign, onClose }) {
  const [selectedTeam,setSelectedTeam]=useState(task.team||teams[0]?.id);
  const [selectedMembers,setSelectedMembers]=useState(getAssignees(task));
  const [filterAvail,setFilterAvail]=useState(true); // show available only by default
  const [aiSuggestion,setAiSuggestion]=useState("");
  const [aiLoading,setAiLoading]=useState(false);
  const team=teams.find(t=>t.id===selectedTeam);
  const members=team?.members||[];
  const getMemberTime=m=>m.shiftStart&&m.shiftEnd?`${m.shiftStart} – ${m.shiftEnd}`:(SHIFTS.find(s=>s.id===(m.shift||"flexible"))||SHIFTS[4]).time;

  // Sort members: available first, then by workload (fewest tasks first)
  const sortedMembers = [...members].sort((a,b) => {
    const aAvail = ["present","busy"].includes(a.status||"present");
    const bAvail = ["present","busy"].includes(b.status||"present");
    if (aAvail !== bAvail) return aAvail ? -1 : 1;
    const aLoad = (allTasks||[]).filter(t=>getAssignees(t).includes(a.name)&&t.status!=="Done").length;
    const bLoad = (allTasks||[]).filter(t=>getAssignees(t).includes(b.name)&&t.status!=="Done").length;
    return aLoad - bLoad;
  });

  const displayMembers = filterAvail
    ? sortedMembers.filter(m=>["present","busy"].includes(m.status||"present"))
    : sortedMembers;

  const unavailableCount = members.filter(m=>!["present","busy"].includes(m.status||"present")).length;

  function toggleMember(name) {
    setSelectedMembers(prev=>
      prev.includes(name) ? prev.filter(n=>n!==name) : [...prev, name]
    );
  }

  async function aiSuggest() {
    setAiLoading(true);
    setAiSuggestion("");
    try {
      const memberInfo = sortedMembers.map(m=>{
        const load=(allTasks||[]).filter(t=>getAssignees(t).includes(m.name)&&t.status!=="Done").length;
        const isAvail=["present","busy"].includes(m.status||"present");
        return `${m.name} (${m.role}, ${isAvail?"available":"unavailable"}, ${load} active tasks)`;
      }).join("; ");
      const result = await callClaude(
        `You are a work management assistant. Suggest the best person to assign this task based on availability and workload.\n\nTask: "${task.title}" [${task.priority} priority]\nTask description: "${task.description||"none"}"\n\nTeam members: ${memberInfo}\n\nReply in 1-2 sentences: who you recommend and why. Be specific about their availability and workload.`
      );
      setAiSuggestion(result);
    } catch(e) { setAiSuggestion("Could not get suggestion: "+e.message); }
    setAiLoading(false);
  }

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(26,5,51,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1100,backdropFilter:"blur(3px)" }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--bg)",borderRadius:6,width:560,maxWidth:"97vw",boxShadow:"0 24px 60px rgba(26,5,51,0.25)",maxHeight:"90vh",overflowY:"auto",display:"flex",flexDirection:"column" }}>
        {/* Header */}
        <div style={{ background:AC.navy,padding:"18px 24px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",borderRadius:"6px 6px 0 0" }}>
          <div>
            <div style={{ color:"#fff",fontSize:16,fontWeight:700 }}>Assign Task</div>
            <div style={{ color:"rgba(255,255,255,0.5)",fontSize:11,marginTop:3,maxWidth:380 }}>"{task.title}"</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.1)",border:"none",borderRadius:4,color:"rgba(255,255,255,0.7)",width:28,height:28,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
        </div>

        <div style={{ padding:"20px 24px",flex:1 }}>
          {/* Currently selected */}
          <div style={{ background:"var(--bg-off)",border:"1px solid var(--border)",borderRadius:6,padding:"10px 14px",marginBottom:16 }}>
            <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8 }}>
              Selected {selectedMembers.length>0 && <span style={{ color:AC.purple,background:AC.purpleBg,borderRadius:8,padding:"1px 7px",marginLeft:4 }}>{selectedMembers.length}</span>}
            </div>
            {selectedMembers.length===0
              ? <span style={{ fontSize:12,color:"var(--text-muted)" }}>No one selected yet</span>
              : <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  {selectedMembers.map(name=>(
                    <div key={name} style={{ display:"flex",alignItems:"center",gap:5,background:"var(--bg)",border:`1.5px solid ${AC.purple}`,borderRadius:20,padding:"3px 10px 3px 6px" }}>
                      <Avatar name={name} size={18}/>
                      <span style={{ fontSize:12,fontWeight:600,color:AC.purple }}>{name.split(" ")[0]}</span>
                      <button onClick={()=>toggleMember(name)} style={{ background:"none",border:"none",color:AC.purple,cursor:"pointer",fontSize:12,padding:0,marginLeft:2,lineHeight:1 }}>✕</button>
                    </div>
                  ))}
                </div>
            }
          </div>

          {/* AI Suggestion */}
          <div style={{ marginBottom:16 }}>
            <button onClick={aiSuggest} disabled={aiLoading}
              style={{ background:AC.purpleBg,border:`1px solid ${AC.purple}`,borderRadius:4,color:AC.purple,padding:"6px 14px",cursor:aiLoading?"default":"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6 }}>
              {aiLoading ? "⏳ Analysing…" : "✨ AI Suggest best person"}
            </button>
            {aiSuggestion && (
              <div style={{ marginTop:8,background:AC.purpleBg,border:`1px solid ${AC.purpleDim}`,borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--text-primary)",lineHeight:1.6 }}>
                <span style={{ fontSize:10,fontWeight:700,color:AC.purple,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4 }}>✨ AI Recommendation</span>
                {aiSuggestion}
              </div>
            )}
          </div>

          {/* Team tabs */}
          <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8 }}>Team</div>
          <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:16 }}>
            {teams.map(t=><button key={t.id} onClick={()=>setSelectedTeam(t.id)} style={{ background:selectedTeam===t.id?t.color:"var(--bg)",border:`1.5px solid ${selectedTeam===t.id?t.color:"var(--border)"}`,borderRadius:4,padding:"5px 14px",cursor:"pointer",fontSize:12,fontWeight:selectedTeam===t.id?700:400,color:selectedTeam===t.id?"#fff":"var(--text-second)",display:"flex",alignItems:"center",gap:6 }}><span style={{ width:6,height:6,borderRadius:"50%",background:selectedTeam===t.id?"rgba(255,255,255,0.6)":t.color,display:"block" }}/>{t.name}<span style={{ fontSize:10,opacity:0.7 }}>{(t.members||[]).length}</span></button>)}
          </div>

          {/* Available filter toggle */}
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
            <div style={{ fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:"0.08em",textTransform:"uppercase" }}>
              Members {filterAvail ? `— available now (${displayMembers.length})` : `— all (${sortedMembers.length})`}
            </div>
            {unavailableCount>0 && (
              <button onClick={()=>setFilterAvail(v=>!v)}
                style={{ background:"transparent",border:`1px solid var(--border)`,borderRadius:4,color:"var(--text-muted)",padding:"3px 10px",cursor:"pointer",fontSize:11 }}>
                {filterAvail ? `Show ${unavailableCount} unavailable` : "Show available only"}
              </button>
            )}
          </div>

          {/* Member list */}
          {displayMembers.length===0
            ? <div style={{ textAlign:"center",padding:"20px 0",color:"var(--text-muted)",border:`1px dashed ${AC.borderMid}`,borderRadius:4,marginBottom:16 }}>No available members right now</div>
            : <div style={{ display:"flex",flexDirection:"column",gap:8,marginBottom:20 }}>
                {displayMembers.map(m=>{
                  const statusMeta=MEMBER_STATUSES.find(s=>s.id===(m.status||"present"))||MEMBER_STATUSES[0];
                  const isAvail=["present","busy"].includes(m.status||"present");
                  const isSelected=selectedMembers.includes(m.name);
                  const shift=SHIFTS.find(s=>s.id===(m.shift||"flexible"))||SHIFTS[4];
                  const activeTasks=(allTasks||[]).filter(t=>getAssignees(t).includes(m.name)&&t.status!=="Done").length;
                  const workloadColor=activeTasks<=2?AC.success:activeTasks<=5?AC.warning:AC.danger;
                  return (
                    <button key={m.id} onClick={()=>toggleMember(m.name)}
                      style={{ background:isSelected?team.color+"12":"var(--bg)",border:`1.5px solid ${isSelected?team.color:"var(--border)"}`,borderRadius:6,padding:"12px 14px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12,transition:"all 0.1s",opacity:isAvail?1:0.6 }}>
                      <div style={{ position:"relative",flexShrink:0 }}>
                        <Avatar name={m.name} size={36}/>
                        <span style={{ position:"absolute",bottom:-1,right:-1,width:10,height:10,borderRadius:"50%",background:statusMeta.dot,border:`2px solid var(--bg)`,display:"block" }}/>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:5,flexWrap:"wrap" }}>
                          <span style={{ fontSize:13,fontWeight:600,color:isSelected?team.color:"var(--text-primary)" }}>{m.name}</span>
                          <span style={{ fontSize:11,color:"var(--text-muted)" }}>{m.role}</span>
                          {!isAvail && <span style={{ fontSize:10,fontWeight:700,color:AC.danger,background:AC.dangerBg,borderRadius:3,padding:"1px 6px" }}>Unavailable</span>}
                        </div>
                        <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                          <span style={{ background:statusMeta.bg,border:`1px solid ${statusMeta.border}`,borderRadius:3,padding:"2px 8px",fontSize:11,fontWeight:600,color:statusMeta.color,display:"inline-flex",alignItems:"center",gap:4 }}>
                            <span style={{ width:5,height:5,borderRadius:"50%",background:statusMeta.dot,display:"block" }}/>{statusMeta.label}
                          </span>
                          <span style={{ fontSize:10,color:"var(--text-muted)",fontFamily:"monospace" }}>{shift.icon} {getMemberTime(m)}</span>
                          <span style={{ fontSize:10,fontWeight:600,color:workloadColor,background:workloadColor+"15",borderRadius:3,padding:"1px 7px" }}>
                            {activeTasks} active task{activeTasks!==1?"s":""}
                          </span>
                        </div>
                      </div>
                      <div style={{ width:22,height:22,borderRadius:4,border:`2px solid ${isSelected?team.color:"var(--border)"}`,background:isSelected?team.color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.1s" }}>
                        {isSelected&&<span style={{ color:"#fff",fontSize:13,fontWeight:700 }}>✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
          }
        </div>

        <div style={{ padding:"14px 24px",borderTop:"1px solid var(--border)",display:"flex",gap:8,justifyContent:"flex-end",background:"var(--bg-off)",borderRadius:"0 0 6px 6px" }}>
          <button onClick={onClose} style={BTN_GHOST}>Cancel</button>
          <button onClick={()=>{ if(selectedMembers.length>0) onReassign(task.id,selectedMembers,selectedTeam); }} disabled={!selectedMembers.length}
            style={{...BTN_PRIMARY,opacity:selectedMembers.length>0?1:0.4}}>
            Assign to {selectedMembers.length>0?selectedMembers.length===1?selectedMembers[0].split(" ")[0]:`${selectedMembers.length} people`:"…"}
          </button>
        </div>
      </div>
    </div>
  );
}
